import type { GlobalSearchFilter, View } from "./types";
import type { AppLanguage } from "./language";

export const languageNames: Record<AppLanguage, { en: string; ja: string }> = {
  en: { en: "English", ja: "Japanese" },
  ja: { en: "英語", ja: "日本語" },
};

export const viewLabels: Record<AppLanguage, Record<View, string>> = {
  en: {
    Dashboard: "Dashboard",
    Calendar: "Calendar",
    Tickets: "Tickets",
    Customers: "Customers",
    Items: "Items",
    Vendors: "Vendors",
    Expenses: "Expenses",
    "Knowledge Base": "Knowledge Base",
    Notifications: "Service Notices",
    Reporting: "Reporting",
    Logs: "Logs",
    Archive: "Archive",
    Users: "Users",
    Settings: "Settings",
  },
  ja: {
    Dashboard: "ダッシュボード",
    Calendar: "カレンダー",
    Tickets: "チケット",
    Customers: "顧客",
    Items: "項目",
    Vendors: "取引先",
    Expenses: "経費",
    "Knowledge Base": "ナレッジ",
    Notifications: "サービス通知",
    Reporting: "レポート",
    Logs: "ログ",
    Archive: "アーカイブ",
    Users: "ユーザー",
    Settings: "設定",
  },
};

export const appShellCopy = {
  en: {
    consoleLabel: "Console",
    searchPlaceholder: "Search tickets, customers, or knowledge articles...",
    searchFilterLabel: "Filter by category",
    allCategories: "All categories",
    noResultsTitle: "No matching results",
    noResultsDescription: "Try another keyword or adjust the type or category filters.",
    searchLoading: "Searching…",
    openTopResult: "Press Enter to open the top result.",
    searchFilters: {
      all: "All",
      ticket: "Tickets",
      customer: "Customers",
      knowledge: "Knowledge base",
    },
    searchKinds: {
      ticket: "Ticket",
      customer: "Customer",
      knowledge: "Article",
    },
  },
  ja: {
    consoleLabel: "コンソール",
    searchPlaceholder: "チケット、顧客、ナレッジ記事を検索...",
    searchFilterLabel: "カテゴリで絞り込む",
    allCategories: "すべてのカテゴリ",
    noResultsTitle: "一致する結果はありません",
    noResultsDescription: "別のキーワードを試すか、種別やカテゴリの絞り込みを調整してください。",
    searchLoading: "検索中…",
    openTopResult: "Enter キーで先頭の結果を開きます。",
    searchFilters: {
      all: "すべて",
      ticket: "チケット",
      customer: "顧客",
      knowledge: "ナレッジ",
    },
    searchKinds: {
      ticket: "チケット",
      customer: "顧客",
      knowledge: "記事",
    },
  },
} satisfies Record<AppLanguage, {
  consoleLabel: string;
  searchPlaceholder: string;
  searchFilterLabel: string;
  allCategories: string;
  noResultsTitle: string;
  noResultsDescription: string;
  searchLoading: string;
  openTopResult: string;
  searchFilters: Record<GlobalSearchFilter, string>;
  searchKinds: Record<Exclude<GlobalSearchFilter, "all">, string>;
}>;

export const profileMenuCopy = {
  en: {
    openProfileSettings: "Open profile settings",
    closeProfileSettings: "Close profile settings",
    profileSettings: "Profile settings",
    preferences: "Preferences",
    preferencesDescription: "Manage your profile photo, password, console theme, language, timezone, and API keys.",
    session: "Session",
    logOut: "Log out",
    appearance: "Appearance",
    appearanceDescription: "Choose the console theme that feels best for your workflow.",
    light: { title: "Light", detail: "Bright neutral surfaces" },
    dark: { title: "Dark", detail: "Low-glare dark surfaces" },
    language: "Language",
    languageDescription: "Choose your display language.If no choice is saved yet, Kuii follows your browser language.",
    profilePhoto: "Profile photo",
    profilePhotoTitle: "Add a personal avatar",
    profilePhotoDescription: "Upload a PNG, JPG, or WebP image up to 5 MB.",
    uploadPhoto: "Upload photo",
    replacePhoto: "Replace photo",
    savingPhoto: "Saving…",
    profilePhotoUnavailable: "Profile photo unavailable",
    removePhoto: "Remove photo",
    navigation: "Navigation",
    navigationDescription: "Switch the left sidebar between full labels and compact icons.",
    labels: { title: "Labels", detail: "Full-width menu with view names" },
    icons: { title: "Icons", detail: "Compact menu with more content space" },
    timezone: "Timezone",
    timezoneDescription: "Set how timestamps appear across tickets, customers, and reporting.",
    security: "Security",
    securityTitle: "Update your password",
    securityDescription: "Change the local password for your workspace account and sign out other active sessions.",
    currentPassword: "Current password",
    newPassword: "New password",
    confirmNewPassword: "Confirm new password",
    passwordHint: "Use at least 8 characters and store the new password somewhere secure.",
    updatePassword: "Update password",
    updatingPassword: "Updating…",
    apiAccess: "API access",
    apiAccessTitle: "Workspace API is enabled",
    apiAccessDescription: "Generate a personal API key for scripts, integrations, and automation.",
    tenantPolicyExpires: (daysLabel: string) => `Workspace policy expires every key after ${daysLabel}.`,
    optionalLabel: "Optional label",
    apiKeyExpiry: "API key expiry",
    generateApiKey: "Generate API key",
    generatingApiKey: "Generating…",
    newKey: "New key",
    copyKeyNow: "Copy this key now. For security, it is only shown once.",
    copy: "Copy",
    copied: "Copied",
    dismiss: "Dismiss",
    copyKeyError: "Unable to copy automatically. Copy the key manually.",
    createdLastUsed: (createdAt: string, lastUsedAt: string) => `Created ${createdAt} · Last used ${lastUsedAt}`,
    expired: "Expired",
    expires: "Expires",
    revokedAt: (timestamp: string) => `Revoked ${timestamp}`,
    revokedBadge: "Revoked",
    deleteRevokedApiKey: "Delete revoked API key",
    deleting: "Deleting…",
    delete: "Delete",
    revoking: "Revoking…",
    revoke: "Revoke",
    noApiKeys: "No personal API keys created yet.",
    passwordUpdated: "Your password was updated.",
    passwordRequired: "Enter your current password, then choose and confirm a new one.",
    passwordTooShort: "New passwords need at least 8 characters.",
    passwordConfirmationMismatch: "The password confirmation does not match.",
    updatePasswordError: "Unable to update your password.",
    updateAvatarError: "Unable to update your avatar.",
    removeAvatarError: "Unable to remove your avatar.",
    createApiKeyError: "Unable to create the API key.",
    deleteApiKeyError: "Unable to delete the API key.",
    revokeApiKeyError: "Unable to revoke the API key.",
  },
  ja: {
    openProfileSettings: "プロフィール設定を開く",
    closeProfileSettings: "プロフィール設定を閉じる",
    profileSettings: "プロフィール設定",
    preferences: "個人設定",
    preferencesDescription: "プロフィール画像、パスワード、コンソールテーマ、言語、タイムゾーン、API キーを管理します。",
    session: "セッション",
    logOut: "ログアウト",
    appearance: "表示",
    appearanceDescription: "作業しやすいコンソールテーマを選択します。",
    light: { title: "ライト", detail: "明るいニュートラルな画面" },
    dark: { title: "ダーク", detail: "まぶしさを抑えた暗色画面" },
    language: "言語",
    languageDescription: "表示言語を選択します。まだ保存済みの選択がない場合は、Kuii はブラウザの言語設定に従います。",
    profilePhoto: "プロフィール画像",
    profilePhotoTitle: "個人用アバターを追加",
    profilePhotoDescription: "5 MB までの PNG、JPG、または WebP 画像をアップロードできます。",
    uploadPhoto: "画像をアップロード",
    replacePhoto: "画像を変更",
    savingPhoto: "保存中…",
    profilePhotoUnavailable: "プロフィール画像は利用できません",
    removePhoto: "画像を削除",
    navigation: "ナビゲーション",
    navigationDescription: "左サイドバーをラベル表示とコンパクトなアイコン表示で切り替えます。",
    labels: { title: "ラベル", detail: "ビュー名を表示する通常メニュー" },
    icons: { title: "アイコン", detail: "表示領域を広く取れるコンパクトメニュー" },
    timezone: "タイムゾーン",
    timezoneDescription: "チケット、顧客、レポートでの時刻表示方法を設定します。",
    security: "セキュリティ",
    securityTitle: "パスワードを更新",
    securityDescription: "このワークスペースアカウントのローカルパスワードを変更し、他のアクティブなセッションからサインアウトします。",
    currentPassword: "現在のパスワード",
    newPassword: "新しいパスワード",
    confirmNewPassword: "新しいパスワードを確認",
    passwordHint: "8 文字以上を使用し、新しいパスワードは安全な場所に保管してください。",
    updatePassword: "パスワードを更新",
    updatingPassword: "更新中…",
    apiAccess: "API アクセス",
    apiAccessTitle: "ワークスペース API は有効です",
    apiAccessDescription: "スクリプト、連携、自動化用の個人 API キーを発行します。",
    tenantPolicyExpires: (daysLabel: string) => `ワークスペースポリシーにより、すべてのキーは ${daysLabel} 後に期限切れになります。`,
    optionalLabel: "任意のラベル",
    apiKeyExpiry: "API キーの有効期限",
    generateApiKey: "API キーを生成",
    generatingApiKey: "生成中…",
    newKey: "新しいキー",
    copyKeyNow: "このキーは今すぐコピーしてください。セキュリティ上の理由で一度しか表示されません。",
    copy: "コピー",
    copied: "コピー済み",
    dismiss: "閉じる",
    copyKeyError: "自動でコピーできませんでした。手動でコピーしてください。",
    createdLastUsed: (createdAt: string, lastUsedAt: string) => `作成: ${createdAt} · 最終使用: ${lastUsedAt}`,
    expired: "期限切れ",
    expires: "有効期限",
    revokedAt: (timestamp: string) => `無効化: ${timestamp}`,
    revokedBadge: "無効化済み",
    deleteRevokedApiKey: "無効化済み API キーを削除",
    deleting: "削除中…",
    delete: "削除",
    revoking: "無効化中…",
    revoke: "無効化",
    noApiKeys: "個人 API キーはまだ作成されていません。",
    passwordUpdated: "パスワードを更新しました。",
    passwordRequired: "現在のパスワードを入力し、新しいパスワードを選んで確認してください。",
    passwordTooShort: "新しいパスワードは 8 文字以上必要です。",
    passwordConfirmationMismatch: "パスワード確認が一致しません。",
    updatePasswordError: "パスワードを更新できませんでした。",
    updateAvatarError: "アバターを更新できませんでした。",
    removeAvatarError: "アバターを削除できませんでした。",
    createApiKeyError: "API キーを作成できませんでした。",
    deleteApiKeyError: "API キーを削除できませんでした。",
    revokeApiKeyError: "API キーを無効化できませんでした。",
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;

export const usersViewCopy = {
  en: {
    page: {
      eyebrow: "Users · Access and permissions",
      title: "Team Access",
      description: "Manage workspace-scoped accounts, operational roles, and directory structure from one dedicated users view.",
    },
    metrics: {
      activeUsersLabel: "Active users",
      activeUsersDetail: "Members who have already signed in to this workspace",
      adminsLabel: "Admins",
      adminsDetail: "Owners and admins with workspace management rights",
      pendingInvitesLabel: "Pending invites",
      pendingInvitesDetail: "Accounts that still need their first sign-in",
      accessPolicyLabel: "Access policy",
      managedValue: "Managed",
      readOnlyValue: "Read only",
      managedDetail: "This role can update workspace members and roles",
      readOnlyDetail: "This role can review access but not modify it",
    },
    actions: {
      view: "View",
      edit: "Edit",
      delete: "Delete",
      remove: "Remove",
      enableUser: "Enable",
      disableUser: "Disable",
      cancel: "Cancel",
      saveChanges: "Save changes",
      savePassword: "Save password",
      addWorkspaceUser: "Add user",
      addTeam: "Add team",
      addCustomRole: "Add custom role",
      createUser: "Create user",
      createTeam: "Create team",
      createRole: "Create role",
      saveTeam: "Save team",
      saveRole: "Save role",
      saving: "Saving…",
      setPassword: "Set password",
    },
    cards: {
      users: {
        eyebrow: "Directory",
        title: "Users",
        summaryTitle: "Directory summary",
        summaryDescription: "Users, including active users, pending invites, and role assignments.",
        includesTitle: "Includes",
        includesItems: ["Role and status labels", "Team membership chips", "Agent category coverage"],
        readOnlyBadge: "Read only",
      },
      teams: {
        eyebrow: "Routing",
        title: "Teams",
        summaryTitle: "Routing summary",
        summaryDescription: "Reusable teams for category coverage and shared assignment boundaries.",
        includesTitle: "Includes",
        includesItems: ["Member counts", "Category coverage", "Team descriptions"],
      },
      roles: {
        eyebrow: "Permissions",
        title: "Roles",
        summaryTitle: "Permissions summary",
        summaryDescription: "Built-in and custom roles used across the workspace.",
        includesTitle: "Includes",
        includesItems: ["Permission summaries", "System vs custom roles", "Assigned seat counts"],
        readOnlyBadge: "Membership roles",
      },
    },
    list: {
      archivedTeam: "Archived team",
      archivedCategory: "Archived category",
      allCategories: "All categories",
      you: "You",
      noPermissionsAssigned: "No permissions assigned",
      noTeamDescription: "No team description yet.",
      loadingRoles: "Loading roles…",
      system: "System",
      custom: "Custom",
    },
    counts: {
      more: (count: number) => `+${count} more`,
      usersSummary: (filtered: number, total: number) => `${filtered} of ${total} users`,
      teamsSummary: (filtered: number, total: number) => `${filtered} of ${total} teams`,
      rolesSummary: (filtered: number, total: number) => `${filtered} of ${total} roles`,
      teamsOnly: (count: number) => `${count} teams`,
      members: (count: number) => `${count} members`,
      seats: (count: number) => `${count} seats`,
    },
    statusLabels: {
      Active: "Active",
      Pending: "Pending",
      Disabled: "Disabled",
    },
    modals: {
      members: {
        eyebrow: "Directory",
        title: "Users",
        description: "Search members by name, email, role, team, or category without stretching the page.",
        searchPlaceholder: "Search users by name, email, role, team, or category",
        searchAriaLabel: "Search users",
        emptyTitle: "No users match that search.",
        emptyDescription: "Try a different name, email, role, team, or category.",
      },
      teams: {
        eyebrow: "Routing",
        title: "Teams",
        description: "Search team names, descriptions, categories, and members from a contained modal list.",
        searchPlaceholder: "Search teams by name, description, category, or member",
        searchAriaLabel: "Search user teams",
        emptySearchTitle: "No teams match that search.",
        emptySearchDescription: "Try a different team name, category, description, or member.",
        emptyDefaultTitle: "No teams created yet.",
        emptyDefaultDescription: "Create a team to reuse category coverage across multiple agents.",
      },
      roles: {
        eyebrow: "Permissions",
        title: "Roles",
        description: "Search role names, descriptions, and permissions in a scrollable modal view.",
        searchPlaceholder: "Search roles by name, description, or permission",
        searchAriaLabel: "Search role presets",
        emptyTitle: "No role presets match that search.",
        emptyDescription: "Try a different role name, description, or permission.",
      },
      password: {
        eyebrow: "Security",
        title: (name: string | null | undefined) => name ? `Set password for ${name}` : "Set password",
        description: "Set a new local password for this workspace account. Existing sessions for that user will be signed out after the change.",
        emailAddress: "Email address",
        newPassword: "New password",
        newPasswordPlaceholder: "Minimum 8 characters",
        confirmPassword: "Confirm password",
        confirmPasswordPlaceholder: "Repeat the new password",
        resetPasswordOnLogin: "Reset password on login",
        resetPasswordOnLoginDescription: "Require this user to sign in with this password and choose a new password before continuing.",
      },
      memberForm: {
        createEyebrow: "Create user",
        editEyebrow: "Edit user",
        createTitle: "New user",
        fallbackTitle: "User",
        createDescription: "Create an account, set a temporary password, and assign the first workspace role.",
        editDescription: "Update the member name or workspace role assignment.",
        fullName: "Full name",
        fullNamePlaceholder: "Jordan Lee",
        emailAddress: "Email address",
        emailPlaceholder: "jordan@company.com",
        role: "Role",
        selectRole: "Select a role",
        accountStatus: "Account status",
        statusEnabled: "Enabled",
        statusDisabled: "Disabled",
        temporaryPassword: "Temporary password",
        temporaryPasswordPlaceholder: "Minimum 8 characters",
        resetPasswordOnLogin: "Reset password on login",
        resetPasswordOnLoginDescription: "Require this user to sign in with the temporary password and choose a new password before continuing.",
        teamAssignmentsTitle: "Team assignments",
        teamAssignmentsDescription: "Teams add reusable routing coverage for shared support areas.",
        noTeams: "Create teams to assign reusable support coverage.",
        directCategoryScopeTitle: "Direct category scope",
        directCategoryScopeDescription: "Add personal categories on top of the categories inherited from the selected teams.",
        selectAll: "Select all",
        deselectAll: "Deselect all",
        inheritedFromTeams: "Inherited from teams",
      },
      teamForm: {
        createEyebrow: "Create team",
        editEyebrow: "Edit team",
        createTitle: "New team",
        fallbackTitle: "Team",
        description: "Create reusable support teams and assign the ticket categories they should cover.",
        teamName: "Team name",
        teamNamePlaceholder: "Billing operations",
        descriptionLabel: "Description",
        descriptionPlaceholder: "Handles invoice, payment, and subscription issues",
        categoryCoverageTitle: "Team category coverage",
        categoryCoverageDescription: "Select the ticket categories this team should automatically cover.",
      },
      roleForm: {
        createEyebrow: "Create custom role",
        editEyebrow: "Edit custom role",
        createTitle: "New custom role",
        fallbackTitle: "Custom role",
        description: "Choose the permissions this workspace-specific role should inherit. Role preset management stays reserved for built-in owner and admin roles.",
        roleName: "Role name",
        roleNamePlaceholder: "Escalation coordinator",
        descriptionLabel: "Description",
        descriptionPlaceholder: "Coordinates escalations and high-priority workflows",
        permissionsTitle: "Permissions",
        permissionsDescription: "Select the capabilities this custom role should receive.",
        permissionsSearchPlaceholder: "Filter permissions by name or description",
        permissionsSearchAriaLabel: "Filter permissions",
        permissionsNoResults: "No permissions match that filter.",
      },
    },
    errors: {
      loadRoles: "Unable to load roles.",
      requiredMemberFields: "Name, email, and role are required.",
      assignAgentScope: "Assign at least one team or ticket category to an agent.",
      temporaryPasswordTooShort: "New workspace users need a temporary password with at least 8 characters.",
      saveUser: "Unable to save the workspace user.",
      teamNameRequired: "Team name is required.",
      teamCategoryRequired: "Assign at least one ticket category to the team.",
      saveTeam: "Unable to save the team.",
      deleteTeam: "Unable to delete the team.",
      removeUser: "Unable to remove the workspace user.",
      newPasswordTooShort: "New passwords need at least 8 characters.",
      passwordMismatch: "The password confirmation does not match.",
      updateLocalPassword: "Unable to update the local password.",
      requiredRoleFields: "Role name and description are required.",
      saveRolePreset: "Unable to save the role preset.",
      deleteRolePreset: "Unable to delete the role preset.",
    },
    confirmations: {
      deleteTeam: (name: string) => `Delete the ${name} team?`,
      removeMember: (name: string) => `Remove ${name} from this workspace?`,
      deleteRole: (role: string) => `Delete the ${role} role preset?`,
    },
    roleLabels: {
      Owner: "Owner",
      Admin: "Admin",
      Agent: "Agent",
      "Customer Manager": "Customer manager",
      "Knowledge Manager": "Knowledge manager",
      "Reporting Manager": "Reporting manager",
      Viewer: "Viewer",
      "Team Member": "Team member",
    },
    roleDetails: {
      "Full access to workspace settings, team permissions, branding, integrations, billing, and operational data.": "Full access to workspace settings, team permissions, branding, integrations, billing, and operational data.",
      "Runs the workspace day to day, including users, categories, ticket operations, CRM updates, and knowledge management.": "Runs the workspace day to day, including users, categories, ticket operations, CRM updates, and knowledge management.",
      "Works tickets, reads customer context, and creates knowledge articles inside the categories assigned by an admin.": "Works tickets, reads customer context, and creates knowledge articles inside the categories assigned by an admin.",
      "Updates customer accounts, contacts, products, and notes without changing ticket workflows.": "Updates customer accounts, contacts, products, and notes without changing ticket workflows.",
      "Maintains collections and published content for the knowledge base without managing users or customer records.": "Maintains collections and published content for the knowledge base without managing users or customer records.",
      "Builds, runs, exports, and schedules reports without managing users, tickets, or customer records.": "Builds, runs, exports, and schedules reports without managing users, tickets, or customer records.",
      "Read-only access across tickets, customers, and knowledge content, with visibility into reporting dashboards.": "Read-only access across tickets, customers, and knowledge content, with visibility into reporting dashboards.",
    },
    capabilityLabels: {
      "Tickets : View": "Tickets : View",
      "Tickets : Edit": "Tickets : Edit",
      "Tickets : Delete": "Tickets : Delete",
      "Customers : View": "Customers : View",
      "Customers : Edit": "Customers : Edit",
      "Customers : Delete": "Customers : Delete",
      "Knowledge : View": "Knowledge : View",
      "Knowledge : Create": "Knowledge : Create",
      "Knowledge : Manage": "Knowledge : Manage",
      "Users : View": "Users : View",
      "Users : Manage": "Users : Manage",
      "Notifications : View": "Notifications : View",
      "Archive : View": "Archive : View",
      "Archive : Edit": "Archive : Edit",
      "Settings : View": "Settings : View",
      "Settings : Manage": "Settings : Manage",
      "Calendar : View": "Calendar : View",
      "Calendar : Create": "Calendar : Create",
      "Calendar : Manage": "Calendar : Manage",
      "API : Access": "API : Access",
      "Billing : Manage": "Billing : Manage",
      "Items : View": "Items : View",
      "Items : Manage": "Items : Manage",
      "Expenses : View": "Expenses : View",
      "Expenses : Edit": "Expenses : Edit",
      "Expenses : Delete": "Expenses : Delete",
      "Expenses : View all": "Expenses : View all",
      "Expenses : Edit all": "Expenses : Edit all",
      "Vendors : View": "Vendors : View",
      "Vendors : Edit": "Vendors : Edit",
      "Vendors : Delete": "Vendors : Delete",
      "Reporting : View": "Reporting : View",
      "Reporting : Manage": "Reporting : Manage",
    },
    capabilityDetails: {
      "Open ticket lists and review ticket details without making changes.": "Open ticket lists and review ticket details without making changes.",
      "Create, update, assign, and resolve tickets.": "Create, update, assign, and resolve tickets.",
      "Permanently delete tickets from the workspace.": "Permanently delete tickets from the workspace.",
      "Open customer accounts and review account details without making changes.": "Open customer accounts and review account details without making changes.",
      "Update customer accounts, contacts, products, and lifecycle notes.": "Update customer accounts, contacts, products, and lifecycle notes.",
      "Permanently delete customer accounts and their related tickets.": "Permanently delete customer accounts and their related tickets.",
      "Open knowledge base collections and read published workspace articles.": "Open knowledge base collections and read published workspace articles.",
      "Draft and publish knowledge base articles.": "Draft and publish knowledge base articles.",
      "Create collections and manage published knowledge content.": "Create collections and manage published knowledge content.",
      "Open the users directory and review role assignments.": "Open the users directory and review role assignments.",
      "Invite, update, and remove Users.": "Invite, update, and remove Users.",
      "Open service notices and retrieve platform announcements for this workspace.": "Open service notices and retrieve platform announcements for this workspace.",
      "Open the archive view and inspect archived workspace data.": "Open the archive view and inspect archived workspace data.",
      "Delete archived records and run archive maintenance actions.": "Delete archived records and run archive maintenance actions.",
      "Open the settings workspace and review workspace configuration.": "Open the settings workspace and review workspace configuration.",
      "Update branding, ticket categories, and administrative workspace settings.": "Update branding, ticket categories, and administrative workspace settings.",
      "Open the calendar and review scheduled items without making changes.": "Open the calendar and review scheduled items without making changes.",
      "Create, update, and delete scheduled calendar items without managing assignment types.": "Create, update, and delete scheduled calendar items without managing assignment types.",
      "Create, edit, and delete assignment types from the user calendar.": "Create, edit, and delete assignment types from the user calendar.",
      "Generate personal API keys and access workspace API endpoints programmatically.": "Generate personal API keys and access workspace API endpoints programmatically.",
      "Update workspace commercial defaults and billing configuration.": "Update workspace commercial defaults and billing configuration.",
      "Open the items catalog and review pricing, SLA, and bundle relationships.": "Open the items catalog and review pricing, SLA, and bundle relationships.",
      "Create, edit, and remove product plans and related items.": "Create, edit, and remove product plans and related items.",
      "Open the expenses ledger and review expense records without making changes.": "Open the expenses ledger and review expense records without making changes.",
      "Create and update expense records.": "Create and update expense records.",
      "Permanently delete expense records from the workspace.": "Permanently delete expense records from the workspace.",
      "See every member's expenses, not just your own.": "See every member's expenses, not just your own.",
      "Edit every member's expenses, not just your own.": "Edit every member's expenses, not just your own.",
      "Open the vendor directory and review vendor details without making changes.": "Open the vendor directory and review vendor details without making changes.",
      "Create and update vendor records.": "Create and update vendor records.",
      "Permanently delete vendors and their related expenses.": "Permanently delete vendors and their related expenses.",
      "Access dashboards and reporting views.": "Access dashboards and reporting views.",
      "Build and manage report packs and exports.": "Build and manage report packs and exports.",
    },
  },
  ja: {
    page: {
      eyebrow: "ユーザー · アクセスと権限",
      title: "チームアクセス",
      description: "この専用のユーザービューから、ワークスペース単位のアカウント、運用ロール、ディレクトリ構成をまとめて管理します。",
    },
    metrics: {
      activeUsersLabel: "アクティブユーザー",
      activeUsersDetail: "このワークスペースにすでにサインインしたメンバー",
      adminsLabel: "管理者",
      adminsDetail: "ワークスペース管理権限を持つオーナーと管理者",
      pendingInvitesLabel: "招待待ち",
      pendingInvitesDetail: "まだ初回サインインが必要なアカウント",
      accessPolicyLabel: "アクセス方針",
      managedValue: "管理可",
      readOnlyValue: "閲覧のみ",
      managedDetail: "このロールはワークスペースメンバーとロールを更新できます",
      readOnlyDetail: "このロールはアクセスを確認できますが変更はできません",
    },
    actions: {
      view: "表示",
      edit: "編集",
      delete: "削除",
      remove: "削除",
      enableUser: "有効化",
      disableUser: "無効化",
      cancel: "キャンセル",
      saveChanges: "変更を保存",
      savePassword: "パスワードを保存",
      addWorkspaceUser: "ユーザーを追加",
      addTeam: "チームを追加",
      addCustomRole: "カスタムロールを追加",
      createUser: "ユーザーを作成",
      createTeam: "チームを作成",
      createRole: "ロールを作成",
      saveTeam: "チームを保存",
      saveRole: "ロールを保存",
      saving: "保存中…",
      setPassword: "パスワードを設定",
    },
    cards: {
      users: {
        eyebrow: "ディレクトリ",
        title: "ユーザー",
        summaryTitle: "ディレクトリ概要",
        summaryDescription: "アクティブユーザー、招待待ち、ロール割り当てを含むユーザー数です。",
        includesTitle: "内容",
        includesItems: ["ロールとステータスの表示", "チーム所属チップ", "担当カテゴリ範囲"],
        readOnlyBadge: "閲覧のみ",
      },
      teams: {
        eyebrow: "ルーティング",
        title: "チーム",
        summaryTitle: "ルーティング概要",
        summaryDescription: "カテゴリ範囲や共通担当境界を再利用できるチームです。",
        includesTitle: "内容",
        includesItems: ["メンバー数", "カテゴリ範囲", "チーム説明"],
      },
      roles: {
        eyebrow: "権限",
        title: "ロール",
        summaryTitle: "権限概要",
        summaryDescription: "ワークスペース全体で使われている標準ロールとカスタムロールです。",
        includesTitle: "内容",
        includesItems: ["権限サマリー", "標準 / カスタムの区別", "割り当て済みシート数"],
        readOnlyBadge: "メンバーシップロール",
      },
    },
    list: {
      archivedTeam: "アーカイブ済みチーム",
      archivedCategory: "アーカイブ済みカテゴリ",
      allCategories: "すべてのカテゴリ",
      you: "あなた",
      noPermissionsAssigned: "割り当てられた権限はありません",
      noTeamDescription: "チーム説明はまだありません。",
      loadingRoles: "ロールを読み込み中…",
      system: "標準",
      custom: "カスタム",
    },
    counts: {
      more: (count: number) => `+${count} 件`,
      usersSummary: (filtered: number, total: number) => `${total} 件中 ${filtered} 件のユーザー`,
      teamsSummary: (filtered: number, total: number) => `${total} 件中 ${filtered} 件のチーム`,
      rolesSummary: (filtered: number, total: number) => `${total} 件中 ${filtered} 件のロール`,
      teamsOnly: (count: number) => `${count} チーム`,
      members: (count: number) => `${count} 名`,
      seats: (count: number) => `${count} シート`,
    },
    statusLabels: {
      Active: "有効",
      Pending: "保留中",
      Disabled: "無効",
    },
    modals: {
      members: {
        eyebrow: "ディレクトリ",
        title: "ユーザー",
        description: "ページを広げずに、名前、メール、ロール、チーム、カテゴリでメンバーを検索できます。",
        searchPlaceholder: "名前、メール、ロール、チーム、カテゴリでユーザーを検索",
        searchAriaLabel: "ユーザーを検索",
        emptyTitle: "検索条件に一致するユーザーはいません。",
        emptyDescription: "別の名前、メール、ロール、チーム、またはカテゴリで試してください。",
      },
      teams: {
        eyebrow: "ルーティング",
        title: "チーム",
        description: "コンパクトなモーダル一覧で、チーム名、説明、カテゴリ、メンバーを検索できます。",
        searchPlaceholder: "名前、説明、カテゴリ、メンバーでチームを検索",
        searchAriaLabel: "ユーザーチームを検索",
        emptySearchTitle: "検索条件に一致するチームはありません。",
        emptySearchDescription: "別のチーム名、カテゴリ、説明、またはメンバーで試してください。",
        emptyDefaultTitle: "チームはまだ作成されていません。",
        emptyDefaultDescription: "複数の担当者でカテゴリ範囲を再利用するにはチームを作成してください。",
      },
      roles: {
        eyebrow: "権限",
        title: "ロール",
        description: "スクロール可能なモーダルで、ロール名、説明、権限を検索できます。",
        searchPlaceholder: "名前、説明、権限でロールを検索",
        searchAriaLabel: "ロールプリセットを検索",
        emptyTitle: "検索条件に一致するロールプリセットはありません。",
        emptyDescription: "別のロール名、説明、または権限で試してください。",
      },
      password: {
        eyebrow: "セキュリティ",
        title: (name: string | null | undefined) => name ? `${name} のパスワードを設定` : "パスワードを設定",
        description: "このワークスペースアカウントの新しいローカルパスワードを設定します。変更後、そのユーザーの既存セッションはサインアウトされます。",
        emailAddress: "メールアドレス",
        newPassword: "新しいパスワード",
        newPasswordPlaceholder: "8 文字以上",
        confirmPassword: "パスワード確認",
        confirmPasswordPlaceholder: "新しいパスワードを再入力",
        resetPasswordOnLogin: "ログイン時にパスワードをリセット",
        resetPasswordOnLoginDescription: "このユーザーにこのパスワードでサインインさせ、続行前に新しいパスワードの設定を求めます。",
      },
      memberForm: {
        createEyebrow: "ユーザーを作成",
        editEyebrow: "ユーザーを編集",
        createTitle: "新しいユーザー",
        fallbackTitle: "ユーザー",
        createDescription: "アカウントを作成し、仮パスワードを設定して、最初のワークスペースロールを割り当てます。",
        editDescription: "メンバー名またはワークスペースロールの割り当てを更新します。",
        fullName: "氏名",
        fullNamePlaceholder: "山田 花子",
        emailAddress: "メールアドレス",
        emailPlaceholder: "hanako@company.com",
        role: "ロール",
        selectRole: "ロールを選択",
        accountStatus: "アカウント状態",
        statusEnabled: "有効",
        statusDisabled: "無効",
        temporaryPassword: "仮パスワード",
        temporaryPasswordPlaceholder: "8 文字以上",
        resetPasswordOnLogin: "ログイン時にパスワードをリセット",
        resetPasswordOnLoginDescription: "このユーザーに仮パスワードでサインインさせ、続行前に新しいパスワードの設定を求めます。",
        teamAssignmentsTitle: "チーム割り当て",
        teamAssignmentsDescription: "チームを使うと、共有サポート領域向けのルーティング範囲を再利用できます。",
        noTeams: "再利用可能な担当範囲を割り当てるにはチームを作成してください。",
        directCategoryScopeTitle: "直接カテゴリ範囲",
        directCategoryScopeDescription: "選択したチームから継承されるカテゴリに加えて、個別のカテゴリを追加します。",
        selectAll: "すべて選択",
        deselectAll: "すべて解除",
        inheritedFromTeams: "チームから継承",
      },
      teamForm: {
        createEyebrow: "チームを作成",
        editEyebrow: "チームを編集",
        createTitle: "新しいチーム",
        fallbackTitle: "チーム",
        description: "再利用可能なサポートチームを作成し、担当するチケットカテゴリを割り当てます。",
        teamName: "チーム名",
        teamNamePlaceholder: "請求オペレーション",
        descriptionLabel: "説明",
        descriptionPlaceholder: "請求書、支払い、サブスクリプションの問い合わせを担当",
        categoryCoverageTitle: "チームのカテゴリ範囲",
        categoryCoverageDescription: "このチームが自動的に担当するチケットカテゴリを選択します。",
      },
      roleForm: {
        createEyebrow: "カスタムロールを作成",
        editEyebrow: "カスタムロールを編集",
        createTitle: "新しいカスタムロール",
        fallbackTitle: "カスタムロール",
        description: "このワークスペース専用ロールに付与する権限を選択します。ロールプリセット管理は標準のオーナーと管理者ロールに限定されます。",
        roleName: "ロール名",
        roleNamePlaceholder: "エスカレーション担当",
        descriptionLabel: "説明",
        descriptionPlaceholder: "エスカレーション対応と高優先度ワークフローを調整",
        permissionsTitle: "権限",
        permissionsDescription: "このカスタムロールに付与する機能を選択します。",
        permissionsSearchPlaceholder: "名前または説明で権限を絞り込み",
        permissionsSearchAriaLabel: "権限を絞り込み",
        permissionsNoResults: "この条件に一致する権限はありません。",
      },
    },
    errors: {
      loadRoles: "ロールを読み込めません。",
      requiredMemberFields: "名前、メールアドレス、ロールは必須です。",
      assignAgentScope: "担当者には少なくとも 1 つのチームまたはチケットカテゴリを割り当ててください。",
      temporaryPasswordTooShort: "新しいワークスペースユーザーには 8 文字以上の仮パスワードが必要です。",
      saveUser: "ワークスペースユーザーを保存できません。",
      teamNameRequired: "チーム名は必須です。",
      teamCategoryRequired: "チームには少なくとも 1 つのチケットカテゴリを割り当ててください。",
      saveTeam: "チームを保存できません。",
      deleteTeam: "チームを削除できません。",
      removeUser: "ワークスペースユーザーを削除できません。",
      newPasswordTooShort: "新しいパスワードは 8 文字以上必要です。",
      passwordMismatch: "パスワード確認が一致しません。",
      updateLocalPassword: "ローカルパスワードを更新できません。",
      requiredRoleFields: "ロール名と説明は必須です。",
      saveRolePreset: "ロールプリセットを保存できません。",
      deleteRolePreset: "ロールプリセットを削除できません。",
    },
    confirmations: {
      deleteTeam: (name: string) => `${name} チームを削除しますか？`,
      removeMember: (name: string) => `${name} をこのワークスペースから削除しますか？`,
      deleteRole: (role: string) => `${role} ロールプリセットを削除しますか？`,
    },
    roleLabels: {
      Owner: "オーナー",
      Admin: "管理者",
      Agent: "担当者",
      "Customer Manager": "顧客管理者",
      "Knowledge Manager": "ナレッジ管理者",
      "Reporting Manager": "レポート管理者",
      Viewer: "閲覧者",
      "Team Member": "チームメンバー",
    },
    roleDetails: {
      "Full access to workspace settings, team permissions, branding, integrations, billing, and operational data.": "ワークスペース設定、チーム権限、ブランディング、連携、請求、運用データにフルアクセスできます。",
      "Runs the workspace day to day, including users, categories, ticket operations, CRM updates, and knowledge management.": "ユーザー、カテゴリ、チケット運用、CRM 更新、ナレッジ管理を含む日常運用を担います。",
      "Works tickets, reads customer context, and creates knowledge articles inside the categories assigned by an admin.": "管理者が割り当てたカテゴリ内でチケット対応、顧客情報の確認、ナレッジ記事の作成を行います。",
      "Updates customer accounts, contacts, products, and notes without changing ticket workflows.": "チケット運用を変更せずに、顧客アカウント、連絡先、製品、メモを更新します。",
      "Maintains collections and published content for the knowledge base without managing users or customer records.": "ユーザーや顧客情報を管理せずに、ナレッジベースのコレクションと公開コンテンツを管理します。",
      "Builds, runs, exports, and schedules reports without managing users, tickets, or customer records.": "ユーザー、チケット、顧客情報を管理せずに、レポートの作成、実行、エクスポート、スケジュールを行います。",
      "Read-only access across tickets, customers, and knowledge content, with visibility into reporting dashboards.": "チケット、顧客、ナレッジを読み取り専用で参照でき、レポートダッシュボードも閲覧できます。",
    },
    capabilityLabels: {
      "Tickets : View": "チケットを閲覧",
      "Tickets : Edit": "チケットを編集",
      "Tickets : Delete": "チケットを削除",
      "Customers : View": "顧客を閲覧",
      "Customers : Edit": "顧客を編集",
      "Customers : Delete": "顧客を削除",
      "Knowledge : View": "ナレッジ記事を閲覧",
      "Knowledge : Create": "ナレッジ記事を作成",
      "Knowledge : Manage": "ナレッジベースを管理",
      "Users : View": "ユーザーを閲覧",
      "Users : Manage": "ユーザーを管理",
      "Notifications : View": "サービス通知を閲覧",
      "Archive : View": "アーカイブを閲覧",
      "Archive : Edit": "アーカイブを編集",
      "Settings : View": "設定を閲覧",
      "Settings : Manage": "設定を管理",
      "Calendar : View": "カレンダーを閲覧",
      "Calendar : Create": "カレンダー項目を作成",
      "Calendar : Manage": "カレンダー割り当て種別を管理",
      "API : Access": "API アクセス",
      "Billing : Manage": "請求を管理",
      "Items : View": "項目を閲覧",
      "Items : Manage": "項目を管理",
      "Expenses : View": "経費を閲覧",
      "Expenses : Edit": "経費を編集",
      "Expenses : Delete": "経費を削除",
      "Expenses : View all": "経費をすべて閲覧",
      "Expenses : Edit all": "経費をすべて編集",
      "Vendors : View": "取引先を閲覧",
      "Vendors : Edit": "取引先を編集",
      "Vendors : Delete": "取引先を削除",
      "Reporting : View": "レポートを閲覧",
      "Reporting : Manage": "レポートを管理",
    },
    capabilityDetails: {
      "Open ticket lists and review ticket details without making changes.": "変更を加えずにチケット一覧を開き、詳細を確認できます。",
      "Create, update, assign, and resolve tickets.": "チケットの作成、更新、割り当て、解決ができます。",
      "Permanently delete tickets from the workspace.": "ワークスペースからチケットを完全に削除できます。",
      "Open customer accounts and review account details without making changes.": "変更を加えずに顧客アカウントを開き、詳細を確認できます。",
      "Update customer accounts, contacts, products, and lifecycle notes.": "顧客アカウント、連絡先、製品、ライフサイクルメモを更新できます。",
      "Permanently delete customer accounts and their related tickets.": "顧客アカウントと関連チケットを完全に削除できます。",
      "Open knowledge base collections and read published workspace articles.": "ナレッジベースのコレクションを開き、公開済みワークスペース記事を閲覧できます。",
      "Draft and publish knowledge base articles.": "ナレッジベース記事を下書きし、公開できます。",
      "Create collections and manage published knowledge content.": "コレクションを作成し、公開済みナレッジコンテンツを管理できます。",
      "Open the users directory and review role assignments.": "ユーザーディレクトリを開き、ロール割り当てを確認できます。",
      "Invite, update, and remove Users.": "ユーザーの招待、更新、削除ができます。",
      "Open service notices and retrieve platform announcements for this workspace.": "サービス通知を開き、このワークスペース向けのプラットフォーム告知を取得できます。",
      "Open the archive view and inspect archived workspace data.": "アーカイブ画面を開き、保存済みワークスペースデータを確認できます。",
      "Delete archived records and run archive maintenance actions.": "アーカイブ済みレコードの削除とアーカイブ保守操作を実行できます。",
      "Open the settings workspace and review workspace configuration.": "設定ワークスペースを開き、ワークスペース設定を確認できます。",
      "Update branding, ticket categories, and administrative workspace settings.": "ブランディング、チケットカテゴリ、管理用ワークスペース設定を更新できます。",
      "Open the calendar and review scheduled items without making changes.": "変更を加えずにカレンダーを開き、予定を確認できます。",
      "Create, update, and delete scheduled calendar items without managing assignment types.": "割り当て種別を管理せずに、予定済みカレンダー項目を作成、更新、削除できます。",
      "Create, edit, and delete assignment types from the user calendar.": "ユーザーカレンダーの割り当て種別を作成、編集、削除できます。",
      "Generate personal API keys and access workspace API endpoints programmatically.": "個人 API キーを発行し、ワークスペース API エンドポイントへプログラムからアクセスできます。",
      "Update workspace commercial defaults and billing configuration.": "ワークスペースの商用初期設定と請求設定を更新できます。",
      "Open the items catalog and review pricing, SLA, and bundle relationships.": "項目カタログを開き、価格、SLA、バンドル関係を確認できます。",
      "Create, edit, and remove product plans and related items.": "製品プランと関連する項目を作成、編集、削除できます。",
      "Open the expenses ledger and review expense records without making changes.": "変更を加えずに経費台帳を開き、経費レコードを確認できます。",
      "Create and update expense records.": "経費レコードの作成と更新ができます。",
      "Permanently delete expense records from the workspace.": "ワークスペースから経費レコードを完全に削除できます。",
      "See every member's expenses, not just your own.": "自分の経費だけでなく、すべてのメンバーの経費を閲覧できます。",
      "Edit every member's expenses, not just your own.": "自分の経費だけでなく、すべてのメンバーの経費を編集できます。",
      "Open the vendor directory and review vendor details without making changes.": "変更を加えずに取引先ディレクトリを開き、取引先の詳細を確認できます。",
      "Create and update vendor records.": "取引先レコードの作成と更新ができます。",
      "Permanently delete vendors and their related expenses.": "取引先と関連する経費を完全に削除できます。",
      "Access dashboards and reporting views.": "ダッシュボードとレポート画面にアクセスできます。",
      "Build and manage report packs and exports.": "レポートパックとエクスポートを作成・管理できます。",
    },
  },
};

export const loginViewCopy = {
  en: {
    accountAccess: "Account access",
    passwordRecovery: "Password recovery",
    welcomeTitle: "Welcome back to your Kuii workspace",
    recoveryTitle: "Recover access to your Kuii workspace",
    welcomeDescription: "Sign in with your customer account to review open work, search shared knowledge, and stay aligned with your support team.",
    forcedResetWelcomeDescription: "Before workspace data is shown, finish the required password update for your local account.",
    recoveryDescription: "Request a secure reset link or set a fresh password so your team can get back into the workspace without waiting on manual support.",
    featureCards: [
      ["Secure workspace access", "Every login stays scoped to your company workspace and shared customer data."],
      ["Shared support context", "Pick up tickets, updates, and knowledge articles without switching tools."],
      ["Protected collaboration", "Role-based access keeps the right information available to the right people."],
    ],
    loginEyebrow: "Login",
    forcedResetEyebrow: "Password update required",
    setNewPasswordEyebrow: "Set a new password",
    resetPasswordEyebrow: "Reset password",
    signInTitle: "Sign in to your workspace",
    forcedResetTitle: "Choose a new password",
    choosePasswordTitle: "Choose a new password",
    requestResetTitle: "Request a password reset",
    signInDescription: "Use the email and password assigned to your customer account to continue.",
    forcedResetDescription: "Your sign-in was accepted, but access stays locked until this password is replaced.",
    choosePasswordDescription: "Reset links expire quickly and can only be used once, so choose a new password now.",
    requestResetDescription: "Enter the email address tied to your local account and we will send a secure reset link.",
    backToSignIn: "Back to sign in",
    email: "Email",
    emailPlaceholder: "name@company.com",
    password: "Password",
    passwordPlaceholder: "Enter your password",
    forgotPassword: "Forgot password?",
    signIn: "Sign in to workspace",
    signingIn: "Signing in…",
    forcedResetInfo: "Use your temporary password, then choose and confirm a new password before continuing to the workspace.",
    temporaryPassword: "Temporary password",
    temporaryPasswordPlaceholder: "Enter your temporary password",
    resetInfo: "This secure link is single-use. Set your new password now to finish regaining access.",
    newPassword: "New password",
    newPasswordPlaceholder: "Create a new password",
    confirmPassword: "Confirm password",
    confirmPasswordPlaceholder: "Repeat your new password",
    setNewPassword: "Set new password",
    updateRequiredPassword: "Update password",
    updatingPassword: "Updating password…",
    resetDisabled: "Password reset is currently disabled for this workspace. Contact your workspace administrator if you need help signing in.",
    resetRequestInfo: "If the email matches a local account, we will send a one-time reset link.",
    sendResetLink: "Send reset link",
    sendingResetLink: "Sending reset link…",
  },
  ja: {
    accountAccess: "アカウントアクセス",
    passwordRecovery: "パスワード復旧",
    welcomeTitle: "Kuii ワークスペースへようこそ",
    recoveryTitle: "Kuii ワークスペースへのアクセスを回復",
    welcomeDescription: "顧客アカウントでサインインして、未対応の作業を確認し、共有ナレッジを検索し、サポートチームと連携しましょう。",
    forcedResetWelcomeDescription: "ワークスペースのデータを表示する前に、ローカルアカウントで必須のパスワード更新を完了してください。",
    recoveryDescription: "安全な再設定リンクをリクエストするか、新しいパスワードを設定して、手動サポートを待たずにワークスペースへ戻れるようにします。",
    featureCards: [
      ["安全なワークスペースアクセス", "すべてのログインは会社のワークスペースと共有顧客データの範囲内に限定されます。"],
      ["共有されたサポート文脈", "ツールを切り替えずにチケット、更新、ナレッジ記事を引き継げます。"],
      ["保護されたコラボレーション", "ロールベースのアクセスにより、必要な人に必要な情報だけが表示されます。"],
    ],
    loginEyebrow: "ログイン",
    forcedResetEyebrow: "パスワード更新が必要です",
    setNewPasswordEyebrow: "新しいパスワードを設定",
    resetPasswordEyebrow: "パスワードを再設定",
    signInTitle: "ワークスペースにサインイン",
    forcedResetTitle: "新しいパスワードを選択",
    choosePasswordTitle: "新しいパスワードを選択",
    requestResetTitle: "パスワード再設定をリクエスト",
    signInDescription: "顧客アカウントに割り当てられたメールアドレスとパスワードで続行します。",
    forcedResetDescription: "サインインは承認されましたが、このパスワードを変更するまでアクセスはロックされます。",
    choosePasswordDescription: "再設定リンクは短時間で期限切れになり、一度しか使えません。今すぐ新しいパスワードを設定してください。",
    requestResetDescription: "ローカルアカウントに紐づくメールアドレスを入力すると、安全な再設定リンクを送信します。",
    backToSignIn: "サインインに戻る",
    email: "メールアドレス",
    emailPlaceholder: "name@company.com",
    password: "パスワード",
    passwordPlaceholder: "パスワードを入力",
    forgotPassword: "パスワードを忘れた場合",
    signIn: "ワークスペースにサインイン",
    signingIn: "サインイン中…",
    forcedResetInfo: "一時パスワードを入力し、新しいパスワードを選択・確認してからワークスペースへ進んでください。",
    temporaryPassword: "一時パスワード",
    temporaryPasswordPlaceholder: "一時パスワードを入力",
    resetInfo: "この安全なリンクは一度だけ利用できます。アクセスを回復するため、今すぐ新しいパスワードを設定してください。",
    newPassword: "新しいパスワード",
    newPasswordPlaceholder: "新しいパスワードを作成",
    confirmPassword: "パスワード確認",
    confirmPasswordPlaceholder: "新しいパスワードを再入力",
    setNewPassword: "新しいパスワードを設定",
    updateRequiredPassword: "パスワードを更新",
    updatingPassword: "パスワードを更新中…",
    resetDisabled: "このワークスペースでは現在パスワード再設定が無効です。サインインできない場合は、ワークスペース管理者に連絡してください。",
    resetRequestInfo: "入力したメールアドレスがローカルアカウントと一致する場合、一度だけ使える再設定リンクを送信します。",
    sendResetLink: "再設定リンクを送信",
    sendingResetLink: "再設定リンクを送信中…",
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;

export const dashboardViewCopy = {
  en: {
    pageEyebrow: "Today · Support operations",
    pageTitle: "Console Overview",
    pageDescription: "Track ticket volume, queue health, and account context without leaving the dashboard.",
    assignedToYou: "Assigned to you",
    yourActiveTickets: "Your Active Tickets",
    updatedToday: "Updated today",
    resolvedToday: "Resolved Today",
    openedToday: "Opened today",
    ticketsCreated: "Tickets Created",
    priorityTicketsTitle: "Priority tickets",
    priorityTicketsDescription: "Urgent work from the ticket categories this user can access.",
    inFocus: (count: number) => `${count} in focus`,
    categoriesCount: (count: number) => `${count} categories`,
    moreCount: (count: number) => `+${count} more`,
    openDetails: "Open details →",
    customer: "Customer",
    customerOnDemand: "Customer profile loads on demand.",
    owner: "Owner",
    unassigned: "Unassigned",
    plan: "Plan",
    noPriorityTicketsTitle: "No priority tickets need attention right now.",
    noPriorityTicketsDescription: "New high-priority tickets in the user's accessible categories will show up here.",
    ticketVolumeTitle: "Ticket volume",
    ticketVolumeDescription: "Last 7 days across all support channels.",
    last7Days: "Last 7 days",
    calendar: "Calendar",
    scheduleTitle: "Schedule",
    scheduleDescription: (name: string) => `Today's assignments for ${name}.`,
    today: "Today",
    loadingPriorityTickets: "Loading priority tickets…",
    loadingSchedule: "Loading schedule…",
    assignmentsScheduled: (count: number) => `${count} assignment${count === 1 ? "" : "s"} scheduled`,
    activeNow: "Active now",
    upNext: "Up next",
    scheduledAssignment: "Scheduled assignment",
    noAssignmentNotes: "No notes added for this assignment.",
    noAssignmentsTitle: "No calendar assignments for today.",
    noAssignmentsDescription: "When this user is scheduled, the day's coverage and rotations will appear here.",
    serviceNoticeEyebrow: "Status",
    serviceNoticeTitle: "Service notice",
    serviceNoticeDescription: "Platform status and maintenance updates.",
    serviceNoticeOperational: "All systems operational",
    serviceNoticeOperationalDetail: "No incidents reported. Support tooling is running normally.",
    priorityLabels: {
      High: "High",
      Medium: "Medium",
      Low: "Low",
    },
    statusLabels: {
      Open: "Open",
      "In Progress": "In Progress",
      "Waiting customer": "Waiting customer",
      Pending: "Pending",
      Resolved: "Resolved",
    },
  },
  ja: {
    pageEyebrow: "今日 · サポート運用",
    pageTitle: "コンソール概要",
    pageDescription: "ダッシュボードを離れずに、チケット件数、キューの健全性、アカウント状況を確認できます。",
    assignedToYou: "あなたに割り当て",
    yourActiveTickets: "担当中のチケット",
    updatedToday: "本日更新",
    resolvedToday: "本日解決",
    openedToday: "本日作成",
    ticketsCreated: "作成チケット数",
    priorityTicketsTitle: "優先対応チケット",
    priorityTicketsDescription: "このユーザーがアクセスできるカテゴリ内の緊急対応チケットです。",
    inFocus: (count: number) => `${count} 件を注視中`,
    categoriesCount: (count: number) => `${count} カテゴリ`,
    moreCount: (count: number) => `+${count} 件`,
    openDetails: "詳細を開く →",
    customer: "顧客",
    customerOnDemand: "顧客プロフィールは必要時に読み込まれます。",
    owner: "担当者",
    unassigned: "未割り当て",
    plan: "プラン",
    noPriorityTicketsTitle: "現在、優先対応が必要なチケットはありません。",
    noPriorityTicketsDescription: "アクセス可能なカテゴリに新しい高優先度チケットが入ると、ここに表示されます。",
    ticketVolumeTitle: "チケット件数",
    ticketVolumeDescription: "直近 7 日間の全サポートチャネルの件数です。",
    last7Days: "直近 7 日間",
    calendar: "カレンダー",
    scheduleTitle: "予定",
    scheduleDescription: (name: string) => `${name} さんの本日の割り当てです。`,
    today: "今日",
    loadingPriorityTickets: "優先対応チケットを読み込み中…",
    loadingSchedule: "予定を読み込み中…",
    assignmentsScheduled: (count: number) => `${count} 件の割り当て`,
    activeNow: "現在対応中",
    upNext: "次の予定",
    scheduledAssignment: "予定済みの割り当て",
    noAssignmentNotes: "この割り当てにはまだメモがありません。",
    noAssignmentsTitle: "本日のカレンダー割り当てはありません。",
    noAssignmentsDescription: "このユーザーが予定に入ると、その日の対応体制やローテーションがここに表示されます。",
    serviceNoticeEyebrow: "ステータス",
    serviceNoticeTitle: "サービス通知",
    serviceNoticeDescription: "プラットフォームの稼働状況とメンテナンス情報です。",
    serviceNoticeOperational: "すべてのシステムが正常稼働中",
    serviceNoticeOperationalDetail: "報告されている障害はありません。サポートツールは正常に稼働しています。",
    priorityLabels: {
      High: "高",
      Medium: "中",
      Low: "低",
    },
    statusLabels: {
      Open: "未対応",
      "In Progress": "対応中",
      "Waiting customer": "顧客待ち",
      Pending: "保留中",
      Resolved: "解決済み",
    },
  },
} satisfies Record<AppLanguage, {
  pageEyebrow: string;
  pageTitle: string;
  pageDescription: string;
  assignedToYou: string;
  yourActiveTickets: string;
  updatedToday: string;
  resolvedToday: string;
  openedToday: string;
  ticketsCreated: string;
  priorityTicketsTitle: string;
  priorityTicketsDescription: string;
  inFocus: (count: number) => string;
  categoriesCount: (count: number) => string;
  moreCount: (count: number) => string;
  openDetails: string;
  customer: string;
  customerOnDemand: string;
  owner: string;
  unassigned: string;
  plan: string;
  noPriorityTicketsTitle: string;
  noPriorityTicketsDescription: string;
  ticketVolumeTitle: string;
  ticketVolumeDescription: string;
  last7Days: string;
  calendar: string;
  scheduleTitle: string;
  scheduleDescription: (name: string) => string;
  today: string;
  loadingPriorityTickets: string;
  loadingSchedule: string;
  assignmentsScheduled: (count: number) => string;
  activeNow: string;
  upNext: string;
  scheduledAssignment: string;
  noAssignmentNotes: string;
  noAssignmentsTitle: string;
  noAssignmentsDescription: string;
  serviceNoticeEyebrow: string;
  serviceNoticeTitle: string;
  serviceNoticeDescription: string;
  serviceNoticeOperational: string;
  serviceNoticeOperationalDetail: string;
  priorityLabels: Record<"High" | "Medium" | "Low", string>;
  statusLabels: Record<"Open" | "In Progress" | "Waiting customer" | "Pending" | "Resolved", string>;
}>;

export const calendarViewCopy = {
  en: {
    assignmentTypeLabels: {
      "On-call": "On-call",
      "Task rotation": "Task rotation",
      Coverage: "Coverage",
    },
    youSuffix: " · You",
    dayUnitShort: "d",
    pageEyebrow: "Team operations",
    pageTitle: "User calendar",
    pageDescription: "Schedule on-call coverage, queue captains, task rotations, and ownership across the support team in weekly and monthly calendar views.",
    editAssignmentTypes: "Edit assignment types",
    scheduledThis: (viewMode: "week" | "month") => `Scheduled this ${viewMode}`,
    assignmentsVisibleIn: (viewMode: "week" | "month") => `Assignments visible in ${viewMode === "week" ? "the current week" : "the current month"}.`,
    membersCovered: "Members covered",
    membersCoveredDetail: "Active members with at least one visible assignment.",
    onCallCoverage: "On-call coverage",
    onCallCoverageWeekDetail: "Days with explicit on-call coverage this week.",
    onCallCoverageMonthDetail: "Days in the month with explicit on-call coverage.",
    rotationsActive: "Rotations active",
    rotationsActiveDetail: (viewMode: "week" | "month") => `Task rotation blocks visible in this ${viewMode}.`,
    calendarView: "Calendar view",
    calendarViewDescription: "Toggle between a single-row weekly calendar and a month calendar layout.",
    thisWeek: "This week",
    thisMonth: "This month",
    scheduleAssignment: "Schedule assignment",
    weekly: "Weekly",
    monthly: "Monthly",
    prev: "← Prev",
    next: "Next →",
    allTeams: "All teams",
    allTeamMembers: "All team members",
    allAssignmentTypes: "All assignment types",
    showScheduledTickets: "Show scheduled tickets",
    hideScheduledTickets: "Hide scheduled tickets",
    showTeamSchedule: "Show team schedule",
    hideTeamSchedule: "Hide team schedule",
    loadingScheduledTickets: "Loading tickets…",
    ticketBadge: "Ticket",
    ticketNoCustomer: "No customer linked",
    assignmentsCount: (count: number) => `${count} assignment${count === 1 ? "" : "s"}`,
    itemsCount: (count: number) => `${count} item${count === 1 ? "" : "s"}`,
    noAssignmentsThisWeek: "No assignments scheduled in this week.",
    loadingCalendar: "Loading calendar…",
    coverageGaps: "Coverage gaps",
    loadingCoverageGaps: "Loading coverage gaps…",
    unscheduledTeammates: "Unscheduled teammates",
    unscheduledTeammatesDescription: (viewMode: "week" | "month") => `Active teammates without a visible assignment in the selected ${viewMode}.`,
    assign: "Assign",
    everyMemberCovered: (viewMode: "week" | "month") => `Every active team member has coverage in the selected ${viewMode}.`,
    closeScheduleAssignment: "Close schedule assignment",
    scheduleEditor: "Schedule editor",
    updateAssignment: "Update assignment",
    createAssignment: "Create assignment",
    scheduleEditorDescription: "Assign coverage, rotations, or recurring ownership for the team calendar.",
    assignmentTitle: "Assignment title",
    assignmentType: "Assignment type",
    team: "Team",
    teamMember: "Team member",
    start: "Start",
    end: "End",
    notes: "Notes",
    assignmentTitlePlaceholder: "Primary on-call",
    notesPlaceholder: "Add escalation notes, customer focus, or rotation context.",
    deleteAssignment: "Delete assignment",
    cancel: "Cancel",
    saving: "Saving…",
    saveChanges: "Save changes",
    createAssignmentAction: "Create assignment",
    closeAssignmentTypeDialog: "Close assignment type dialog",
    calendarSettings: "Calendar settings",
    assignmentTypesTitle: "Edit assignment types",
    assignmentTypesDescription: "Create, rename, and delete assignment types for the teams you can manage. Owners and admins can manage assignment types for every team.",
    newAssignmentType: "New assignment type",
    newAssignmentTypePlaceholder: "Customer success review",
    addType: "Add type",
    existingTypes: "Existing types",
    existingTypesDescription: "Changes apply only to the selected team.",
    typesCount: (count: number) => `${count} type${count === 1 ? "" : "s"}`,
    availableWhenScheduling: "Available when scheduling assignments for this team.",
    edit: "Edit",
    save: "Save",
    delete: "Delete",
    noAssignmentTypesYet: "No assignment types exist for this team yet.",
    done: "Done",
    activeNow: "Active now",
    upNext: "Up next",
    scheduledAssignment: "Scheduled assignment",
    noType: "No type",
    untitledAssignment: "Untitled assignment",
    defaultNoNotes: "No notes added for this assignment.",
    errorLoadTypes: "Unable to load assignment types right now.",
    errorRequired: "Team, title, team member, start, and end time are required.",
    errorInvalidTime: "Enter a valid start and end time.",
    errorEndBeforeStart: "End time must be after the start time.",
    errorSaveAssignment: "Unable to save the assignment right now.",
    errorUpgradeFutureEvents: "Upgrade to allow further future calendar events.",
    errorEnterNewType: "Enter a name for the new assignment type.",
    errorSelectTeamFirst: "Select a team before creating an assignment type.",
    errorTypeExists: "That assignment type already exists for the selected team.",
    errorCreateType: "Unable to create the assignment type right now.",
    errorEnterTypeName: "Enter a name for the assignment type.",
    errorUpdateType: "Unable to update the assignment type right now.",
    errorDeleteType: "Unable to delete the assignment type right now.",
    confirmDeleteType: (name: string) => `Delete the ${name} assignment type for this team? Existing assignments will keep their current label.`,
    confirmDeleteAssignment: "Delete this schedule assignment?",
    errorDeleteAssignment: "Unable to delete the assignment right now.",
  },
  ja: {
    assignmentTypeLabels: {
      "On-call": "オンコール",
      "Task rotation": "タスクローテーション",
      Coverage: "カバレッジ",
    },
    youSuffix: " · あなた",
    dayUnitShort: "日",
    pageEyebrow: "チーム運用",
    pageTitle: "ユーザーカレンダー",
    pageDescription: "週表示と月表示のカレンダーで、オンコール体制、キュー担当、タスクローテーション、担当割り当てを管理します。",
    editAssignmentTypes: "割り当て種別を編集",
    scheduledThis: (viewMode: "week" | "month") => viewMode === "week" ? "今週の予定数" : "今月の予定数",
    assignmentsVisibleIn: (viewMode: "week" | "month") => viewMode === "week" ? "現在の週に表示されている割り当てです。" : "現在の月に表示されている割り当てです。",
    membersCovered: "対応メンバー数",
    membersCoveredDetail: "表示中の割り当てを少なくとも 1 件持つアクティブメンバー数です。",
    onCallCoverage: "オンコール対応率",
    onCallCoverageWeekDetail: "今週オンコール担当が明示されている日数です。",
    onCallCoverageMonthDetail: "今月オンコール担当が明示されている日数です。",
    rotationsActive: "稼働中のローテーション",
    rotationsActiveDetail: (viewMode: "week" | "month") => viewMode === "week" ? "今週表示中のタスクローテーション数です。" : "今月表示中のタスクローテーション数です。",
    calendarView: "カレンダービュー",
    calendarViewDescription: "1 行の週表示カレンダーと月表示カレンダーを切り替えます。",
    thisWeek: "今週",
    thisMonth: "今月",
    scheduleAssignment: "割り当てを追加",
    weekly: "週表示",
    monthly: "月表示",
    prev: "← 前へ",
    next: "次へ →",
    allTeams: "すべてのチーム",
    allTeamMembers: "すべてのチームメンバー",
    allAssignmentTypes: "すべての割り当て種別",
    showScheduledTickets: "予定チケットを表示",
    hideScheduledTickets: "予定チケットを非表示",
    showTeamSchedule: "チームの予定を表示",
    hideTeamSchedule: "チームの予定を非表示",
    loadingScheduledTickets: "チケットを読み込み中…",
    ticketBadge: "チケット",
    ticketNoCustomer: "顧客の紐付けなし",
    assignmentsCount: (count: number) => `${count} 件の割り当て`,
    itemsCount: (count: number) => `${count} 件`,
    noAssignmentsThisWeek: "この週に予定されている割り当てはありません。",
    loadingCalendar: "カレンダーを読み込み中…",
    coverageGaps: "カバレッジ不足",
    loadingCoverageGaps: "カバレッジ不足を読み込み中…",
    unscheduledTeammates: "未割り当てメンバー",
    unscheduledTeammatesDescription: (viewMode: "week" | "month") => viewMode === "week" ? "選択中の週に表示される割り当てがないアクティブメンバーです。" : "選択中の月に表示される割り当てがないアクティブメンバーです。",
    assign: "割り当てる",
    everyMemberCovered: (viewMode: "week" | "month") => viewMode === "week" ? "選択中の週では、すべてのアクティブメンバーにカバレッジがあります。" : "選択中の月では、すべてのアクティブメンバーにカバレッジがあります。",
    closeScheduleAssignment: "スケジュール割り当てを閉じる",
    scheduleEditor: "スケジュール編集",
    updateAssignment: "割り当てを更新",
    createAssignment: "割り当てを作成",
    scheduleEditorDescription: "チームカレンダーにカバレッジ、ローテーション、定期的な担当を割り当てます。",
    assignmentTitle: "割り当てタイトル",
    assignmentType: "割り当て種別",
    team: "チーム",
    teamMember: "担当メンバー",
    start: "開始",
    end: "終了",
    notes: "メモ",
    assignmentTitlePlaceholder: "一次オンコール",
    notesPlaceholder: "エスカレーションメモ、対応顧客、またはローテーションの背景を追加します。",
    deleteAssignment: "割り当てを削除",
    cancel: "キャンセル",
    saving: "保存中…",
    saveChanges: "変更を保存",
    createAssignmentAction: "割り当てを作成",
    closeAssignmentTypeDialog: "割り当て種別ダイアログを閉じる",
    calendarSettings: "カレンダー設定",
    assignmentTypesTitle: "割り当て種別を編集",
    assignmentTypesDescription: "管理可能なチーム向けに割り当て種別を作成、名称変更、削除できます。オーナーと管理者はすべてのチームの割り当て種別を管理できます。",
    newAssignmentType: "新しい割り当て種別",
    newAssignmentTypePlaceholder: "カスタマーサクセスレビュー",
    addType: "種別を追加",
    existingTypes: "既存の種別",
    existingTypesDescription: "変更は選択中のチームにのみ適用されます。",
    typesCount: (count: number) => `${count} 件`,
    availableWhenScheduling: "このチームの割り当てを作成するときに利用できます。",
    edit: "編集",
    save: "保存",
    delete: "削除",
    noAssignmentTypesYet: "このチームにはまだ割り当て種別がありません。",
    done: "完了",
    activeNow: "現在対応中",
    upNext: "次の予定",
    scheduledAssignment: "予定済みの割り当て",
    noType: "種別なし",
    untitledAssignment: "無題の割り当て",
    defaultNoNotes: "この割り当てにはまだメモがありません。",
    errorLoadTypes: "現在、割り当て種別を読み込めません。",
    errorRequired: "チーム、タイトル、担当メンバー、開始時刻、終了時刻は必須です。",
    errorInvalidTime: "有効な開始時刻と終了時刻を入力してください。",
    errorEndBeforeStart: "終了時刻は開始時刻より後である必要があります。",
    errorSaveAssignment: "現在、割り当てを保存できません。",
    errorUpgradeFutureEvents: "さらに先の将来のカレンダー予定を作成するにはアップグレードしてください。",
    errorEnterNewType: "新しい割り当て種別名を入力してください。",
    errorSelectTeamFirst: "割り当て種別を作成する前にチームを選択してください。",
    errorTypeExists: "選択したチームには同じ割り当て種別がすでに存在します。",
    errorCreateType: "現在、割り当て種別を作成できません。",
    errorEnterTypeName: "割り当て種別名を入力してください。",
    errorUpdateType: "現在、割り当て種別を更新できません。",
    errorDeleteType: "現在、割り当て種別を削除できません。",
    confirmDeleteType: (name: string) => `このチームの「${name}」割り当て種別を削除しますか？既存の割り当てには現在のラベルが残ります。`,
    confirmDeleteAssignment: "このスケジュール割り当てを削除しますか？",
    errorDeleteAssignment: "現在、割り当てを削除できません。",
  },
} satisfies Record<AppLanguage, {
  assignmentTypeLabels: Record<"On-call" | "Task rotation" | "Coverage", string>;
  youSuffix: string;
  dayUnitShort: string;
  pageEyebrow: string;
  pageTitle: string;
  pageDescription: string;
  editAssignmentTypes: string;
  scheduledThis: (viewMode: "week" | "month") => string;
  assignmentsVisibleIn: (viewMode: "week" | "month") => string;
  membersCovered: string;
  membersCoveredDetail: string;
  onCallCoverage: string;
  onCallCoverageWeekDetail: string;
  onCallCoverageMonthDetail: string;
  rotationsActive: string;
  rotationsActiveDetail: (viewMode: "week" | "month") => string;
  calendarView: string;
  calendarViewDescription: string;
  thisWeek: string;
  thisMonth: string;
  scheduleAssignment: string;
  weekly: string;
  monthly: string;
  prev: string;
  next: string;
  allTeams: string;
  allTeamMembers: string;
  allAssignmentTypes: string;
  showScheduledTickets: string;
  hideScheduledTickets: string;
  showTeamSchedule: string;
  hideTeamSchedule: string;
  loadingScheduledTickets: string;
  ticketBadge: string;
  ticketNoCustomer: string;
  assignmentsCount: (count: number) => string;
  itemsCount: (count: number) => string;
  noAssignmentsThisWeek: string;
  loadingCalendar: string;
  coverageGaps: string;
  loadingCoverageGaps: string;
  unscheduledTeammates: string;
  unscheduledTeammatesDescription: (viewMode: "week" | "month") => string;
  assign: string;
  everyMemberCovered: (viewMode: "week" | "month") => string;
  closeScheduleAssignment: string;
  scheduleEditor: string;
  updateAssignment: string;
  createAssignment: string;
  scheduleEditorDescription: string;
  assignmentTitle: string;
  assignmentType: string;
  team: string;
  teamMember: string;
  start: string;
  end: string;
  notes: string;
  assignmentTitlePlaceholder: string;
  notesPlaceholder: string;
  deleteAssignment: string;
  cancel: string;
  saving: string;
  saveChanges: string;
  createAssignmentAction: string;
  closeAssignmentTypeDialog: string;
  calendarSettings: string;
  assignmentTypesTitle: string;
  assignmentTypesDescription: string;
  newAssignmentType: string;
  newAssignmentTypePlaceholder: string;
  addType: string;
  existingTypes: string;
  existingTypesDescription: string;
  typesCount: (count: number) => string;
  availableWhenScheduling: string;
  edit: string;
  save: string;
  delete: string;
  noAssignmentTypesYet: string;
  done: string;
  activeNow: string;
  upNext: string;
  scheduledAssignment: string;
  noType: string;
  untitledAssignment: string;
  defaultNoNotes: string;
  errorLoadTypes: string;
  errorRequired: string;
  errorInvalidTime: string;
  errorEndBeforeStart: string;
  errorSaveAssignment: string;
  errorUpgradeFutureEvents: string;
  errorEnterNewType: string;
  errorSelectTeamFirst: string;
  errorTypeExists: string;
  errorCreateType: string;
  errorEnterTypeName: string;
  errorUpdateType: string;
  errorDeleteType: string;
  confirmDeleteType: (name: string) => string;
  confirmDeleteAssignment: string;
  errorDeleteAssignment: string;
}>;

export const ticketsViewCopy = {
  en: {
    none: "None",
    support: "Support",
    unassigned: "Unassigned",
    needsOwner: "Needs owner",
    assigned: "Assigned",
    current: "Current",
    you: "You",
    noMatchesFound: "No matches found",
    priorityLabels: {
      High: "High",
      Medium: "Medium",
      Low: "Low",
    },
    statusLabels: {
      Open: "Open",
      "In Progress": "In Progress",
      "Waiting customer": "Waiting customer",
      Pending: "Pending",
      Resolved: "Resolved",
    },
    tabLabels: {
      "All Tickets": "All Tickets",
      "Assigned to Me": "Assigned to Me",
      Unassigned: "Unassigned",
    },
    tableHeaders: {
      id: "Ticket ID",
      subject: "Subject",
      category: "Category",
      customer: "Customer",
      item: "Item",
      sla: "SLA",
      priority: "Priority",
      status: "Status",
      duration: "Duration",
      schedule: "Schedule",
      assignee: "Assignee",
      lastUpdate: "Last Update",
    },
    pageEyebrow: "Tickets · Support operations",
    pageTitle: "Ticket Console",
    pageDescription: "Review queue health, assignments, and urgent issues from one streamlined ticket workspace.",
    newTicket: "New Ticket",
    stats: {
      openTickets: { title: "Open Tickets", detail: "12% from last week" },
      avgResponse: {
        title: "Avg Response",
        emptyValue: "—",
        emptyDetail: "No tickets in this view have a recorded agent reply yet.",
        sampledDetail: (responded: number, total: number) => `Based on the first sent reply on ${responded} of ${total} tickets in view.`,
      },
      unassigned: { title: "Unassigned", detail: "Wait time: 14m" },
    },
    filterButton: "Filters",
    columnsButton: "Columns",
    resultsCount: (count: number) => `${count} result${count === 1 ? "" : "s"}`,
    columnsShown: (visible: number, total: number) => `${visible} of ${total} columns shown`,
    displayLimit: "Display limit",
    displayLimitOption: (count: number) => `${count} rows`,
    clearAll: "Clear all",
    noFilterHint: "Use filters to narrow by owner, status, schedule, customer, or product.",
    noMatchingTicketsTitle: "No matching tickets",
    noMatchingTicketsFiltered: "Your current tab and filter combination did not match any tickets.",
    noMatchingTicketsDefault: "No tickets match the current tab. Try another tab or create a new ticket.",
    clearFilters: "Clear filters",
    showingSummary: (shown: number, total: number, filterCount: number) => `Showing ${shown} of ${total} ticket${total === 1 ? "" : "s"}${filterCount > 0 ? ` after ${filterCount} filter${filterCount === 1 ? "" : "s"}` : ""}`,
    pageSummary: (page: number, totalPages: number) => `Page ${page} of ${totalPages}`,
    pageNumber: (page: number) => `Go to page ${page}`,
    previousPage: "Previous page",
    nextPage: "Next page",
    sortBy: (label: string, sortColumnActive: boolean, direction: "asc" | "desc") => `Sort by ${label}${sortColumnActive ? `, currently ${direction === "asc" ? "ascending" : "descending"}` : ""}`,
    selectedCount: (count: number) => `${count} selected`,
    noFiltersSelected: "No filters selected",
    filterBadgeTypeLabels: {
      customerIds: "Customer",
      products: "Product",
      categories: "Category",
      assignees: "Assignee",
      priorities: "Priority",
      statuses: "Status",
      assignment: "Owner",
      schedule: "Schedule",
      updatedWithin: "Updated",
      default: "Filter",
    },
    assignmentFilters: {
      any: { label: "Any owner", detail: "Include all assignment states." },
      mine: { label: "Assigned to me", detail: "Focus on your queue." },
      assigned: { label: "Assigned", detail: "Only tickets with an owner." },
      unassigned: { label: "Unassigned", detail: "Find tickets that need triage." },
    },
    scheduleFilters: {
      any: { label: "Any schedule", detail: "Show scheduled and unscheduled work." },
      scheduled: { label: "Scheduled", detail: "Tickets with any follow-up time set." },
      upcoming: { label: "Upcoming", detail: "Scheduled for now or later." },
      overdue: { label: "Overdue", detail: "Scheduled time has already passed." },
      unscheduled: { label: "Unscheduled", detail: "No follow-up time has been set." },
    },
    updatedFilters: {
      any: { label: "Any time", detail: "No last-updated restriction." },
      "24h": { label: "Last 24 hours", detail: "Recently active tickets." },
      "7d": { label: "Last 7 days", detail: "Current weekly queue." },
      "30d": { label: "Last 30 days", detail: "Broad monthly review." },
    },
    quickView: {
      close: "Close quick view",
      eyebrow: "Quick view",
      ticketDetails: "Ticket details",
      changeCategory: "Change ticket category",
      changeItem: "Change ticket item",
      changePriority: "Change ticket priority",
      changeStatus: "Change ticket status",
      paused: "Paused",
      left: "left",
      over: "over",
      description: "Description",
      duration: "Duration",
      created: (value: string) => `Created ${value}`,
      schedule: "Schedule",
      noScheduleSet: "No schedule set",
      open: "Open",
      customerDetails: "Customer details",
      email: "Email",
      phone: "Phone",
      region: "Region",
      productSeats: "Product seats",
      renewalDate: "Renewal date",
      products: "Products",
      emptyItem: "No item",
      none: "None",
      noCustomerProfile: "This ticket is not linked to a customer profile.",
      noSla: "No SLA",
      unknown: "Unknown",
    },
    columnDialog: {
      close: "Close ticket columns dialog",
      eyebrow: "Ticket table",
      title: "Show only the columns you need",
      description: "Tidy up the queue by hiding secondary fields. Ticket ID and Subject stay pinned so the table remains easy to scan.",
      listTitle: "Column list",
      listDescription: "Toggle individual fields and drag to reorder the main ticket grid.",
      resetAll: "Reset all",
      alwaysShown: "Always shown",
      visibleInTable: "Visible in the table",
      hiddenFromTable: "Hidden from the table",
      locked: "Locked",
      reorderHandleLabel: "Drag to reorder column",
      changesApplyImmediately: "Changes apply immediately to the table behind this dialog.",
      showAllColumns: "Show all columns",
      apply: "Apply",
    },
    filterDialog: {
      close: "Close ticket filters dialog",
      eyebrow: "Ticket filters",
      title: "Refine the queue",
      description: "Build a focused view with quick presets, owner rules, schedule controls, and multi-select filters. Search and tab selection still apply underneath.",
      quickPresets: {
        urgent: { label: "Urgent work", detail: "High priority and still active." },
        "needs-owner": { label: "Needs owner", detail: "Unassigned triage queue." },
        scheduled: { label: "Scheduled follow-ups", detail: "Tickets with callbacks planned." },
        recent: { label: "Updated today", detail: "Last 24 hours only." },
      },
      preview: "Preview",
      previewDescription: "tickets will remain after applying this filter set.",
      baseScope: "Base scope",
      baseScopeSummary: (tab: string, count: number) => `${tab} · ${count} tickets in scope`,
      baseScopeDescription: "Global header search does not change this queue view.",
      statusAndPriority: "Status and priority",
      statusAndPriorityDescription: "Pick one or many queue states.",
      priority: "Priority",
      status: "Status",
      assignment: "Assignment",
      assignmentDescription: "Mix owner rules with specific people.",
      assignees: "Assignees",
      searchAssignees: "Search assignees",
      noAssigneeSearchMatches: "No assignees match that search.",
      allAssignees: "All assignees",
      categoryAndProduct: "Category and product",
      categoryAndProductDescription: "Narrow to the workstream or item involved.",
      categories: "Categories",
      products: "Products",
      customerScheduleRecency: "Customer, schedule, and recency",
      customerScheduleRecencyDescription: "Combine account context with timing signals.",
      customers: "Customers",
      searchCustomers: "Search customers",
      noCustomerSearchMatches: "No customers match that search.",
      allCustomers: "All customers",
      schedule: "Schedule",
      lastUpdated: "Last updated",
      resetDraft: "Reset draft",
      clearActiveFilters: "Clear active filters",
      cancel: "Cancel",
      applyFilters: (count: number) => `Apply filters · ${count}`,
    },
    createDialog: {
      close: "Close create ticket dialog",
      eyebrow: "Create ticket",
      title: "New ticket",
      description: "Log a new support issue and open it immediately in the detail workspace.",
      subject: "Subject",
      subjectPlaceholder: "Briefly describe the issue",
      customer: "Customer",
      category: "Category",
      product: "Item",
      assignee: "Assignee",
      priority: "Priority",
      status: "Status",
      schedule: "End date",
      scheduleHelp: (timezone: string) => `Optional. Set an end date in ${timezone} to send a scheduled email reminder. Stored in UTC.`,
      scheduleStart: "Start date",
      scheduleStartHelp: (timezone: string) => `Optional. Pick when work on this ticket should begin in ${timezone} to send a scheduled email reminder. Stored in UTC.`,
      descriptionLabel: "Description",
      descriptionPlaceholder: "Describe the customer issue, context, and impact",
      noCustomerSelectedProductHelp: "No customer selected, so item can also be set to None.",
      noAssignedProductsHelp: "This customer has no assigned items yet, so Support is used by default.",
      assignedItemsHelp: (count: number) => `Showing ${count} assigned item${count === 1 ? "" : "s"} for this customer.`,
      ticketSla: (sla: string) => `Ticket SLA: ${sla}`,
      categoryFields: "Category fields",
      categoryFieldsDescription: (category: string) => `Extra details required for ${category} tickets.`,
      fieldsCount: (count: number) => `${count} field${count === 1 ? "" : "s"}`,
      selectField: (label: string) => `Select ${label}`,
      ticketPreview: "Ticket preview",
      noCustomerLinked: "No customer linked",
      noSla: "No SLA",
      noScheduleSet: "No schedule set",
      nextSteps: "What happens next",
      nextStepOne: "• The ticket opens immediately in the detail view.",
      nextStepTwo: "• Activity is logged automatically when the ticket is created.",
      cancel: "Cancel",
      createTicket: "Create ticket",
      fieldEmpty: "—",
      customFieldRequired: (label: string) => `${label} is required.`,
    },
  },
  ja: {
    none: "なし",
    support: "サポート",
    unassigned: "未割り当て",
    needsOwner: "担当者が必要",
    assigned: "割り当て済み",
    current: "現在",
    you: "あなた",
    noMatchesFound: "一致する候補はありません",
    priorityLabels: {
      High: "高",
      Medium: "中",
      Low: "低",
    },
    statusLabels: {
      Open: "未対応",
      "In Progress": "対応中",
      "Waiting customer": "顧客待ち",
      Pending: "保留中",
      Resolved: "解決済み",
    },
    tabLabels: {
      "All Tickets": "すべてのチケット",
      "Assigned to Me": "自分に割り当て",
      Unassigned: "未割り当て",
    },
    tableHeaders: {
      id: "チケット ID",
      subject: "件名",
      category: "カテゴリ",
      customer: "顧客",
      item: "項目",
      sla: "SLA",
      priority: "優先度",
      status: "ステータス",
      duration: "経過時間",
      schedule: "予定",
      assignee: "担当者",
      lastUpdate: "最終更新",
    },
    pageEyebrow: "チケット · サポート運用",
    pageTitle: "チケットコンソール",
    pageDescription: "キューの健全性、担当状況、緊急案件を 1 つのチケット画面で確認できます。",
    newTicket: "新しいチケット",
    stats: {
      openTickets: { title: "未解決チケット", detail: "先週比 12%" },
      avgResponse: {
        title: "平均応答",
        emptyValue: "—",
        emptyDetail: "この表示内で担当者返信が記録されたチケットはまだありません。",
        sampledDetail: (responded: number, total: number) => `表示中 ${total} 件のうち、初回送信返信がある ${responded} 件をもとに算出。`,
      },
      unassigned: { title: "未割り当て", detail: "待機時間: 14分" },
    },
    filterButton: "フィルター",
    columnsButton: "列",
    resultsCount: (count: number) => `${count} 件の結果`,
    columnsShown: (visible: number, total: number) => `${total} 列中 ${visible} 列を表示`,
    displayLimit: "表示件数",
    displayLimitOption: (count: number) => `${count} 件`,
    clearAll: "すべてクリア",
    noFilterHint: "フィルターで担当者、ステータス、予定、顧客、製品ごとに絞り込めます。",
    noMatchingTicketsTitle: "一致するチケットはありません",
    noMatchingTicketsFiltered: "現在のタブとフィルター条件に一致するチケットはありません。",
    noMatchingTicketsDefault: "現在のタブに一致するチケットはありません。別のタブを試すか、新しいチケットを作成してください。",
    clearFilters: "フィルターをクリア",
    showingSummary: (shown: number, total: number, filterCount: number) => `${total} 件中 ${shown} 件を表示${filterCount > 0 ? `（${filterCount} 件のフィルター適用後）` : ""}`,
    pageSummary: (page: number, totalPages: number) => `${page} / ${totalPages} ページ`,
    pageNumber: (page: number) => `${page} ページへ移動`,
    previousPage: "前のページ",
    nextPage: "次のページ",
    sortBy: (label: string, sortColumnActive: boolean, direction: "asc" | "desc") => `${label}で並び替え${sortColumnActive ? `（現在は${direction === "asc" ? "昇順" : "降順"}）` : ""}`,
    selectedCount: (count: number) => `${count} 件選択中`,
    noFiltersSelected: "フィルター未選択",
    filterBadgeTypeLabels: {
      customerIds: "顧客",
      products: "製品",
      categories: "カテゴリ",
      assignees: "担当者",
      priorities: "優先度",
      statuses: "ステータス",
      assignment: "担当",
      schedule: "予定",
      updatedWithin: "更新日",
      default: "フィルター",
    },
    assignmentFilters: {
      any: { label: "担当者を問わない", detail: "すべての割り当て状態を含みます。" },
      mine: { label: "自分に割り当て", detail: "自分のキューに絞り込みます。" },
      assigned: { label: "割り当て済み", detail: "担当者がいるチケットのみ。" },
      unassigned: { label: "未割り当て", detail: "トリアージが必要なチケットを探します。" },
    },
    scheduleFilters: {
      any: { label: "予定を問わない", detail: "予定あり・なしの両方を表示します。" },
      scheduled: { label: "予定あり", detail: "フォローアップ時刻が設定されているチケット。" },
      upcoming: { label: "今後の予定", detail: "現在以降に予定されているもの。" },
      overdue: { label: "期限超過", detail: "予定時刻をすでに過ぎたもの。" },
      unscheduled: { label: "予定なし", detail: "フォローアップ時刻が設定されていません。" },
    },
    updatedFilters: {
      any: { label: "期間を問わない", detail: "最終更新日の制限なし。" },
      "24h": { label: "過去 24 時間", detail: "最近動きのあったチケット。" },
      "7d": { label: "過去 7 日間", detail: "現在の週間キュー。" },
      "30d": { label: "過去 30 日間", detail: "月次レビュー向け。" },
    },
    quickView: {
      close: "クイックビューを閉じる",
      eyebrow: "クイックビュー",
      ticketDetails: "チケット詳細",
      changeCategory: "チケットカテゴリを変更",
      changeItem: "チケット項目を変更",
      changePriority: "チケット優先度を変更",
      changeStatus: "チケットステータスを変更",
      paused: "一時停止",
      left: "残り",
      over: "超過",
      description: "説明",
      duration: "経過時間",
      created: (value: string) => `作成: ${value}`,
      schedule: "予定",
      noScheduleSet: "予定なし",
      open: "開く",
      customerDetails: "顧客詳細",
      email: "メールアドレス",
      phone: "電話番号",
      region: "地域",
      productSeats: "製品シート数",
      renewalDate: "更新日",
      products: "製品",
      emptyItem: "未設定",
      none: "なし",
      noCustomerProfile: "このチケットは顧客プロフィールに関連付けられていません。",
      noSla: "SLA なし",
      unknown: "不明",
    },
    columnDialog: {
      close: "チケット列ダイアログを閉じる",
      eyebrow: "チケット表",
      title: "必要な列だけを表示",
      description: "補助的な項目を非表示にしてキューを整理します。チケット ID と件名は固定表示されるため、一覧性は維持されます。",
      listTitle: "列一覧",
      listDescription: "メインチケット表に表示する項目を切り替え、ドラッグで並び替えできます。",
      resetAll: "すべてリセット",
      alwaysShown: "常に表示",
      visibleInTable: "テーブルに表示",
      hiddenFromTable: "テーブルに非表示",
      locked: "固定",
      reorderHandleLabel: "ドラッグして列を並び替え",
      changesApplyImmediately: "変更は背後のテーブルにすぐ反映されます。",
      showAllColumns: "すべての列を表示",
      apply: "適用",
    },
    filterDialog: {
      close: "チケットフィルターダイアログを閉じる",
      eyebrow: "チケットフィルター",
      title: "キューを絞り込む",
      description: "クイックプリセット、担当ルール、予定条件、複数選択フィルターで集中しやすい表示を作れます。検索とタブ選択も引き続き適用されます。",
      quickPresets: {
        urgent: { label: "緊急対応", detail: "高優先度で未完了の案件。" },
        "needs-owner": { label: "担当者が必要", detail: "未割り当てのトリアージキュー。" },
        scheduled: { label: "予定済みフォローアップ", detail: "折り返し予定のあるチケット。" },
        recent: { label: "本日更新", detail: "過去 24 時間のみ。" },
      },
      preview: "プレビュー",
      previewDescription: "件のチケットがフィルター適用後に残ります。",
      baseScope: "対象範囲",
      baseScopeSummary: (tab: string, count: number) => `${tab} · 対象 ${count} 件`,
      baseScopeDescription: "ヘッダーのグローバル検索はこのキュー表示を変更しません。",
      statusAndPriority: "ステータスと優先度",
      statusAndPriorityDescription: "1 つまたは複数のキュー状態を選択します。",
      priority: "優先度",
      status: "ステータス",
      assignment: "担当",
      assignmentDescription: "担当ルールと特定担当者を組み合わせます。",
      assignees: "担当者",
      searchAssignees: "担当者を検索",
      noAssigneeSearchMatches: "検索条件に一致する担当者はいません。",
      allAssignees: "すべての担当者",
      categoryAndProduct: "カテゴリと製品",
      categoryAndProductDescription: "対象の業務領域または項目に絞ります。",
      categories: "カテゴリ",
      products: "製品",
      customerScheduleRecency: "顧客・予定・更新日時",
      customerScheduleRecencyDescription: "顧客情報とタイミング条件を組み合わせます。",
      customers: "顧客",
      searchCustomers: "顧客を検索",
      noCustomerSearchMatches: "検索条件に一致する顧客はいません。",
      allCustomers: "すべての顧客",
      schedule: "予定",
      lastUpdated: "最終更新",
      resetDraft: "下書きをリセット",
      clearActiveFilters: "有効なフィルターをクリア",
      cancel: "キャンセル",
      applyFilters: (count: number) => `フィルターを適用 · ${count}`,
    },
    createDialog: {
      close: "チケット作成ダイアログを閉じる",
      eyebrow: "チケット作成",
      title: "新しいチケット",
      description: "新しいサポート課題を記録し、そのまま詳細画面で開きます。",
      subject: "件名",
      subjectPlaceholder: "問題を簡潔に説明してください",
      customer: "顧客",
      category: "カテゴリ",
      product: "項目",
      assignee: "担当者",
      priority: "優先度",
      status: "ステータス",
      schedule: "終了日",
      scheduleHelp: (timezone: string) => `任意です。${timezone} の時刻で終了日を設定すると、予定されたメールリマインダーが送信されます。保存時は UTC で保持されます。`,
      scheduleStart: "開始日",
      scheduleStartHelp: (timezone: string) => `任意です。${timezone} の時刻でこのチケットの作業開始予定を設定すると、予定されたメールリマインダーが送信されます。保存時は UTC で保持されます。`,
      descriptionLabel: "説明",
      descriptionPlaceholder: "顧客の課題、背景、影響を記述してください",
      noCustomerSelectedProductHelp: "顧客が選択されていないため、項目は「なし」にも設定できます。",
      noAssignedProductsHelp: "この顧客にはまだ項目が割り当てられていないため、既定でサポートが使われます。",
      assignedItemsHelp: (count: number) => `この顧客に割り当てられている項目 ${count} 件を表示しています。`,
      ticketSla: (sla: string) => `チケット SLA: ${sla}`,
      categoryFields: "カテゴリ項目",
      categoryFieldsDescription: (category: string) => `${category} チケットに必要な追加情報です。`,
      fieldsCount: (count: number) => `${count} 項目`,
      selectField: (label: string) => `${label} を選択`,
      ticketPreview: "チケットプレビュー",
      noCustomerLinked: "顧客未設定",
      noSla: "SLA なし",
      noScheduleSet: "予定なし",
      nextSteps: "次に起きること",
      nextStepOne: "• チケットはすぐに詳細ビューで開きます。",
      nextStepTwo: "• チケット作成時にアクティビティが自動で記録されます。",
      cancel: "キャンセル",
      createTicket: "チケットを作成",
      fieldEmpty: "—",
      customFieldRequired: (label: string) => `${label} は必須です。`,
    },
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;

export const ticketDetailViewCopy = {
  en: {
    none: "None",
    support: "Support",
    unassigned: "Unassigned",
    assigned: "Assigned",
    needsOwner: "Needs owner",
    you: "You",
    noMatchesFound: "No matches found",
    priorityLabels: {
      High: "High",
      Medium: "Medium",
      Low: "Low",
    },
    statusLabels: {
      Open: "Open",
      "In Progress": "In Progress",
      "Waiting customer": "Waiting customer",
      Pending: "Pending",
      Resolved: "Resolved",
    },
    page: {
      backToTickets: "Back to tickets",
      eyebrow: "Ticket detail · Support workflow",
      changeItem: "Change ticket item",
      changePriority: "Change ticket priority",
      changeStatus: "Change ticket status",
      emptyItem: "No item",
      scheduled: "Scheduled",
      untitled: "Untitled ticket",
      deleteTicket: "Delete ticket",
      paused: "Paused",
      left: "left",
      over: "over",
    },
    assignee: {
      label: "Assignee",
      searchPlaceholder: "Search assignees",
    },
    description: {
      title: "Description",
      edit: "Edit",
      save: "Save description",
      placeholder: "Add a clear summary of the customer issue and desired outcome.",
      empty: "No description provided yet.",
    },
    linkedArticles: {
      title: "Linked articles",
      linkedCount: (count: number) => `${count} linked`,
      searchPlaceholder: "Search knowledge articles by title, summary, or keyword.",
      searchResults: "Search results",
      noSummary: "No summary added yet.",
      attach: "Attach",
      noMatchesTitle: "No matching articles found",
      noMatchesDescription: "Try a different title, keyword, or summary phrase.",
      allLinkedTitle: "All visible articles are already linked",
      allLinkedDescription: "Open the knowledge base to create more articles that can be attached here.",
      unavailableNotice: "Some previously linked articles are no longer available in this workspace view.",
      openLabel: (title: string) => `Open ${title}`,
      removeLabel: (title: string) => `Remove ${title}`,
    },
    attachments: {
      title: "Attachments",
      filesCount: (count: number) => `${count} files`,
      uploading: "Uploading…",
      selectFiles: "Select files",
      hint: "Upload files to attach them to this ticket.",
      uploadError: "Unable to upload attachments.",
      removeError: "Unable to remove attachment.",
      loading: "Loading attachments…",
      previewLabel: (name: string) => `Preview ${name}`,
      downloadLabel: (name: string) => `Download ${name}`,
      copyLinkLabel: (name: string) => `Copy link to ${name}`,
      linkCopied: "Link copied",
      linkCopyError: "Unable to copy link.",
      removeLabel: (name: string) => `Remove ${name}`,
      uploadedAt: (value: string) => `Uploaded ${value}`,
      previewTitle: "Attachment preview",
      closePreview: "Close attachment preview",
      close: "Close",
      loadingPreview: "Loading preview…",
      pdfUnavailableTitle: "PDF preview unavailable",
      textUnavailableTitle: "Text preview unavailable",
      previewUnavailableTitle: "Preview not available for this file type",
      previewUnavailableDescription: "Use the download action to open this attachment locally.",
      unableToLoadTextPreview: "Unable to load text preview.",
      unableToLoadPdfPreview: "Unable to load PDF preview.",
      sizeLimitHint: "Up to 100MB total per ticket.",
      sizeLimitError: (limitMb: number) => `Attachments for this ticket cannot exceed ${limitMb}MB in total. Choose smaller files or remove existing attachments before uploading.`,
      uploadProgressLabel: (percent: number) => `Uploading attachments, ${percent}% complete`,
      publicBadge: "Public link",
      publicHint: "Anyone with the link can download this file without signing in.",
      shareLabel: "Public download",
      shareOptionDefault: (state: string) => `Workspace default (${state})`,
      shareOptionAllow: "Allow",
      shareOptionDeny: "Deny",
      shareDefaultAllowed: "allowed",
      shareDefaultDenied: "denied",
      shareAria: (name: string) => `Public download setting for ${name}`,
      shareUpdateError: "Unable to update the sharing setting.",
      copyPublicLinkLabel: (name: string) => `Copy public link to ${name}`,
    },
    notes: {
      title: "Support notes",
      addNote: "Add note",
      helper: "Internal notes stay team-only. External notes join the same feed, queue customer delivery later, and pause SLA.",
      kinds: {
        internal: "Internal",
        external: "External",
      },
      helperChips: {
        internal: "Team-only",
        external: "Pauses SLA",
      },
      submit: {
        internal: "Add internal note",
        external: "Add external note",
      },
      placeholders: {
        internal: "Add internal notes, context, and next steps for the team.",
        external: "Write the update intended for the customer. Delivery will be wired in later.",
      },
      formattingHint: "Use / to add headings, lists, checklists, quotes, and code formatting.",
      editorFailed: "Formatting tools could not load. Refresh and try again.",
      emptyTitle: "No case notes added yet",
      emptyDescription: "Add the first note above to start a structured ticket history.",
      existingCaseNote: "Existing case note",
      imported: "Imported",
      importedInitials: "CN",
      customer: "Customer",
      customerReplyMeta: "Customer reply",
      external: "External",
      externalMeta: "Customer note · SLA paused",
      internal: "Internal",
    },
    details: {
      title: "Ticket details",
      category: "Category",
      changeCategory: "Change ticket category",
      sla: "SLA",
      pausedRemaining: (value: string) => `Paused with ${value} remaining`,
      untilBreach: (value: string) => `${value} until SLA breach`,
      overSla: (value: string) => `${value} over SLA`,
      schedule: "Schedule",
      openScheduleEditor: "Open schedule editor",
      noScheduleSet: "No schedule set",
      duration: "Duration",
      open: "Open",
      created: (value: string) => `Created ${value}`,
      unknown: "Unknown",
      lastUpdated: "Last updated",
      categoryFields: "Category fields",
      saveFields: "Save fields",
      selectField: (label: string) => `Select ${label}`,
      customFieldRequired: (label: string) => `${label} is required.`,
    },
    customer: {
      title: "Customer",
      email: "Email",
      phone: "Phone",
      region: "Region",
      renewalDate: "Renewal date",
      addCustomer: "Add customer",
      changeCustomer: "Change customer",
      changeCustomerTitle: "Change linked customer",
      addCustomerTitle: "Add linked customer",
      changeCustomerDescription: "Choose a different customer profile for this ticket.",
      addCustomerDescription: "Link this ticket to a customer profile.",
      openProfile: "Open customer profile",
      closeDialog: "Close customer picker",
      searchPlaceholder: "Search customers by company, contact, email, or region",
      noCustomerOption: "No customer linked",
      noCustomerOptionDetail: "Keep this ticket without a linked customer profile.",
      noCustomerMatches: "No customers match that search.",
      cancel: "Cancel",
      saveCustomer: "Save customer",
      noCustomerProfile: "This ticket is not linked to a customer profile.",
    },
    relatedTickets: {
      title: "Related Tickets",
      openTicket: (id: string) => `Open ticket ${id}`,
      emptyTitle: "No related tickets",
      emptyWithCustomer: "This customer has no other tickets in the current dataset.",
      emptyWithoutCustomer: "Tickets without a customer are not grouped into customer history.",
      viewAllTitle: "View all related tickets",
      viewAllDescription: (count: number) => `${count} related tickets for this customer.`,
      open: "Open →",
      modalClose: "Close related tickets",
      modalTitleWithCustomer: "Customer ticket history",
      modalTitleWithoutCustomer: "Related tickets",
      modalDescriptionWithCustomer: (company: string) => `All related tickets for ${company}.`,
      modalDescriptionWithoutCustomer: "Tickets without a customer do not have customer history.",
    },
    activity: {
      title: "Activity",
      emptyTitle: "No activity captured yet",
      emptyDescription: "Updates for this ticket will appear here as customer and support actions are logged.",
      viewAllTitle: "View full activity history",
      viewAllDescription: (count: number) => `${count} entries captured for this ticket.`,
      open: "Open →",
      modalClose: "Close activity timeline",
      modalTitle: "Ticket timeline",
      modalDescription: (id: string) => `Detailed activity for ticket #${id}.`,
    },
    scheduleModal: {
      closeLabel: "Close schedule dialog",
      title: "Schedule",
      heading: "Edit follow-up time",
      description: "Choose when this ticket should come back into the queue.",
      close: "Close",
      currentSchedule: "Current schedule",
      startDateTime: "Start date and time",
      noStartSet: "No start date",
      dateTime: "End date and time",
      timezoneHelp: (timezone: string) => `Shown in ${timezone}. Saved in UTC.`,
      clear: "Clear schedule",
      save: "Save schedule",
    },
    articlePreview: {
      closeLabel: "Close article preview",
      title: "Linked article",
      openFullPage: "Open full page",
      close: "Close",
      created: (value: string) => `Created ${value}`,
      noSummary: "No summary added yet.",
      noStructuredContentTitle: "No structured content yet",
      noStructuredContentDescription: "This article currently only has summary information.",
    },
  },
  ja: {
    none: "なし",
    support: "サポート",
    unassigned: "未割り当て",
    assigned: "割り当て済み",
    needsOwner: "担当者が必要",
    you: "あなた",
    noMatchesFound: "一致する候補はありません",
    priorityLabels: {
      High: "高",
      Medium: "中",
      Low: "低",
    },
    statusLabels: {
      Open: "未対応",
      "In Progress": "対応中",
      "Waiting customer": "顧客待ち",
      Pending: "保留中",
      Resolved: "解決済み",
    },
    page: {
      backToTickets: "チケット一覧へ戻る",
      eyebrow: "チケット詳細 · サポートワークフロー",
      changeItem: "チケット項目を変更",
      changePriority: "チケット優先度を変更",
      changeStatus: "チケットステータスを変更",
      emptyItem: "未設定",
      scheduled: "予定あり",
      untitled: "無題のチケット",
      deleteTicket: "チケットを削除",
      paused: "一時停止",
      left: "残り",
      over: "超過",
    },
    assignee: {
      label: "担当者",
      searchPlaceholder: "担当者を検索",
    },
    description: {
      title: "説明",
      edit: "編集",
      save: "説明を保存",
      placeholder: "顧客の課題と期待する解決内容を明確に記載してください。",
      empty: "説明はまだありません。",
    },
    linkedArticles: {
      title: "関連ナレッジ記事",
      linkedCount: (count: number) => `${count} 件をリンク済み`,
      searchPlaceholder: "タイトル、要約、キーワードでナレッジ記事を検索します。",
      searchResults: "検索結果",
      noSummary: "要約はまだありません。",
      attach: "添付",
      noMatchesTitle: "一致する記事はありません",
      noMatchesDescription: "別のタイトル、キーワード、要約フレーズで試してください。",
      allLinkedTitle: "表示中の記事はすべてリンク済みです",
      allLinkedDescription: "ここに添付できる記事を増やすには、ナレッジで新しい記事を作成してください。",
      unavailableNotice: "以前リンクした一部の記事は、このワークスペース表示では利用できません。",
      openLabel: (title: string) => `${title} を開く`,
      removeLabel: (title: string) => `${title} を削除`,
    },
    attachments: {
      title: "添付ファイル",
      filesCount: (count: number) => `${count} 件のファイル`,
      uploading: "アップロード中…",
      selectFiles: "ファイルを選択",
      hint: "ファイルをアップロードしてこのチケットに添付します。",
      uploadError: "添付ファイルをアップロードできませんでした。",
      removeError: "添付ファイルを削除できませんでした。",
      loading: "添付ファイルを読み込み中…",
      previewLabel: (name: string) => `${name} をプレビュー`,
      downloadLabel: (name: string) => `${name} をダウンロード`,
      copyLinkLabel: (name: string) => `${name} のリンクをコピー`,
      linkCopied: "リンクをコピーしました",
      linkCopyError: "リンクをコピーできませんでした。",
      removeLabel: (name: string) => `${name} を削除`,
      uploadedAt: (value: string) => `アップロード日時: ${value}`,
      previewTitle: "添付ファイルのプレビュー",
      closePreview: "添付ファイルのプレビューを閉じる",
      close: "閉じる",
      loadingPreview: "プレビューを読み込み中…",
      pdfUnavailableTitle: "PDF プレビューは利用できません",
      textUnavailableTitle: "テキストプレビューは利用できません",
      previewUnavailableTitle: "このファイル形式ではプレビューできません",
      previewUnavailableDescription: "ダウンロードしてローカルで開いてください。",
      unableToLoadTextPreview: "テキストプレビューを読み込めませんでした。",
      unableToLoadPdfPreview: "PDF プレビューを読み込めませんでした。",
      sizeLimitHint: "1 件のチケットにつき合計 100MB まで。",
      sizeLimitError: (limitMb: number) => `このチケットの添付ファイルは合計 ${limitMb}MB を超えられません。より小さいファイルを選ぶか、既存の添付ファイルを削除してからアップロードしてください。`,
      uploadProgressLabel: (percent: number) => `添付ファイルをアップロード中、${percent}% 完了`,
      publicBadge: "公開リンク",
      publicHint: "リンクを知っていれば、サインインせずに誰でもこのファイルをダウンロードできます。",
      shareLabel: "公開ダウンロード",
      shareOptionDefault: (state: string) => `ワークスペースの既定 (${state})`,
      shareOptionAllow: "許可",
      shareOptionDeny: "拒否",
      shareDefaultAllowed: "許可",
      shareDefaultDenied: "拒否",
      shareAria: (name: string) => `${name} の公開ダウンロード設定`,
      shareUpdateError: "共有設定を更新できませんでした。",
      copyPublicLinkLabel: (name: string) => `${name} の公開リンクをコピー`,
    },
    notes: {
      title: "サポートメモ",
      addNote: "メモを追加",
      helper: "内部メモはチーム内のみで共有されます。外部メモは同じフィードに追加され、後で顧客送信に利用され、SLA を一時停止します。",
      kinds: {
        internal: "内部",
        external: "外部",
      },
      helperChips: {
        internal: "チーム限定",
        external: "SLA を一時停止",
      },
      submit: {
        internal: "内部メモを追加",
        external: "外部メモを追加",
      },
      placeholders: {
        internal: "チーム向けの内部メモ、背景、次の対応を入力してください。",
        external: "顧客向けの更新内容を入力してください。送信連携は後で追加されます。",
      },
      formattingHint: "/ で見出し、リスト、チェックリスト、引用、コード装飾を追加できます。",
      editorFailed: "書式エディターを読み込めませんでした。再読み込みしてお試しください。",
      emptyTitle: "ケースメモはまだありません",
      emptyDescription: "最初のメモを追加して、チケット履歴を残しましょう。",
      existingCaseNote: "既存のケースメモ",
      imported: "取り込み済み",
      importedInitials: "CN",
      customer: "顧客",
      customerReplyMeta: "顧客からの返信",
      external: "外部",
      externalMeta: "顧客向けメモ · SLA 一時停止中",
      internal: "内部",
    },
    details: {
      title: "チケット詳細",
      category: "カテゴリ",
      changeCategory: "チケットカテゴリを変更",
      sla: "SLA",
      pausedRemaining: (value: string) => `${value} を残して一時停止中`,
      untilBreach: (value: string) => `SLA 違反まで ${value}`,
      overSla: (value: string) => `SLA を ${value} 超過`,
      schedule: "予定",
      openScheduleEditor: "予定編集を開く",
      noScheduleSet: "予定なし",
      duration: "経過時間",
      open: "開始から",
      created: (value: string) => `作成: ${value}`,
      unknown: "不明",
      lastUpdated: "最終更新",
      categoryFields: "カテゴリ項目",
      saveFields: "項目を保存",
      selectField: (label: string) => `${label} を選択`,
      customFieldRequired: (label: string) => `${label} は必須です。`,
    },
    customer: {
      title: "顧客",
      email: "メールアドレス",
      phone: "電話番号",
      region: "地域",
      renewalDate: "更新日",
      addCustomer: "顧客を追加",
      changeCustomer: "顧客を変更",
      changeCustomerTitle: "紐づく顧客を変更",
      addCustomerTitle: "顧客を紐づけ",
      changeCustomerDescription: "このチケットに紐づく顧客プロフィールを変更します。",
      addCustomerDescription: "このチケットに顧客プロフィールを紐づけます。",
      openProfile: "顧客プロフィールを開く",
      closeDialog: "顧客選択ダイアログを閉じる",
      searchPlaceholder: "会社名、担当者、メール、地域で顧客を検索",
      noCustomerOption: "顧客を紐づけない",
      noCustomerOptionDetail: "このチケットを顧客プロフィールに紐づけないままにします。",
      noCustomerMatches: "検索条件に一致する顧客はありません。",
      cancel: "キャンセル",
      saveCustomer: "顧客を保存",
      noCustomerProfile: "このチケットは顧客プロフィールに関連付けられていません。",
    },
    relatedTickets: {
      title: "関連チケット",
      openTicket: (id: string) => `チケット ${id} を開く`,
      emptyTitle: "関連チケットはありません",
      emptyWithCustomer: "この顧客には、現在のデータセット内に他のチケットがありません。",
      emptyWithoutCustomer: "顧客未設定のチケットは顧客履歴にまとめられません。",
      viewAllTitle: "すべての関連チケットを表示",
      viewAllDescription: (count: number) => `この顧客に関連するチケットは ${count} 件です。`,
      open: "開く →",
      modalClose: "関連チケットを閉じる",
      modalTitleWithCustomer: "顧客のチケット履歴",
      modalTitleWithoutCustomer: "関連チケット",
      modalDescriptionWithCustomer: (company: string) => `${company} に関連するすべてのチケットです。`,
      modalDescriptionWithoutCustomer: "顧客未設定のチケットには顧客履歴がありません。",
    },
    activity: {
      title: "アクティビティ",
      emptyTitle: "アクティビティはまだありません",
      emptyDescription: "顧客やサポートの操作が記録されると、ここに表示されます。",
      viewAllTitle: "すべてのアクティビティ履歴を表示",
      viewAllDescription: (count: number) => `このチケットには ${count} 件の記録があります。`,
      open: "開く →",
      modalClose: "アクティビティタイムラインを閉じる",
      modalTitle: "チケットタイムライン",
      modalDescription: (id: string) => `チケット #${id} の詳細なアクティビティです。`,
    },
    scheduleModal: {
      closeLabel: "予定ダイアログを閉じる",
      title: "予定",
      heading: "フォローアップ時刻を編集",
      description: "このチケットをいつキューに戻すかを設定します。",
      close: "閉じる",
      currentSchedule: "現在の予定",
      startDateTime: "開始日時",
      noStartSet: "開始日なし",
      dateTime: "終了日時",
      timezoneHelp: (timezone: string) => `${timezone} で表示されます。保存時は UTC です。`,
      clear: "予定をクリア",
      save: "予定を保存",
    },
    articlePreview: {
      closeLabel: "記事プレビューを閉じる",
      title: "関連記事",
      openFullPage: "全文ページを開く",
      close: "閉じる",
      created: (value: string) => `作成: ${value}`,
      noSummary: "要約はまだありません。",
      noStructuredContentTitle: "構造化コンテンツはまだありません",
      noStructuredContentDescription: "この記事には現在、要約情報のみがあります。",
    },
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;

export const customerDetailViewCopy = {
  en: {
    priorityLabels: {
      High: "High",
      Medium: "Medium",
      Low: "Low",
    },
    statusLabels: {
      Open: "Open",
      "In Progress": "In Progress",
      "Waiting customer": "Waiting customer",
      Pending: "Pending",
      Resolved: "Resolved",
    },
    healthLabels: {
      Healthy: "Healthy",
      Watch: "Watch",
      Escalated: "Escalated",
      Disabled: "Disabled",
    },
    buttons: {
      edit: "Edit",
      delete: "Delete",
      cancel: "Cancel",
      closeDialog: "Close dialog",
      close: "Close",
    },
    page: {
      backToAccounts: "Back to accounts",
      eyebrow: "Account details · Customer profile",
      editCustomerProfile: "Edit customer profile",
      locationNotSet: "Location not set",
      accountHealth: "Account health",
      changeCustomerHealth: "Change customer health",
    },
    stats: {
      productSeats: "Item seats",
      productSeatsDetail: "Allocated across the current item footprint",
      openTickets: "Open tickets",
      openTicketsDetail: "Current unresolved support load",
      resolved: "Resolved",
      resolvedDetail: "Closed tickets for this account",
      priorityRisk: "Priority risk",
      priorityRiskDetail: "High priority unresolved issues",
    },
    contacts: {
      title: "Contacts",
      editableDescription: "Create, edit, and remove customer contacts.",
      readonlyDescription: "Read-only customer contacts for this account.",
      total: (count: number) => `${count} total`,
      addContact: "Add contact",
      emptyTitle: "No contacts yet",
      emptyDetail: "Add a contact to keep account ownership current.",
      noPhone: "No phone",
    },
    products: {
      title: "Items",
      description: "Each item can renew on its own schedule or remain active without one.",
      scheduled: (count: number) => `${count} scheduled`,
      openEnded: (count: number) => `${count} open-ended`,
      addProduct: "Add item",
      noItemsTitle: "No items available",
      noItemsDetail: "Create an item in the Items view before assigning items to this account.",
      emptyTitle: "No items yet",
      emptyDetail: "Add an item to track renewal timing and footprint.",
      seats: (count: number) => `${count} seats`,
      noRenewalSchedule: "No renewal schedule",
      noRenewal: "No renewal",
      days: (count: number) => `${count} days`,
      renewalDate: "Renewal date",
      selectItem: "Select an item",
      inactiveItemError: "This item assignment is no longer linked to an active item. Choose one of the current items before saving.",
    },
    notes: {
      title: "Notes",
      description: "Internal account notes and recent context.",
      total: (count: number) => `${count} total`,
      addNote: "Add note",
      emptyTitle: "No notes yet",
      emptyDetail: "Add internal context for the next teammate review.",
      addAccountContext: "Add account context",
    },
    relatedTickets: {
      title: "Related tickets",
      description: "Support activity connected to this account.",
      total: (count: number) => `${count} total`,
      empty: "No tickets are linked to this account yet.",
      needsOwner: "Needs owner",
    },
    relatedExpenses: {
      title: "Related expenses",
      description: "Expenses linked to this account.",
      total: (count: number) => `${count} total`,
      empty: "No expenses are linked to this account yet.",
      unknownVendor: "Unknown vendor",
      noDescription: "No description",
    },
    history: {
      title: "Action history",
      description: "Track contact, item, and customer status changes for this customer.",
      events: (count: number) => `${count} events`,
      products: (count: number) => `Items · ${count}`,
      contacts: (count: number) => `Contacts · ${count}`,
      status: (count: number) => `Status · ${count}`,
      emptyTitle: "No history yet",
      emptyDetail: "New contact, item, and status changes will appear here.",
      entityLabels: {
        Product: "Item",
        Contact: "Contact",
        "Account status": "Account status",
      },
      actions: {
        updatedCustomerStatus: "Updated customer status",
        updatedContact: "Updated contact",
        addedContact: "Added contact",
        deletedContact: "Deleted contact",
        updatedProduct: "Updated item",
        addedProduct: "Added item",
        deletedProduct: "Deleted item",
      },
      details: {
        customerStatusChanged: (from: string, to: string) => `Customer status changed from ${from} to ${to}.`,
        contactSaved: (name: string, role: string, edited: boolean) => `${name} (${role}) ${edited ? "was updated" : "was added"}.`,
        contactRemoved: (name: string) => `${name} was removed from the account roster.`,
        productSaved: (name: string, seats: number, renewalDate?: string) => `${name} was updated with ${seats} seats${renewalDate ? ` and renewal on ${renewalDate}` : ""}.`,
        productAdded: (name: string, seats: number, renewalDate?: string) => `${name} was added with ${seats} seats${renewalDate ? ` and renewal on ${renewalDate}` : ""}.`,
        productRemoved: (name: string) => `${name} was removed from the account item footprint.`,
      },
    },
    forms: {
      customer: {
        eyebrow: "Edit customer",
        description: "Update the account name and location from the customer profile.",
        company: "Company",
        companyPlaceholder: "Acme, Inc.",
        location: "Location",
        locationPlaceholder: "Toronto, North America",
        save: "Save customer",
      },
      contact: {
        editEyebrow: "Edit contact",
        createEyebrow: "Create contact",
        defaultTitle: "Customer contact",
        newTitle: "New contact",
        description: "Create or update the people who own this account and keep outreach details current.",
        role: "Role",
        rolePlaceholder: "Primary",
        name: "Name",
        namePlaceholder: "Contact name",
        email: "Email",
        emailPlaceholder: "name@company.com",
        phone: "Phone",
        phonePlaceholder: "Optional phone number",
        save: "Save contact",
        create: "Create contact",
      },
      product: {
        editEyebrow: "Edit item",
        createEyebrow: "Create item assignment",
        editTitle: "Update customer item",
        createTitle: "Add item",
        description: "Assign an item to this account, set the seat count, and track renewal timing in one focused form.",
        item: "Item",
        seats: "Seats",
        renewalDate: "Renewal date",
        save: "Save item",
        create: "Create item",
      },
      note: {
        editEyebrow: "Edit note",
        createEyebrow: "Create note",
        editTitle: "Update account note",
        createTitle: "Add note",
        description: "Capture internal account context, reminders, and handoff details without leaving the customer profile.",
        note: "Note",
        save: "Save note",
        create: "Create note",
      },
    },
  },
  ja: {
    priorityLabels: {
      High: "高",
      Medium: "中",
      Low: "低",
    },
    statusLabels: {
      Open: "未対応",
      "In Progress": "対応中",
      "Waiting customer": "顧客待ち",
      Pending: "保留中",
      Resolved: "解決済み",
    },
    healthLabels: {
      Healthy: "健全",
      Watch: "要注意",
      Escalated: "エスカレーション",
      Disabled: "無効",
    },
    buttons: {
      edit: "編集",
      delete: "削除",
      cancel: "キャンセル",
      closeDialog: "ダイアログを閉じる",
      close: "閉じる",
    },
    page: {
      backToAccounts: "アカウント一覧へ戻る",
      eyebrow: "アカウント詳細 · 顧客プロフィール",
      editCustomerProfile: "顧客プロフィールを編集",
      locationNotSet: "所在地未設定",
      accountHealth: "アカウント健全性",
      changeCustomerHealth: "顧客健全性を変更",
    },
    stats: {
      productSeats: "項目シート数",
      productSeatsDetail: "現在の項目構成全体に割り当て済み",
      openTickets: "未解決チケット",
      openTicketsDetail: "現在の未解決サポート負荷",
      resolved: "解決済み",
      resolvedDetail: "このアカウントで完了したチケット",
      priorityRisk: "優先リスク",
      priorityRiskDetail: "高優先度の未解決課題",
    },
    contacts: {
      title: "連絡先",
      editableDescription: "顧客連絡先の作成、編集、削除を行います。",
      readonlyDescription: "このアカウントの顧客連絡先を閲覧できます。",
      total: (count: number) => `合計 ${count} 件`,
      addContact: "連絡先を追加",
      emptyTitle: "連絡先はまだありません",
      emptyDetail: "アカウント担当を最新に保つため、連絡先を追加してください。",
      noPhone: "電話番号なし",
    },
    products: {
      title: "項目",
      description: "各項目は個別の更新スケジュールを持つことも、設定なしで継続することもできます。",
      scheduled: (count: number) => `更新予定 ${count} 件`,
      openEnded: (count: number) => `期限未設定 ${count} 件`,
      addProduct: "項目を追加",
      noItemsTitle: "利用可能な項目がありません",
      noItemsDetail: "このアカウントに項目を割り当てる前に、項目画面で項目を作成してください。",
      emptyTitle: "項目はまだありません",
      emptyDetail: "更新時期と利用状況を追跡するため、項目を追加してください。",
      seats: (count: number) => `${count} シート`,
      noRenewalSchedule: "更新予定なし",
      noRenewal: "更新なし",
      days: (count: number) => `${count} 日`,
      renewalDate: "更新日",
      selectItem: "項目を選択",
      inactiveItemError: "この項目割り当ては有効な項目に紐付いていません。保存前に現在の項目を選択してください。",
    },
    notes: {
      title: "メモ",
      description: "内部向けのアカウントメモと最近の背景情報です。",
      total: (count: number) => `合計 ${count} 件`,
      addNote: "メモを追加",
      emptyTitle: "メモはまだありません",
      emptyDetail: "次の担当者レビューに向けて内部コンテキストを追加してください。",
      addAccountContext: "アカウントの背景を追加",
    },
    relatedTickets: {
      title: "関連チケット",
      description: "このアカウントに紐づくサポート対応です。",
      total: (count: number) => `合計 ${count} 件`,
      empty: "このアカウントに紐づくチケットはまだありません。",
      needsOwner: "担当者が必要",
    },
    relatedExpenses: {
      title: "関連経費",
      description: "このアカウントに紐づく経費です。",
      total: (count: number) => `合計 ${count} 件`,
      empty: "このアカウントに紐づく経費はまだありません。",
      unknownVendor: "不明なベンダー",
      noDescription: "説明なし",
    },
    history: {
      title: "変更履歴",
      description: "この顧客に対する連絡先、項目、ステータス変更を追跡します。",
      events: (count: number) => `${count} 件のイベント`,
      products: (count: number) => `項目 · ${count}`,
      contacts: (count: number) => `連絡先 · ${count}`,
      status: (count: number) => `ステータス · ${count}`,
      emptyTitle: "履歴はまだありません",
      emptyDetail: "新しい連絡先、項目、ステータス変更がここに表示されます。",
      entityLabels: {
        Product: "項目",
        Contact: "連絡先",
        "Account status": "アカウント状態",
      },
      actions: {
        updatedCustomerStatus: "顧客ステータスを更新",
        updatedContact: "連絡先を更新",
        addedContact: "連絡先を追加",
        deletedContact: "連絡先を削除",
        updatedProduct: "項目を更新",
        addedProduct: "項目を追加",
        deletedProduct: "項目を削除",
      },
      details: {
        customerStatusChanged: (from: string, to: string) => `顧客ステータスが ${from} から ${to} に変更されました。`,
        contactSaved: (name: string, role: string, edited: boolean) => `${name}（${role}）を${edited ? "更新" : "追加"}しました。`,
        contactRemoved: (name: string) => `${name} をアカウントの連絡先一覧から削除しました。`,
        productSaved: (name: string, seats: number, renewalDate?: string) => `${name} を ${seats} シートで更新しました${renewalDate ? `。更新日は ${renewalDate} です` : "。"}`,
        productAdded: (name: string, seats: number, renewalDate?: string) => `${name} を ${seats} シートで追加しました${renewalDate ? `。更新日は ${renewalDate} です` : "。"}`,
        productRemoved: (name: string) => `${name} をアカウントの項目構成から削除しました。`,
      },
    },
    forms: {
      customer: {
        eyebrow: "顧客を編集",
        description: "顧客プロフィールからアカウント名と所在地を更新します。",
        company: "会社名",
        companyPlaceholder: "株式会社サンプル",
        location: "所在地",
        locationPlaceholder: "トロント、北米",
        save: "顧客を保存",
      },
      contact: {
        editEyebrow: "連絡先を編集",
        createEyebrow: "連絡先を作成",
        defaultTitle: "顧客連絡先",
        newTitle: "新しい連絡先",
        description: "このアカウントを担当する人を作成または更新し、連絡先情報を最新に保ちます。",
        role: "役割",
        rolePlaceholder: "主担当",
        name: "氏名",
        namePlaceholder: "連絡先名",
        email: "メールアドレス",
        emailPlaceholder: "hanako@company.com",
        phone: "電話番号",
        phonePlaceholder: "任意の電話番号",
        save: "連絡先を保存",
        create: "連絡先を作成",
      },
      product: {
        editEyebrow: "項目を編集",
        createEyebrow: "項目割り当てを作成",
        editTitle: "顧客項目を更新",
        createTitle: "項目を追加",
        description: "このアカウントに項目を割り当て、シート数を設定し、更新時期を 1 つのフォームで管理します。",
        item: "項目",
        seats: "シート数",
        renewalDate: "更新日",
        save: "項目を保存",
        create: "項目を作成",
      },
      note: {
        editEyebrow: "メモを編集",
        createEyebrow: "メモを作成",
        editTitle: "アカウントメモを更新",
        createTitle: "メモを追加",
        description: "顧客プロフィールを離れずに、内部向けコンテキスト、リマインダー、引き継ぎ情報を記録します。",
        note: "メモ",
        save: "メモを保存",
        create: "メモを作成",
      },
    },
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;

export const customersViewCopy = {
  en: {
    healthLabels: {
      All: "All",
      Healthy: "Healthy",
      Watch: "Watch",
      Escalated: "Escalated",
      Disabled: "Disabled",
    },
    page: {
      eyebrow: "Accounts · Relationship health",
      title: "Customer Desk",
      description: "Review account health, active support load, and product adoption from one customer workspace.",
      newCustomer: "New Customer",
    },
    stats: {
      managedAccounts: "Managed accounts",
      managedAccountsDetail: "Active customer accounts",
      openTickets: "Open tickets",
      openTicketsDetail: "Across all customer accounts",
      atRisk: "At risk",
      atRiskDetail: "Watch or escalated accounts",
      renewingSoon: "Renewing soon",
      renewingSoonDetail: "Products due within 90 days",
    },
    directory: {
      title: "Customer directory",
      description: "A list view with account details, product footprint, and ticket context inline.",
      loading: "Loading customers…",
      emptyTitle: "No customer matches the selected health filter.",
      emptyDetail: "Try another health state or create a new customer.",
      seats: (count: number) => `${count} seats`,
      primaryContact: "Primary contact",
      secondaryContact: "Secondary contact",
      noPrimaryContact: "No primary contact",
      open: "Open",
      resolved: "Resolved",
      priority: "Priority",
    },
    form: {
      closeDialog: "Close create customer dialog",
      eyebrow: "Create account",
      title: "New customer",
      description: "Add the account and open its profile to continue managing contacts, products, and notes.",
      company: "Company",
      companyPlaceholder: "Acme, Inc.",
      primaryContact: "Primary contact",
      primaryContactPlaceholder: "Taylor Morgan",
      email: "Email",
      emailPlaceholder: "taylor@acme.com",
      phone: "Phone",
      phonePlaceholder: "+1 (555) 555-0112",
      region: "Region",
      regionPlaceholder: "Toronto, North America",
      health: "Health",
      cancel: "Cancel",
      createCustomer: "Create customer",
    },
    common: {
      dash: "—",
    },
  },
  ja: {
    healthLabels: {
      All: "すべて",
      Healthy: "健全",
      Watch: "要注意",
      Escalated: "エスカレーション",
      Disabled: "無効",
    },
    page: {
      eyebrow: "アカウント · 関係性の健全性",
      title: "顧客デスク",
      description: "1 つの顧客ワークスペースから、アカウント健全性、進行中のサポート負荷、製品利用状況を確認します。",
      newCustomer: "新規顧客",
    },
    stats: {
      managedAccounts: "管理アカウント",
      managedAccountsDetail: "有効な顧客アカウント",
      openTickets: "未解決チケット",
      openTicketsDetail: "全顧客アカウント合計",
      atRisk: "リスクあり",
      atRiskDetail: "要注意またはエスカレーション中のアカウント",
      renewingSoon: "まもなく更新",
      renewingSoonDetail: "90 日以内に更新予定の製品",
    },
    directory: {
      title: "顧客一覧",
      description: "アカウント詳細、製品構成、チケット状況を一覧で確認できます。",
      loading: "顧客を読み込み中…",
      emptyTitle: "選択した健全性フィルターに一致する顧客がありません。",
      emptyDetail: "別の健全性状態を試すか、新しい顧客を作成してください。",
      seats: (count: number) => `${count} シート`,
      primaryContact: "主担当連絡先",
      secondaryContact: "副担当連絡先",
      noPrimaryContact: "主担当連絡先なし",
      open: "未対応",
      resolved: "解決済み",
      priority: "優先",
    },
    form: {
      closeDialog: "顧客作成ダイアログを閉じる",
      eyebrow: "アカウントを作成",
      title: "新しい顧客",
      description: "アカウントを追加し、プロフィールを開いて連絡先、製品、メモの管理を続けます。",
      company: "会社名",
      companyPlaceholder: "株式会社サンプル",
      primaryContact: "主担当連絡先",
      primaryContactPlaceholder: "山田 花子",
      email: "メールアドレス",
      emailPlaceholder: "hanako@company.com",
      phone: "電話番号",
      phonePlaceholder: "+1 (555) 555-0112",
      region: "地域",
      regionPlaceholder: "トロント、北米",
      health: "健全性",
      cancel: "キャンセル",
      createCustomer: "顧客を作成",
    },
    common: {
      dash: "—",
    },
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;

export const itemsViewCopy = {
  en: {
    categoryLabels: {
      Product: "Product",
      Service: "Service",
      Subscription: "Subscription",
    },
    statusLabels: {
      Draft: "Draft",
      Public: "Public",
      Disabled: "Disabled",
    },
    intervalLabels: {
      Daily: "Daily",
      Weekly: "Weekly",
      Monthly: "Monthly",
      Yearly: "Yearly",
      "One-time": "One-time",
    },
    slaUnitLabels: {
      Hours: "Hours",
      Minutes: "Minutes",
    },
    accentLabels: {
      green: "Green",
      blue: "Blue",
      orange: "Orange",
      purple: "Purple",
    },
    page: {
      eyebrow: "Items · Catalog management",
      title: "Items",
      description: "Manage the products, services, subscriptions, pricing, linked bundles, and ticket SLAs your team uses across customer accounts.",
      catalogState: "Catalog state",
      syncedState: "Item templates synced 6 mins ago",
      createdState: "New item template created just now",
      updatedState: (name: string) => `${name} template updated just now`,
    },
    metrics: {
      trackedItems: "Tracked items",
      trackedItemsDetail: "Products, services, and subscriptions available to assign.",
      publicItems: "Public items",
      publicItemsDetail: "Visible templates currently ready for active customer use.",
      savedPrices: "Saved prices",
      savedPricesDetail: "Items with a standard internal price and billing cadence.",
      slaCoverage: "SLA coverage",
      slaCoverageDetail: (count: number) => `${count} bundled templates currently reference another item.`,
    },
    library: {
      eyebrow: "Items",
      title: "Tracked items and pricing",
      description: "Maintain the internal library your team uses to organize which products, services, and subscriptions each customer account has enabled for their own end customers.",
      loading: "Loading items…",
      emptyTitle: "No items are available yet.",
      emptyDetail: "Create a new item to start building your catalog.",
      newItem: "New item",
      readOnly: "Read only",
    },
    table: {
      headers: {
        name: "Item",
        status: "Status",
        linked: "Linked",
        category: "Type",
        price: "Cost",
        sla: "SLA",
        action: "Action",
      },
      sortAriaLabel: (label: string, state?: "asc" | "desc") => `Sort items by ${label}${state ? `, currently ${state === "asc" ? "ascending" : "descending"}` : ""}`,
      linkedItems: "Linked items",
      linkedCount: (count: number) => `${count} linked`,
      none: "None",
      edit: "Edit",
      delete: "Delete",
      noPrice: "No price set",
    },
    modal: {
      closeDialog: "Close catalog modal",
      createEyebrow: "Create item template",
      editEyebrow: "Edit item template",
      createTitle: "New item",
      editFallbackTitle: "Item template",
      description: "Manage the internal item template, its pricing, and linked products or services in a focused modal.",
      itemName: "Item name",
      itemNamePlaceholder: "Checkout API",
      itemType: "Item type",
      status: "Status",
      pricing: "Pricing",
      pricingDescription: (currencyLabel: string) => `Set the standard price and billing cadence your team uses when recording this item. All prices use ${currencyLabel}.`,
      ticketSla: "Ticket SLA",
      ticketSlaDescription: "Optionally stamp new tickets created for this item with an SLA target.",
      enabled: "Enabled",
      disabledHint: "Leave disabled to create tickets without an SLA badge for this item.",
      linkedItems: "Linked products and services",
      linkedItemsDescription: "Reference other items to model bundles, packaged services, or add-ons sold together.",
      createAnotherFirst: "Create another item first to reference it here.",
      clearForm: "Clear form",
      resetChanges: "Reset changes",
      cancel: "Cancel",
      createItem: "Create item",
      saveItem: "Save item",
      accentColor: "Accent color",
    },
    errors: {
      missingName: "Add a name before saving.",
      missingPrice: "Add at least one price before saving this item.",
      invalidPositiveSla: "Add a positive SLA value before enabling it for this item.",
      invalidSla: "Add a valid SLA value before saving this item.",
    },
    pricing: {
      emptyAmount: "—",
    },
    sla: {
      disabled: "Disabled",
      hour: "hour",
      hours: "hours",
      minute: "minute",
      minutes: "minutes",
    },
  },
  ja: {
    categoryLabels: {
      Product: "製品",
      Service: "サービス",
      Subscription: "サブスクリプション",
    },
    statusLabels: {
      Draft: "下書き",
      Public: "公開中",
      Disabled: "無効",
    },
    intervalLabels: {
      Daily: "日次",
      Weekly: "週次",
      Monthly: "月次",
      Yearly: "年次",
      "One-time": "一回限り",
    },
    slaUnitLabels: {
      Hours: "時間",
      Minutes: "分",
    },
    accentLabels: {
      green: "グリーン",
      blue: "ブルー",
      orange: "オレンジ",
      purple: "パープル",
    },
    page: {
      eyebrow: "項目 · カタログ管理",
      title: "項目",
      description: "顧客アカウント全体でチームが利用する製品、サービス、サブスクリプション、価格、関連バンドル、チケット SLA を管理します。",
      catalogState: "カタログ状態",
      syncedState: "項目テンプレートは 6 分前に同期されました",
      createdState: "新しい項目テンプレートを作成しました",
      updatedState: (name: string) => `${name} テンプレートを更新しました`,
    },
    metrics: {
      trackedItems: "登録済み項目",
      trackedItemsDetail: "割り当て可能な製品、サービス、サブスクリプション。",
      publicItems: "公開中項目",
      publicItemsDetail: "現在顧客利用向けに公開されているテンプレート。",
      savedPrices: "価格設定済み",
      savedPricesDetail: "標準価格と請求サイクルが設定された項目。",
      slaCoverage: "SLA 対応",
      slaCoverageDetail: (count: number) => `${count} 件のバンドルテンプレートが他の項目を参照しています。`,
    },
    library: {
      eyebrow: "項目",
      title: "登録済み項目と価格",
      description: "各顧客アカウントで有効な製品、サービス、サブスクリプションを整理するための内部ライブラリを管理します。",
      loading: "項目を読み込み中…",
      emptyTitle: "利用可能な項目はまだありません。",
      emptyDetail: "新しい項目を作成してカタログを開始してください。",
      newItem: "新規項目",
      readOnly: "閲覧のみ",
    },
    table: {
      headers: {
        name: "項目",
        status: "状態",
        linked: "関連",
        category: "種別",
        price: "価格",
        sla: "SLA",
        action: "操作",
      },
      sortAriaLabel: (label: string, state?: "asc" | "desc") => `項目を ${label} で並び替え${state ? `、現在は${state === "asc" ? "昇順" : "降順"}` : ""}`,
      linkedItems: "関連項目",
      linkedCount: (count: number) => `${count} 件関連`,
      none: "なし",
      edit: "編集",
      delete: "削除",
      noPrice: "価格未設定",
    },
    modal: {
      closeDialog: "カタログダイアログを閉じる",
      createEyebrow: "項目テンプレートを作成",
      editEyebrow: "項目テンプレートを編集",
      createTitle: "新規項目",
      editFallbackTitle: "項目テンプレート",
      description: "内部項目テンプレート、その価格、関連する製品やサービスをこのモーダルで管理します。",
      itemName: "項目名",
      itemNamePlaceholder: "チェックアウト API",
      itemType: "項目タイプ",
      status: "状態",
      pricing: "価格設定",
      pricingDescription: (currencyLabel: string) => `この項目記録時に使う標準価格と請求サイクルを設定します。すべての価格は ${currencyLabel} を使用します。`,
      ticketSla: "チケット SLA",
      ticketSlaDescription: "この項目向けに作成される新規チケットへ SLA 目標を自動設定できます。",
      enabled: "有効",
      disabledHint: "無効のままにすると、この項目のチケットには SLA バッジが付きません。",
      linkedItems: "関連製品・サービス",
      linkedItemsDescription: "バンドル、パッケージサービス、または一緒に販売される追加オプションを表現するために他の項目を参照します。",
      createAnotherFirst: "ここで参照するには、先に別の項目を作成してください。",
      clearForm: "フォームをクリア",
      resetChanges: "変更をリセット",
      cancel: "キャンセル",
      createItem: "項目を作成",
      saveItem: "項目を保存",
      accentColor: "アクセントカラー",
    },
    errors: {
      missingName: "保存する前に名前を追加してください。",
      missingPrice: "この項目を保存する前に少なくとも 1 つ価格を追加してください。",
      invalidPositiveSla: "有効化する前に正の SLA 値を追加してください。",
      invalidSla: "保存する前に有効な SLA 値を追加してください。",
    },
    pricing: {
      emptyAmount: "—",
    },
    sla: {
      disabled: "無効",
      hour: "時間",
      hours: "時間",
      minute: "分",
      minutes: "分",
    },
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;

export const knowledgeBaseViewCopy = {
  en: {
    page: {
      eyebrow: "Knowledge base · Support enablement",
      title: "Knowledge Base",
      description: "Centralize shared support guides while keeping teammate-only drafts and personal runbooks in a separate private section.",
      newArticle: "New article",
    },
    featured: {
      eyebrow: "Featured articles",
      title: "Most-used shared references",
      emptyTitle: "No featured articles yet.",
      emptyDetail: "Publish or pin shared articles to surface key references here.",
    },
    collections: {
      eyebrow: "Collections",
      title: "Organize articles by topic",
      newCollection: "New collection",
      articleCount: (count: number) => `${count} articles`,
      openCollection: "Open collection",
      emptyTitle: "No collections yet.",
      emptyDetail: "Create a collection to group related guidance by topic.",
    },
    latest: {
      eyebrow: "Latest articles",
      title: "Recently published shared knowledge",
      visibleCount: (count: number) => `${count} visible`,
      noSummary: "No summary added yet.",
      emptyTitle: "No shared articles yet.",
      emptyDetail: "Create or publish an article to start building the shared knowledge base.",
    },
    popular: {
      eyebrow: "Popular this week",
      emptyTitle: "No article views yet this week.",
      emptyDetail: "Open articles to start ranking the most-viewed content from the last 7 days.",
    },
    quality: {
      eyebrow: "Maintain quality",
      title: "Keep support guidance current",
      description: "Review top-viewed articles weekly, archive outdated procedures, and pin incident runbooks before major releases.",
    },
    private: {
      eyebrow: "Private section",
      title: "Private articles",
      visibleCount: (count: number) => `${count} visible`,
      description: "Private articles stay out of shared collections and are only visible to the owner and the people they add.",
      emptyTitle: "No private articles yet.",
      emptyDetail: "Create a private article for personal notes, teammate handoffs, or small-group playbooks.",
      badge: "Private",
      owner: "Owner",
      you: "You",
      unknown: "Unknown",
      sharedWith: "Shared with",
      sharedWithSuffix: (names: string, more: number) => ` · Shared with ${names}${more > 0 ? ` +${more}` : ""}`,
    },
    modal: {
      closeDialog: "Close collection dialog",
      eyebrow: "New collection",
      title: "Create knowledge base collection",
      description: "Add a new container for related articles so support can browse guidance by topic.",
      name: "Collection name",
      namePlaceholder: "API troubleshooting",
      descriptionLabel: "Description",
      descriptionPlaceholder: "Runbooks, triage guides, and repeatable resolution steps for API support issues.",
      cancel: "Cancel",
      creating: "Creating…",
      create: "Create collection",
    },
    errors: {
      missingFields: "Add a name and description before creating the collection.",
      createFailed: "Failed to create collection.",
    },
    loading: {
      featured: "Loading featured articles…",
      collections: "Loading collections…",
      latest: "Loading latest articles…",
      popular: "Loading popular articles…",
      private: "Loading private articles…",
    },
  },
  ja: {
    page: {
      eyebrow: "ナレッジ · サポート強化",
      title: "ナレッジ",
      description: "共有サポートガイドを一元化しつつ、チームメイト限定の下書きや個人用ランブックを専用のプライベート領域に分けて管理します。",
      newArticle: "記事を作成",
    },
    featured: {
      eyebrow: "注目記事",
      title: "よく使われる共有リファレンス",
      emptyTitle: "注目記事はまだありません。",
      emptyDetail: "共有記事を公開またはピン留めすると、ここに主要な参照先が表示されます。",
    },
    collections: {
      eyebrow: "コレクション",
      title: "記事をトピック別に整理",
      newCollection: "コレクションを作成",
      articleCount: (count: number) => `${count} 件の記事`,
      openCollection: "コレクションを開く",
      emptyTitle: "コレクションはまだありません。",
      emptyDetail: "トピックごとに関連ナレッジをまとめるコレクションを作成してください。",
    },
    latest: {
      eyebrow: "最新記事",
      title: "最近公開された共有ナレッジ",
      visibleCount: (count: number) => `${count} 件表示`,
      noSummary: "要約はまだありません。",
      emptyTitle: "共有記事はまだありません。",
      emptyDetail: "記事を作成または公開して共有ナレッジベースを開始してください。",
    },
    popular: {
      eyebrow: "今週の人気",
      emptyTitle: "今週の閲覧記事はまだありません。",
      emptyDetail: "記事を開くと、直近 7 日間でもっとも閲覧されたコンテンツの順位付けが始まります。",
    },
    quality: {
      eyebrow: "品質を保つ",
      title: "サポートガイダンスを最新に維持",
      description: "閲覧数の多い記事を毎週見直し、古い手順をアーカイブし、大きなリリース前にインシデント用ランブックを固定表示します。",
    },
    private: {
      eyebrow: "プライベート領域",
      title: "プライベート記事",
      visibleCount: (count: number) => `${count} 件表示`,
      description: "プライベート記事は共有コレクションには表示されず、所有者と追加されたメンバーだけが閲覧できます。",
      emptyTitle: "プライベート記事はまだありません。",
      emptyDetail: "個人メモ、引き継ぎ、少人数向けプレイブック用にプライベート記事を作成してください。",
      badge: "非公開",
      owner: "所有者",
      you: "あなた",
      unknown: "不明",
      sharedWith: "共有先",
      sharedWithSuffix: (names: string, more: number) => ` · ${names} に共有${more > 0 ? ` 他 ${more} 名` : ""}`,
    },
    modal: {
      closeDialog: "コレクションダイアログを閉じる",
      eyebrow: "新しいコレクション",
      title: "ナレッジコレクションを作成",
      description: "関連する記事をまとめる新しい入れ物を追加し、トピック別にサポート情報を閲覧できるようにします。",
      name: "コレクション名",
      namePlaceholder: "API トラブルシューティング",
      descriptionLabel: "説明",
      descriptionPlaceholder: "API サポート課題向けのランブック、トリアージガイド、再利用可能な解決手順。",
      cancel: "キャンセル",
      creating: "作成中…",
      create: "コレクションを作成",
    },
    errors: {
      missingFields: "コレクション作成前に名前と説明を入力してください。",
      createFailed: "コレクションの作成に失敗しました。",
    },
    loading: {
      featured: "注目記事を読み込み中…",
      collections: "コレクションを読み込み中…",
      latest: "最新記事を読み込み中…",
      popular: "人気記事を読み込み中…",
      private: "プライベート記事を読み込み中…",
    },
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;

export const knowledgeCollectionViewCopy = {
  en: {
    page: {
      back: "All collections",
      newArticle: "New article",
      deleteCollection: "Delete collection",
      eyebrow: "Knowledge base collection",
    },
    featured: {
      eyebrow: "Featured in collection",
      title: "Pinned references for faster support handling",
      count: (count: number) => `${count} featured`,
      noSummary: "No summary added yet.",
    },
    articles: {
      eyebrow: "Collection articles",
      title: (name: string) => `Everything in ${name}`,
      total: (count: number) => `${count} total`,
      emptyTitle: "No articles in this collection yet.",
      emptyDetail: "Create the first article to start organizing guidance here.",
      noSummary: "No summary added yet.",
    },
    summary: {
      eyebrow: "Collection summary",
      featured: "Featured",
      popular: "Popular",
      drafts: "Drafts",
    },
    guidance: {
      eyebrow: "Keep structure clear",
      title: "Use collections to group repeatable guidance",
      description: "Keep troubleshooting steps, release notes, and internal playbooks separated so support can scan for the right answer faster.",
    },
    delete: {
      closeDialog: "Close delete collection dialog",
      eyebrow: "Danger zone",
      title: (name: string) => `Delete ${name}?`,
      description: (count: number) => `This permanently removes the collection and deletes ${count} ${count === 1 ? "article" : "articles"} inside it.`,
      confirmCheckbox: "I understand that deleting this collection will also delete every article inside it.",
      typeDelete: "Type delete to confirm",
      placeholder: "delete",
      cancel: "Cancel",
      deleting: "Deleting…",
      deleteCollection: "Delete collection",
    },
    errors: {
      confirmArticles: "Confirm that all articles in this collection will be deleted.",
      typeDelete: 'Type "delete" to confirm collection removal.',
      deleteFailed: "Failed to delete collection.",
    },
  },
  ja: {
    page: {
      back: "コレクション一覧",
      newArticle: "記事を作成",
      deleteCollection: "コレクションを削除",
      eyebrow: "ナレッジコレクション",
    },
    featured: {
      eyebrow: "このコレクションの注目記事",
      title: "すばやいサポート対応のための固定リファレンス",
      count: (count: number) => `${count} 件の注目`,
      noSummary: "要約はまだありません。",
    },
    articles: {
      eyebrow: "コレクション内の記事",
      title: (name: string) => `${name} の全記事`,
      total: (count: number) => `合計 ${count} 件`,
      emptyTitle: "このコレクションにはまだ記事がありません。",
      emptyDetail: "最初の記事を作成して、ここでガイダンスを整理しましょう。",
      noSummary: "要約はまだありません。",
    },
    summary: {
      eyebrow: "コレクション概要",
      featured: "注目",
      popular: "人気",
      drafts: "下書き",
    },
    guidance: {
      eyebrow: "構成を明確に保つ",
      title: "再利用できるガイダンスはコレクションで整理",
      description: "トラブルシューティング手順、リリースノート、内部プレイブックを分けることで、サポートが正しい答えをすばやく見つけられます。",
    },
    delete: {
      closeDialog: "コレクション削除ダイアログを閉じる",
      eyebrow: "危険操作",
      title: (name: string) => `${name} を削除しますか？`,
      description: (count: number) => `この操作でコレクションは完全に削除され、中の ${count} 件の記事も削除されます。`,
      confirmCheckbox: "このコレクションを削除すると、中のすべての記事も削除されることを理解しました。",
      typeDelete: "確認のため delete と入力",
      placeholder: "delete",
      cancel: "キャンセル",
      deleting: "削除中…",
      deleteCollection: "コレクションを削除",
    },
    errors: {
      confirmArticles: "このコレクション内のすべての記事が削除されることを確認してください。",
      typeDelete: '削除を確認するには "delete" と入力してください。',
      deleteFailed: "コレクションの削除に失敗しました。",
    },
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;

export const knowledgeArticleViewCopy = {
  en: {
    page: {
      back: "Back",
      privateEyebrow: "Private knowledge article",
      sharedEyebrow: "Knowledge base article",
      noSummary: "No summary added yet.",
      private: "Private",
      ownArticle: "You own this article",
      sharedWithYou: "Shared with you",
      deleteArticle: "Delete article",
      deleting: "Deleting…",
      editArticle: "Edit article",
      created: "Created",
      noContent: "No structured content yet.",
      confirmDelete: (title: string) => `Delete "${title}"? This action cannot be undone.`,
    },
    pin: {
      pin: "Pin article",
      unpin: "Unpin article",
    },
    meta: {
      eyebrow: "Article metadata",
      collection: "Collection",
      unassigned: "Unassigned",
      featured: "Featured",
      hiddenForPrivate: "Hidden for private articles",
      pinned: "Pinned",
      notPinned: "Not pinned",
      category: "Category",
      status: "Status",
      readTime: "Read time",
      created: "Created",
      updated: "Updated",
      author: "Author",
      owner: "Owner",
      sharedWith: "Shared with",
      unknown: "Unknown",
      ownerOnly: "Only the owner can open this article.",
    },
    keywords: {
      eyebrow: "Keywords",
      empty: "No keywords added.",
    },
  },
  ja: {
    page: {
      back: "戻る",
      privateEyebrow: "プライベート記事",
      sharedEyebrow: "ナレッジ記事",
      noSummary: "要約はまだありません。",
      private: "非公開",
      ownArticle: "あなたが所有する記事",
      sharedWithYou: "あなたに共有されています",
      deleteArticle: "記事を削除",
      deleting: "削除中…",
      editArticle: "記事を編集",
      created: "作成日",
      noContent: "構造化コンテンツはまだありません。",
      confirmDelete: (title: string) => `「${title}」を削除しますか？この操作は元に戻せません。`,
    },
    pin: {
      pin: "記事を固定",
      unpin: "記事の固定を解除",
    },
    meta: {
      eyebrow: "記事メタデータ",
      collection: "コレクション",
      unassigned: "未設定",
      featured: "注目表示",
      hiddenForPrivate: "非公開記事では非表示",
      pinned: "固定済み",
      notPinned: "未固定",
      category: "カテゴリ",
      status: "状態",
      readTime: "読了時間",
      created: "作成日",
      updated: "更新日",
      author: "作成者",
      owner: "所有者",
      sharedWith: "共有先",
      unknown: "不明",
      ownerOnly: "この記事を開けるのは所有者のみです。",
    },
    keywords: {
      eyebrow: "キーワード",
      empty: "キーワードはまだありません。",
    },
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;

export const newArticleViewCopy = {
  en: {
    page: {
      back: "Back to knowledge base",
      eyebrow: (editing: boolean) => `Knowledge base · ${editing ? "Edit article" : "New article"}`,
      title: (editing: boolean) => editing ? "Edit article" : "Create article",
      description: (editing: boolean) => editing
        ? "Update published guidance, metadata, and structured content in a focused editing workspace."
        : "Draft a support article with structured content blocks, internal notes, and publishing metadata in one full-screen workspace.",
      saving: "Saving…",
      saveDraft: "Save draft",
      saveChanges: "Save changes",
      publishArticle: "Publish article",
      publishing: "Publishing…",
      autosaved: "Autosaved locally",
      loadedDraft: "Loaded article draft",
      pendingAutosave: "Changes pending autosave",
      editorFailed: "Editor failed to load",
      savedDraftAt: (time: string) => `Draft saved · ${time}`,
      autosavedAt: (time: string) => `Autosaved locally · ${time}`,
      savedToKnowledge: "Saved to knowledge base",
      publishedToKnowledge: "Published to knowledge base",
      publishUpdatedSummary: (title: string, blocks: number, section: string) => `${title} was updated with ${blocks} content blocks in the ${section} section.`,
      publishNewSummary: (title: string, blocks: number, section: string) => `${title} was published with ${blocks} content blocks in the ${section} section.`,
    },
    draft: {
      articleTitle: "Article title",
      articleTitlePlaceholder: "Enter article title",
      summary: "Summary",
      summaryPlaceholder: "Short overview for search results and internal previews",
      accessScope: "Access scope",
      workspaceLabel: "Shared workspace",
      workspaceDetail: "Visible to everyone who can access the shared knowledge base. Appears in collections and featured sections.",
      privateLabel: "Private section",
      privateDetail: "Visible only to you and the teammates you choose. Hidden from shared collections and rankings.",
      privateNeedsUser: "Private articles need a signed-in workspace user so ownership and sharing can be enforced.",
      collection: "Collection",
      privatePlacement: "Private placement",
      privatePlacementTitle: "Private articles live in the private section",
      privatePlacementDetail: "They are intentionally kept out of shared collections, featured cards, and popularity ranking.",
      category: "Category",
      bodyEyebrow: "Article body",
      bodyTitle: "Full text editor",
      blockCount: (count: number) => `${count} blocks`,
      loadingEditor: "Loading editor…",
      editorPlaceholder: "Start writing the article body, runbook, or internal playbook…",
    },
    meta: {
      eyebrow: "Publishing metadata",
      status: "Status",
      estimatedReadTime: "Estimated read time",
      calculatedFromWords: (count: number) => `Calculated from ${count} words in the title, summary, and article body.`,
      featuredPlacement: "Featured placement",
      pinned: "Pinned to featured articles",
      notPinned: "Not pinned",
      privatePinnedDetail: "Private articles stay out of featured placement.",
      pinnedDetail: "This article will appear in the featured articles section.",
      notPinnedDetail: "Turn this on to show the article in featured articles.",
      keywords: "Keywords",
      privateSharing: "Private sharing",
      owner: "Owner",
      unknown: "Unknown",
      chooseTeammates: "Choose teammates who can open this private article. They can read it, but only the owner can edit or delete it.",
      peopleWithAccess: "People with access",
      noUsersToShare: "No other Users are available to share with yet.",
      switchToPrivate: "Switch this article to the private section if you want to limit access to yourself and selected teammates.",
    },
    readiness: {
      eyebrow: "Readiness checklist",
      titleSlug: "Title and slug are defined",
      summaryReady: "Summary is ready for previews",
      bodyStructured: "Body includes meaningful structured content",
      privateConfigured: "Private placement is configured",
      featuredConfigured: "Pinned articles appear in Featured articles",
      keywords: "Keywords support findability",
    },
    tips: {
      eyebrow: "Editor tips",
      headings: "Use headings to separate diagnosis, recovery, and escalation guidance.",
      checklists: "Add checklists for repeatable support procedures.",
      tables: "Use tables when agents need exact field mappings or response codes.",
    },
    preview: {
      eyebrow: "Publish preview",
    },
    statusOptions: {
      Draft: "Draft",
      Review: "Review",
      Published: "Published",
    },
    sectionLabels: {
      private: "private",
    },
  },
  ja: {
    page: {
      back: "ナレッジへ戻る",
      eyebrow: (editing: boolean) => `ナレッジ · ${editing ? "記事を編集" : "新規記事"}`,
      title: (editing: boolean) => editing ? "記事を編集" : "記事を作成",
      description: (editing: boolean) => editing
        ? "公開済みガイダンス、メタデータ、構造化コンテンツを専用の編集ワークスペースで更新します。"
        : "構造化コンテンツ、内部メモ、公開用メタデータを 1 つのフルスクリーン編集画面で作成します。",
      saving: "保存中…",
      saveDraft: "下書きを保存",
      saveChanges: "変更を保存",
      publishArticle: "記事を公開",
      publishing: "公開中…",
      autosaved: "ローカルに自動保存済み",
      loadedDraft: "記事の下書きを読み込みました",
      pendingAutosave: "変更は自動保存待ちです",
      editorFailed: "エディターの読み込みに失敗しました",
      savedDraftAt: (time: string) => `下書きを保存 · ${time}`,
      autosavedAt: (time: string) => `ローカルに自動保存 · ${time}`,
      savedToKnowledge: "ナレッジに保存しました",
      publishedToKnowledge: "ナレッジに公開しました",
      publishUpdatedSummary: (title: string, blocks: number, section: string) => `${title} を更新し、${section} セクションに ${blocks} 個のコンテンツブロックを保存しました。`,
      publishNewSummary: (title: string, blocks: number, section: string) => `${title} を公開し、${section} セクションに ${blocks} 個のコンテンツブロックを追加しました。`,
    },
    draft: {
      articleTitle: "記事タイトル",
      articleTitlePlaceholder: "記事タイトルを入力",
      summary: "要約",
      summaryPlaceholder: "検索結果や内部プレビュー向けの短い概要",
      accessScope: "公開範囲",
      workspaceLabel: "共有ワークスペース",
      workspaceDetail: "共有ナレッジへアクセスできる全員に表示されます。コレクションや注目記事にも表示されます。",
      privateLabel: "プライベート領域",
      privateDetail: "あなたと選択したチームメイトのみに表示されます。共有コレクションやランキングには表示されません。",
      privateNeedsUser: "プライベート記事には、所有権と共有設定を適用するためサインイン済みユーザーが必要です。",
      collection: "コレクション",
      privatePlacement: "プライベート配置",
      privatePlacementTitle: "プライベート記事はプライベート領域に保存されます",
      privatePlacementDetail: "共有コレクション、注目カード、人気ランキングには意図的に表示されません。",
      category: "カテゴリ",
      bodyEyebrow: "記事本文",
      bodyTitle: "全文エディター",
      blockCount: (count: number) => `${count} ブロック`,
      loadingEditor: "エディターを読み込み中…",
      editorPlaceholder: "記事本文、ランブック、または内部プレイブックを書き始めてください…",
    },
    meta: {
      eyebrow: "公開メタデータ",
      status: "状態",
      estimatedReadTime: "推定読了時間",
      calculatedFromWords: (count: number) => `タイトル、要約、本文の ${count} 語から計算されています。`,
      featuredPlacement: "注目表示",
      pinned: "注目記事に固定",
      notPinned: "固定しない",
      privatePinnedDetail: "プライベート記事は注目表示されません。",
      pinnedDetail: "この記事は注目記事セクションに表示されます。",
      notPinnedDetail: "オンにすると注目記事に表示されます。",
      keywords: "キーワード",
      privateSharing: "プライベート共有",
      owner: "所有者",
      unknown: "不明",
      chooseTeammates: "このプライベート記事を開けるチームメイトを選択します。閲覧はできますが、編集と削除は所有者のみ可能です。",
      peopleWithAccess: "アクセス可能なメンバー",
      noUsersToShare: "共有できる他のユーザーはまだいません。",
      switchToPrivate: "自分と選択したチームメイトのみに制限したい場合は、この記事をプライベート領域に切り替えてください。",
    },
    readiness: {
      eyebrow: "公開準備チェック",
      titleSlug: "タイトルとスラッグが定義されている",
      summaryReady: "要約がプレビュー用に準備できている",
      bodyStructured: "本文に十分な構造化コンテンツがある",
      privateConfigured: "プライベート配置が設定されている",
      featuredConfigured: "固定記事が注目記事に表示される",
      keywords: "キーワードで見つけやすくなっている",
    },
    tips: {
      eyebrow: "編集のヒント",
      headings: "見出しで診断、復旧、エスカレーション手順を分けてください。",
      checklists: "繰り返し使うサポート手順にはチェックリストを追加してください。",
      tables: "正確なフィールド対応やレスポンスコードが必要な場合は表を使ってください。",
    },
    preview: {
      eyebrow: "公開プレビュー",
    },
    statusOptions: {
      Draft: "下書き",
      Review: "レビュー中",
      Published: "公開済み",
    },
    sectionLabels: {
      private: "非公開",
    },
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;

export const reportingViewCopy = {
  en: {
    page: {
      eyebrow: "Reporting workspace",
      title: "Reporting",
      description: "Focused ticket, customer, and product reporting with date filters, grouping, SLA checks, growth tracking, and spend analysis.",
    },
    filters: {
      eyebrow: "Report filters",
      title: "Primary report",
      exportCsv: "Export CSV",
      reportType: "Report type",
      startDate: "Start date",
      endDate: "End date",
      ticketMetric: "Ticket metric",
      productMetric: "Product metric",
      customer: "Customer",
      allCustomers: "All customers",
      ticketOptions: "Ticket options",
      chartMode: "Chart mode",
      reportTypes: {
        tickets: "Tickets",
        products: "Products",
      },
      ticketMetrics: {
        openedClosed: "Opened vs closed",
        opened: "Opened",
        closed: "Closed",
        firstResponse: "First response time",
      },
      productMetrics: {
        customers: "Customers by product",
        monthlySpend: "Monthly revenue by product",
        seats: "Seats by product",
        customerSpend: "Top customer spenders",
      },
      ticketOptionsLabels: {
        assignee: "Assignee",
        category: "Category",
        slaBroken: "SLA broken",
        scheduled: "Scheduled",
      },
      chartModeOptions: {
        snapshot: "Current snapshot",
        timeline: "Over time",
      },
    },
    labels: {
      unassigned: "Unassigned",
      opened: "Opened",
      closed: "Closed",
      avgFirstResponse: "Avg first response",
      customers: "Customers",
      estimatedMonthlySpend: "Estimated monthly spend",
      monthlySpend: "Monthly revenue",
      seats: "Seats",
    },
    tickets: {
      title: "Ticket reporting",
      groupedSubtitle: "Grouped bars reflect the selected ticket grouping.",
      trendSubtitle: "Trend chart reflects the selected date range and ticket filters.",
      summary: {
        opened: {
          label: "Opened",
          detail: "Tickets created inside the selected window after all active filters.",
        },
        closed: {
          label: "Closed",
          detail: "Resolved tickets counted by their closing date inside the same window.",
        },
        firstResponse: {
          label: "First response",
          detail: "Uses the first outbound customer-visible ticket note as the first response.",
        },
        slaBroken: {
          label: "SLA broken",
          detail: "Tickets currently beyond the SLA deadline or resolved after it.",
        },
      },
    },
    customers: {
      growthChart: {
        title: "Customer growth over time",
        subtitle: "Cumulative customers based on first recorded account activity.",
      },
      spendChart: {
        title: "Customer spending over time",
        subtitle: "Estimated monthly spend derived from mapped product plans and seat counts.",
      },
      panel: {
        eyebrow: "Customer reporting",
        title: "Customer summary",
        badge: "Live",
      },
      summary: {
        customers: {
          label: "Customers",
          detail: "Customers with recorded activity before the selected end date.",
        },
        growth: {
          label: "Growth in range",
          detail: "Net new customers inferred from the earliest recorded customer activity.",
        },
        spend: {
          label: "Est. monthly spend",
          detail: "Estimated monthly spend from mapped product plans and seat counts.",
        },
      },
    },
    products: {
      unnamedProduct: "Unnamed product",
      panel: {
        eyebrow: "Product reporting",
        title: "Product summary",
        badge: "Revenue",
      },
      summary: {
        productsInUse: {
          label: "Products in use",
          detail: "Unique products first recorded inside the selected reporting range.",
        },
        customersWithProducts: {
          label: "Customers with products",
          detail: "Customers with at least one product first recorded in the selected range.",
        },
        mappedMonthlySpend: {
          label: "Mapped monthly revenue",
          detail: "Estimated monthly revenue from products first recorded in the selected range.",
        },
      },
      charts: {
        customers: {
          snapshotTitle: "Customers by product",
          timelineTitle: "Customers by product over time",
          snapshotSubtitle: "How many customers first picked up each product within the selected range.",
          timelineSubtitle: "Tracks product adoption over time using product-related activity when available.",
        },
        monthlySpend: {
          snapshotTitle: "Monthly revenue by product",
          timelineTitle: "Monthly revenue by product over time",
          snapshotSubtitle: "Estimated monthly revenue from products first recorded inside the selected range.",
          timelineSubtitle: "Tracks mapped monthly revenue over the selected range using product start dates when available.",
        },
        seats: {
          snapshotTitle: "Seats by product",
          timelineTitle: "Seats by product over time",
          snapshotSubtitle: "Seat totals from products first recorded inside the selected range.",
          timelineSubtitle: "Tracks cumulative seats over time for the leading products in the selected range.",
        },
        customerSpend: {
          snapshotTitle: "Top customer spenders",
          timelineTitle: "Top customer spenders over time",
          snapshotSubtitle: "Highest-spend customers based on products first recorded in the selected range.",
          timelineSubtitle: "Tracks spend accumulation over time for the current highest-spend customers.",
        },
      },
    },
    notes: {
      eyebrow: "Notes",
      items: [
        "Opened tickets are counted by creation date.",
        "Closed tickets are counted by last update date only when status is resolved.",
        "First response is calculated from the first outbound customer-visible ticket note.",
        "Product spend uses mapped product plan pricing and normalizes recurring intervals into monthly estimates.",
        "Product timelines use product-related customer activity when available and otherwise fall back to the customer start date.",
      ],
    },
  },
  ja: {
    page: {
      eyebrow: "レポートワークスペース",
      title: "レポート",
      description: "日付フィルター、グルーピング、SLA 確認、成長追跡、支出分析を備えた、チケット・顧客・製品向けの集中レポート画面です。",
    },
    filters: {
      eyebrow: "レポートフィルター",
      title: "メインレポート",
      exportCsv: "CSV をエクスポート",
      reportType: "レポート種別",
      startDate: "開始日",
      endDate: "終了日",
      ticketMetric: "チケット指標",
      productMetric: "製品指標",
      customer: "顧客",
      allCustomers: "すべての顧客",
      ticketOptions: "チケットオプション",
      chartMode: "表示モード",
      reportTypes: {
        tickets: "チケット",
        products: "製品",
      },
      ticketMetrics: {
        openedClosed: "作成と解決",
        opened: "作成数",
        closed: "解決数",
        firstResponse: "初回応答時間",
      },
      productMetrics: {
        customers: "製品別顧客数",
        monthlySpend: "製品別月額収益",
        seats: "製品別シート数",
        customerSpend: "顧客別支出上位",
      },
      ticketOptionsLabels: {
        assignee: "担当者",
        category: "カテゴリ",
        slaBroken: "SLA 超過",
        scheduled: "予定あり",
      },
      chartModeOptions: {
        snapshot: "現在のスナップショット",
        timeline: "時系列",
      },
    },
    labels: {
      unassigned: "未割り当て",
      opened: "作成数",
      closed: "解決数",
      avgFirstResponse: "平均初回応答",
      customers: "顧客数",
      estimatedMonthlySpend: "推定月額費用",
      monthlySpend: "月額収益",
      seats: "シート数",
    },
    tickets: {
      title: "チケットレポート",
      groupedSubtitle: "選択したチケットのグループ化に応じた棒グラフを表示します。",
      trendSubtitle: "選択した日付範囲とチケットフィルターに応じた推移を表示します。",
      summary: {
        opened: {
          label: "作成数",
          detail: "有効なフィルターを適用したうえで、選択期間内に作成されたチケット数です。",
        },
        closed: {
          label: "解決数",
          detail: "同じ期間内に、解決日時で集計された解決済みチケット数です。",
        },
        firstResponse: {
          label: "初回応答",
          detail: "最初の顧客向け送信チケットノートを初回応答として使用します。",
        },
        slaBroken: {
          label: "SLA 超過",
          detail: "現在 SLA 期限を超過している、または超過後に解決されたチケット数です。",
        },
      },
    },
    customers: {
      growthChart: {
        title: "顧客成長の推移",
        subtitle: "最初に記録されたアカウント活動にもとづく累積顧客数です。",
      },
      spendChart: {
        title: "顧客支出の推移",
        subtitle: "紐づいた製品プランとシート数から推定した月額費用です。",
      },
      panel: {
        eyebrow: "顧客レポート",
        title: "顧客サマリー",
        badge: "最新",
      },
      summary: {
        customers: {
          label: "顧客数",
          detail: "選択した終了日までに活動記録がある顧客数です。",
        },
        growth: {
          label: "期間内の成長",
          detail: "最も早い顧客活動記録から推定した純増顧客数です。",
        },
        spend: {
          label: "推定月額費用",
          detail: "紐づいた製品プランとシート数から推定した月額費用です。",
        },
      },
    },
    products: {
      unnamedProduct: "名称未設定の製品",
      panel: {
        eyebrow: "製品レポート",
        title: "製品サマリー",
        badge: "収益",
      },
      summary: {
        productsInUse: {
          label: "利用中の製品",
          detail: "選択したレポート期間内で最初に記録された一意の製品数です。",
        },
        customersWithProducts: {
          label: "製品利用中の顧客",
          detail: "選択した期間内で最初に製品が記録された顧客数です。",
        },
        mappedMonthlySpend: {
          label: "算出済み月額収益",
          detail: "選択した期間内で最初に記録された製品から推定した月額収益です。",
        },
      },
      charts: {
        customers: {
          snapshotTitle: "製品別顧客数",
          timelineTitle: "製品別顧客数の推移",
          snapshotSubtitle: "選択した期間内で各製品を初めて導入した顧客数です。",
          timelineSubtitle: "利用可能な場合は製品関連の活動を使って、製品採用の推移を追跡します。",
        },
        monthlySpend: {
          snapshotTitle: "製品別月額収益",
          timelineTitle: "製品別月額収益の推移",
          snapshotSubtitle: "選択した期間内で最初に記録された製品から推定した月額収益です。",
          timelineSubtitle: "利用可能な場合は製品開始日を使って、選択期間の月額収益推移を追跡します。",
        },
        seats: {
          snapshotTitle: "製品別シート数",
          timelineTitle: "製品別シート数の推移",
          snapshotSubtitle: "選択した期間内で最初に記録された製品のシート合計です。",
          timelineSubtitle: "選択した期間内の主要製品について、累積シート数の推移を追跡します。",
        },
        customerSpend: {
          snapshotTitle: "顧客別支出上位",
          timelineTitle: "顧客別支出上位の推移",
          snapshotSubtitle: "選択した期間内で最初に記録された製品にもとづく支出上位の顧客です。",
          timelineSubtitle: "現在の高支出顧客について、時間経過に伴う支出の積み上がりを追跡します。",
        },
      },
    },
    notes: {
      eyebrow: "メモ",
      items: [
        "作成数はチケットの作成日で集計されます。",
        "解決数は、ステータスが解決済みの場合のみ最終更新日で集計されます。",
        "初回応答は最初の顧客向け送信チケットノートから算出するため、専用の応答イベントテーブルは不要です。",
        "製品費用は、紐づいた製品プランの価格を使い、定期課金の間隔を月額に正規化して算出します。",
        "製品の時系列は、利用可能であれば製品関連の顧客活動を使い、なければ顧客開始日にフォールバックします。",
      ],
    },
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;

export const settingsViewCopy = {
  en: {
    common: {
      enabled: "Enabled",
      disabled: "Disabled",
      on: "On",
      off: "Off",
      done: "Done",
      edit: "Edit",
      delete: "Delete",
      reset: "Reset",
      cancel: "Cancel",
      remove: "Remove",
      addField: "Add field",
      clearForm: "Clear form",
      resetChanges: "Reset changes",
      noDescriptionYet: "No description yet.",
      removeLogoAria: "Remove logo",
      workspaceLogoAlt: "Workspace logo",
      noLogoUploadedYet: "No logo uploaded yet",
      clickToReplaceLogo: "Click to replace this logo.",
      clickToChooseLogo: "Click to choose a logo file.",
      sortBy: (label: string, active: boolean, direction: "asc" | "desc") => `Sort categories by ${label}${active ? `, currently ${direction === "asc" ? "ascending" : "descending"}` : ""}`,
      action: "Action",
    },
    page: {
      eyebrow: "Settings",
      title: "Control Center",
      sectionEyebrow: (section: string) => `Settings · ${section}`,
      backToSettings: "Back to settings",
      descriptionManage: "Manage branding, billing, routing, and workspace operations from one place.",
      descriptionReadOnly: "Manage workspace configuration and billing setup for your team.",
      environment: "Environment",
      environmentStatus: "Production synced 4 mins ago",
    },
    metrics: {
      brandSetupLabel: "Brand setup",
      brandSetupDetail: "Primary colour and internal app logo configured for your team workspace",
      ticketCategoriesLabel: "Ticket categories",
      ticketCategoriesDetail: "Shared routing groups used across tickets and the knowledge base",
      inboxRoutesLabel: "Inbox routes",
      inboxRoutesDetail: "Workspace support mailboxes configured for direct case intake",
    },
    groups: {
      workspace: {
        eyebrow: "Branding & Auth",
        title: "Workspace",
        description: "Brand setup, workspace defaults, authentication, file sharing, and developer platform.",
        items: [
          { title: "Brand setup", detail: "Primary colour, console name, and workspace logo." },
          { title: "Workspace defaults", detail: "Timezone and currency used across the workspace." },
          { title: "Authentication", detail: "Local account password reset and login controls." },
          { title: "File sharing", detail: "Public attachment downloads and per-attachment overrides." },
          { title: "Developer platform", detail: "API access and workspace token expiry." },
        ],
      },
      ticketing: {
        eyebrow: "Routing & Alerts",
        title: "Ticketing",
        description: "Ticket categories, inbox routes, and ticket notifications.",
        items: [
          { title: "Ticket categories", detail: "Shared taxonomy for ticket routing and the knowledge base." },
          { title: "Inbox routes", detail: "Workspace support mailboxes for direct case intake." },
          { title: "Ticket notifications", detail: "Email alerts for ticket events and SLA milestones." },
        ],
      },
      security: {
        eyebrow: "Logs & Restore",
        title: "Security & Compliance",
        description: "Audit logging, access logging, log retention, log language, workspace archiving, and database restore.",
        items: [
          { title: "Audit logging", detail: "Capture and retain workspace audit events." },
          { title: "Access logging", detail: "Capture and retain workspace access events." },
          { title: "Log retention", detail: "Choose how long audit and access logs are kept." },
          { title: "Log language", detail: "Language used for log text entries." },
          { title: "Workspace archiving", detail: "Archive resolved tickets to Workspace storage after a set age." },
          { title: "Database restore", detail: "Roll the workspace database back to a restore point." },
        ],
      },
    },
    categories: {
      eyebrow: "Ticketing",
      title: "Categories",
      description: "Manage the shared taxonomy used for ticket routing and knowledge base organization so teams can reuse the same categories everywhere.",
      loading: "Loading categories…",
      countLabel: "categories",
      count: (count: number) => `${count} categories`,
      manageAction: "Manage categories",
      newCategory: "New category",
      searchPlaceholder: "Search categories by name",
      searchAriaLabel: "Search categories",
      emptySearchTitle: "No categories match that search.",
      emptySearchDescription: "Try a different category name.",
      noCategoriesTitle: "No categories created yet",
      noCategoriesDescription: "Create shared categories so tickets and knowledge articles can reuse the same routing structure.",
      table: {
        category: "Category",
        usage: "Usage",
        status: "Status",
      },
      customFieldsCount: (count: number) => `${count} custom field${count === 1 ? "" : "s"}`,
      linkedCount: (count: number) => `${String(count).padStart(2, "0")} linked`,
      ticketsAndArticles: (tickets: number, articles: number) => `${tickets} tickets · ${articles} articles`,
      inUse: "In use",
      ready: "Ready",
      managerModal: {
        closeAria: "Close category manager",
        eyebrow: "Category manager",
        description: "Search, review, and maintain your shared ticket categories without pushing the rest of settings further down the page.",
      },
      modal: {
        closeAria: "Close ticket category modal",
        createEyebrow: "Create category",
        editEyebrow: "Edit category",
        newTitle: "New category",
        fallbackTitle: "Category",
        description: "Define the shared category name and guidance your team should use across tickets and knowledge articles.",
        categoryName: "Category name",
        categoryNamePlaceholder: "Security / Access",
        routingGuidance: "Routing guidance",
        routingGuidancePlaceholder: "Describe the tickets or knowledge articles that belong in this category.",
        customFields: "Custom ticket fields",
        customFieldsDescription: "Add fields that should appear on tickets created in this category.",
        fieldLabel: (index: number) => `Field ${index}`,
        fieldLabelTitle: "Field label",
        fieldLabelPlaceholder: "Environment",
        fieldType: "Field type",
        fieldTypes: {
          text: "Single line text",
          textarea: "Multi-line text",
          select: "Select list",
          date: "Date",
        },
        required: "Required",
        requiredDescription: "Agents must fill this before saving a ticket.",
        helpText: "Help text",
        helpTextPlaceholder: "Optional guidance shown next to the field",
        options: "Options",
        optionsPlaceholder: "Production\nSandbox\nQA",
        optionsHelp: "Use one option per line.",
        noCustomFieldsTitle: "No custom fields yet",
        noCustomFieldsDescription: "Add optional or required fields that should appear on tickets for this category.",
        createAction: "Create category",
        saveAction: "Save category",
      },
      errors: {
        missingCategoryName: "Add a category name before saving.",
        missingFieldLabel: "Give every custom field a label before saving the category.",
        duplicateFieldLabel: "Custom field labels must be unique within a category.",
        missingSelectOption: (label: string) => `Add at least one option for ${label || "each select field"}.`,
        duplicateCategory: "A category with this name already exists.",
      },
    },
    emailIntake: {
      eyebrow: "Email intake",
      title: "Workspace support mailboxes",
      description: "Emails sent to these mailboxes create tickets in your workspace. Customers can email them directly, or you can set up a redirect from your own support address.",
      loading: "Loading mailboxes…",
      countLabel: "routes",
      count: (count: number) => `${count} routes`,
      manageAction: "Manage mailboxes",
      addRoute: "Add route",
      searchPlaceholder: "Search mailboxes by name",
      searchAriaLabel: "Search workspace support mailboxes",
      emptySearchTitle: "No mailboxes match that search.",
      emptySearchDescription: "Try a different mailbox name.",
      managerModal: {
        closeAria: "Close mailbox manager",
        eyebrow: "Email-to-ticket routes",
        description: "Incoming emails create tickets in the selected category. To use your own support address, configure an external redirect to one of these mailboxes.",
      },
      refreshAction: "Refresh",
      refreshingAction: "Refreshing…",
      saveBeforeOutbound: "Save route changes before changing outbound email.",
      tenantHostnameUnavailable: "Workspace hostname unavailable",
      tenantHostnameUnavailableDetail: "Open this workspace on its workspace domain to configure direct workspace intake addresses.",
      mailboxName: "Mailbox name",
      defaultCategory: "Default category",
      defaultItem: "Default item",
      outbound: "Outbound email",
      enableOutbound: "Enable outbound",
      disableOutbound: "Disable outbound",
      outboundEnabledBadge: "Outbound on",
      outboundPendingBadge: "Verification pending",
      outboundVerifiedBadge: "Verified sender",
      outboundDisabledBadge: "Outbound off",
      customOutboundBadge: "Custom sender",
      outboundFromLabel: "Custom sender address",
      outboundFromPlaceholder: "billing@yourcompany.com",
      outboundFromHint: "Leave blank to send from the workspace mailbox. After entering a custom sender address, click Save route to request an AWS SES verification email. Open that inbox and approve the sender before using it.",
      customOutboundSaveHint: "Click Save route to request a verification email for this custom sender address.",
      outboundRouteSummary: (address: string) => `Outbound messages will send from ${address}.`,
      customOutboundPendingHint: (address: string) => `AWS SES sent a verification email to ${address}. Open that inbox and approve it before sending from this address.`,
      outboundPendingMessage: "Outbound email is enabled and AWS SES verification is pending.",
      outboundReadyMessage: "Outbound email is enabled and AWS SES verified this sender address for sending.",
      outboundDisabledMessage: "Outbound email is disabled for this route.",
      outboundBusyMessage: "Updating outbound email setup…",
      outboundRefreshedMessage: "Outbound verification status refreshed.",
      templateButton: "Email template",
      templateDefaultBadge: "Default template",
      templateCustomBadge: "Custom template",
      noDefaultCategory: "No default category",
      noDefaultItem: "No default item",
      noMailboxesTitle: "No workspace mailboxes configured",
      noMailboxesDescription: "Add mailbox names like help or billing to create email addresses that turn incoming mail into tickets.",
      saveRoutes: "Save routes",
      saveRoute: "Save route",
      templateModal: {
        closeAria: "Close email template editor",
        eyebrow: "Outbound email template",
        title: (address: string) => `Template for ${address}`,
        description: "Paste custom HTML for outbound ticket replies from this mailbox. Leave it blank to use Kuii's default email layout.",
        mailboxLabel: "Sending from",
        subjectLabel: "Subject template",
        subjectPlaceholder: "Update on {{ticket_number}}: {{ticket_title}}",
        htmlLabel: "HTML template",
        htmlPlaceholder: "<html>\n  <body>...</body>\n</html>",
        placeholdersTitle: "Available placeholders",
        placeholders: {
          actor_name: "Agent or teammate name",
          recipient_name: "Recipient name",
          reply_to_address: "Mailbox reply-to address",
          ticket_notes: "Formatted ticket note blocks",
          ticket_number: "Ticket number",
          ticket_status: "Ticket status",
          ticket_title: "Ticket title",
          workspace_name: "Workspace name",
        },
        previewTitle: "Live preview",
        previewDescription: "Preview uses sample values so you can spot layout issues before saving. Email clients may not render everything exactly the same, so test before going live.",
        useDefault: "Load default template",
        useDefaultSubject: "Load default subject",
        clearCustom: "Use default instead",
        done: "Done",
        subjectHint: "Leave blank to use Kuii's default ticket update subject.",
        defaultHint: "Leave this empty to fall back to Kuii's built-in template.",
        saveHint: "Template edits save together with the route list. Email clients may not render everything exactly the same, so test before going live.",
      },
      errors: {
        missingTenantDomain: "Open this workspace on its workspace domain before saving mailboxes.",
        missingMailbox: "Add the mailbox name for every route before saving.",
        invalidMailbox: "Use mailbox names such as support, help, or billing.",
        duplicateMailbox: "Each workspace mailbox can only appear once.",
        invalidOutboundFrom: "Enter a valid custom sender email address or leave it blank.",
      },
      syncBadge: {
        success: "Synced",
        error: "Sync failed",
        syncing: "Syncing",
        idle: "Ready",
      },
      syncMessages: {
        idle: "Mailbox routing will sync when you save.",
        syncing: "Syncing mailbox routes…",
        success: "Mailbox routes updated just now.",
        error: "Mailbox route update failed.",
      },
    },
    notifications: {
      eyebrow: "Ticket notifications",
      title: "Ticket email alerts",
      descriptionPrefix: "Choose which ticket events, scheduled follow-ups, and SLA milestones should trigger ticket notification emails and optionally limit each alert to specific ticket categories. Emails are sent by",
      descriptionSender: "notify@kuii.net",
      countLabel: "active rules",
      count: (count: number) => `${count} active`,
      categoriesLabel: "Category filters",
      allCategories: "All categories",
      selectedCategories: (value: string) => `Categories: ${value}`,
      timezoneLabel: "Ticket notification timezone",
      timezoneDescription: "Choose which timezone all ticket notification emails use when showing times and schedule changes.",
      useWorkspaceTimezone: (timezone: string) => `Use workspace timezone (${timezone})`,
      saveAction: "Save ticket notifications",
      modal: {
        eyebrow: "Ticket notification rules",
        title: "Ticket notification rules",
        description: "Manage each ticket notification trigger, its delivery toggle, and optional category filters in one focused modal.",
        summaryLabel: "Enabled now",
        noneEnabled: "No ticket notification rules are enabled yet.",
        openAction: "Manage ticket alerts",
        closeAria: "Close ticket notification rules modal",
      },
      events: {
        ticketCreated: {
          title: "Ticket created",
          description: "Send an email when a new ticket is created in the workspace.",
          toggleAria: "Toggle ticket created email alerts",
        },
        ticketUpdated: {
          title: "Ticket updated",
          description: "Send an email when an existing ticket is updated.",
          toggleAria: "Toggle ticket updated email alerts",
        },
        ticketScheduledReminder: {
          title: "Scheduled follow-up",
          description: "Send an email when a ticket's scheduled start or end time is within the next 30 minutes.",
          toggleAria: "Toggle scheduled follow-up email alerts",
        },
        ticketSlaWarning: {
          title: "SLA warning",
          description: "Send an email shortly before a ticket reaches its SLA deadline.",
          toggleAria: "Toggle SLA warning email alerts",
        },
        ticketSlaBreached: {
          title: "SLA breached",
          description: "Send an email when a ticket passes its SLA deadline.",
          toggleAria: "Toggle SLA breach email alerts",
        },
      },
    },
    billing: {
      eyebrow: "Billing summary",
      title: "Plan",
      description: "Workspace billing includes ticket, article, and log volume.",
      choosePlan: "Choose a paid plan to start billing and add a payment method.",
      upgradeAction: "Upgrade",
      renewalDate: "Renewal date",
      paymentMethod: "Payment method",
      billingContact: "Billing contact",
      billingSupportNotice: "Please contact accounts@kuii.net to update your billing contact or for any payment disputes.",
      managedInPaddle: "Managed in Paddle",
      notAvailable: "Not available",
      freeDowngradeWarningEyebrow: "Caution",
      freeDowngradeWarningTitle: "Free plan downgrade notice",
      freeDowngradeWarningBody: (date: string) => `Tickets, knowledge base articles, and logs above the Free plan limits will be deleted 30 days after this downgrade, on ${date}.`,
      freeDowngradeWarningDetail: "Upgrade again before then if you need to keep usage above the Free allowance.",
      ticketsTracked: "Tickets tracked",
      articlesTracked: "Articles tracked",
      storageUsed: "Logs stored",
      startSoloCheckout: "Start Solo checkout",
      startGrowthCheckout: "Start Growth checkout",
      openingCheckout: "Opening checkout…",
      openCheckoutError: "Unable to open Paddle checkout right now.",
      changePlanAction: "Change plan",
      changingPlan: "Changing plan…",
      changePlanError: "Unable to change the Paddle subscription right now.",
      manageBilling: "Manage billing",
      openingBilling: "Opening billing…",
      manageBillingError: "Unable to open the Paddle billing portal right now.",
      upgradeModal: {
        closeAria: "Close plan chooser",
        eyebrow: "Workspace billing",
        title: "Choose a plan",
        description: "Pick the plan that fits your workspace.",
        soloName: "Solo",
        soloPrice: "$15/mo",
        soloDescription: "A plan for solopreneurs",
        soloFeatures: {
          tickets: "Includes 5,000 tickets + $5 per 5,000 additional tickets",
          articles: "Includes 100 knowledge articles + $1 per 100 additional articles",
          storage: "Includes 100,000 logs + $1 per additional 100,000 logs",
          overages: "Limited to 1 user",
        },
        growthBadge: "Popular",
        growthName: "Growth",
        growthPrice: "$49/mo",
        growthDescription: "A higher-capacity plan for growing teams and larger queues.",
        growthFeatures: {
          tickets: "Includes 20,000 tickets + $5 per 5,000 additional tickets",
          articles: "Includes 500 knowledge articles + $1 per 100 additional articles",
          storage: "Includes 500,000 logs + $1 per additional 100,000 logs",
          overages: "No user limit",
        },
        currentPlan: "Current plan",
        selectPlan: (planName: string) => `Choose ${planName}`,
        switchPlan: (planName: string) => `Switch to ${planName}`,
      },
    },
    currency: {
      eyebrow: "Workspace defaults",
      title: "Regional defaults",
      description: "Choose the workspace timezone used by notifications, logs, and exported log timestamps, plus the default currency used for pricing and reporting.",
      timezoneLabel: "Workspace timezone",
      timezoneDescription: "Notifications, the logs workspace, and exported log timestamps all use this timezone.",
      timezoneHelp: "Update this once to keep workspace-wide timestamps aligned everywhere.",
      label: "Default workspace currency",
      help: "Items and reporting summaries update immediately when this setting changes.",
      previewLabel: "Current display",
    },
    brand: {
      eyebrow: "Brand",
      title: "Internal app branding",
      description: "Choose a primary colour and logo for the internal workspace.",
      consoleName: "Console name",
      consoleNameDetail: "Shown above the \"Console\" label in the top-left of the app header. Leave blank to use \"Kuii\".",
      consoleNamePlaceholder: "Kuii",
      applyConsoleName: "Apply name",
      primaryColor: "Primary colour",
      primaryColorDetail: "Applied to the internal app header, active navigation state, and core actions.",
      applyColor: "Apply colour",
      logo: "Logo",
      logoUploadHelp: "Click the preview area to upload or replace a PNG, JPG, SVG, or WebP logo.",
      logoRatioHelp: "Recommended image ratio: 1:1 (square).",
    },
    authentication: {
      eyebrow: "Authentication",
      title: "Workspace login",
      description: "Control whether local email-and-password accounts can request password reset links directly from the workspace login page.",
      passwordReset: "Local account password reset",
      toggleAria: "Toggle local account password reset",
      enabledDetail: "Users signing in with local email-and-password accounts can request reset links from the workspace login page.",
      disabledDetail: "Hide the reset flow from the workspace login page until your team is ready to support self-service password recovery.",
      expiryDetail: "Existing valid reset links continue to work until they expire, even if you turn this off later.",
    },
    attachmentSharing: {
      eyebrow: "File sharing",
      title: "Public attachment downloads",
      description: "Let people without a workspace account download ticket attachments through a shareable link, so customers can receive files directly.",
      systemLabel: "Public downloads",
      systemToggleAria: "Toggle public attachment downloads",
      systemEnabledDetail: "Anyone with an attachment's link can download it without signing in. The link is unguessable, but treat shared files as public.",
      systemDisabledDetail: "Attachments stay private to signed-in workspace members. Public links return a not-found response.",
      overridesLabel: "Per-attachment overrides",
      overridesToggleAria: "Toggle per-attachment download overrides",
      overridesEnabledDetail: "Ticket editors can flip an individual attachment to the opposite of the workspace default from the ticket view.",
      overridesDisabledDetail: "The workspace default applies to every attachment and per-attachment controls are hidden.",
      maxSizeDetail: "Each ticket can hold up to 100MB of attachments in total.",
    },
    api: {
      eyebrow: "Developer platform",
      title: "API access",
      description: "Enable workspace API keys and let roles with the API access permission generate personal keys from profile settings.",
      workspaceApi: "Workspace API",
      toggleAria: "Toggle API access",
      enabledDetail: "Users whose roles include API access can create personal keys from the profile menu and use them against workspace API endpoints.",
      disabledDetail: "Keep API access off until you are ready to issue personal API keys for approved roles.",
      expiryEnabledDetail: (daysLabel: string) => `All API keys expire after ${daysLabel} unless a shorter per-key expiry is selected.`,
      expiryDisabledDetail: "Leave expiry unset to keep keys valid until revoked, unless users choose a shorter expiry while creating a key.",
      tenantTokenExpiry: "Workspace token expiry",
      noTenantExpiry: "No workspace expiry",
      expiryHelpTitle: "How expiry is enforced",
      expiryHelpLineOne: "This policy applies to every API key in the workspace, including existing keys.",
      expiryHelpLineTwo: "Users can still choose a shorter expiry when they create a key. The earliest expiry always wins.",
    },
    archiving: {
      eyebrow: "Data lifecycle",
      title: "Workspace archiving",
      description: "Choose how old resolved tickets must be before they are automatically archived to Workspace storage.",
      retentionLabel: "Archive tickets after",
      noRetentionLabel: "Do not auto-archive",
      retentionDetail: (label: string) => `Resolved tickets older than ${label} are archived automatically.`,
      noRetentionDetail: "Resolved tickets are kept in the live database until they are archived manually or by storage overflow maintenance.",
      helpTitle: "How archiving works",
      helpExistingAndNew: "Changing this policy applies to existing resolved tickets as well as new ones.",
      helpAccess: "Archived tickets remain available for reference and can be inspected or downloaded from the Archive view.",
      helpScope: "Only resolved tickets are eligible for automatic archiving. Open and in-progress tickets are never archived automatically.",
      helpOverflow: "This runs alongside the storage overflow archival, which moves the oldest cold data regardless of this setting.",
    },
    databaseRestore: {
      eyebrow: "Database safety",
      title: "Restore database",
      description: (days: number) => `Roll the database back using a calendar date. This workspace can restore data from the last ${days} days.`,
      warningTitle: "Destructive restore",
      warningBody: "This overwrites the live database in place. In-flight queries may be cancelled, and everyone should stop making changes before you continue.",
      windowDetail: (days: number) => `Restore is limited to the last ${days} days for this billing plan.`,
      modalLauncherTitle: "Review available restore points first",
      modalLauncherDescription: "Open the restore flow to load the available restore window before you confirm the rollback.",
      restoreAction: "Restore database",
      restoringAction: "Restoring…",
      invalidSelection: "Choose a restore date and time within the available window.",
      confirmation: (restoreTimestamp: string) => `Restore the database to ${restoreTimestamp}? This overwrites the current database and reloads the app.`,
      success: (restoreTimestamp: string) => `Database restore started for ${restoreTimestamp}. Reloading the workspace…`,
      error: "Failed to restore the database.",
      loadError: "Failed to load the available restore points.",
      modal: {
        closeAria: "Close restore database dialog",
        eyebrow: "Restore window",
        description: "Choose any timestamp within the available restore window. Kuii restores to the selected point in time.",
        loading: "Loading restore points…",
        retryAction: "Retry",
        emptyTitle: "No restore points available",
        emptyDescription: "This workspace does not currently have any restore points available within its rollback window.",
        earliestLabel: "Earliest restore",
        latestLabel: "Latest restore",
        inputLabel: "Restore date and time",
        inputDescription: "Choose any date and time within the restore window.",
        listTitle: "Available restore points",
        count: (count: number) => `${count} restore points`,
        restorePointDetail: (timestamp: string) => `Restore to ${timestamp}.`,
        timezoneDetail: (timezone: string) => `Restore timestamps are shown in the workspace timezone (${timezone}).`,
        windowSummary: (earliest: string, latest: string) => `Available window: ${earliest} to ${latest}.`,
      },
    },
    productPlans: {
      syncedState: "Item templates synced 6 mins ago",
      createdState: "New item template created just now",
      updatedState: (name: string) => `${name} template updated just now`,
      errors: {
        missingName: "Add a name before saving.",
        missingPrice: "Add at least one price before saving this item.",
        invalidPositiveSla: "Add a positive SLA value before enabling it for this item.",
        invalidSla: "Add a valid SLA value before saving this item.",
      },
    },
  },
  ja: {
    common: {
      enabled: "有効",
      disabled: "無効",
      on: "オン",
      off: "オフ",
      done: "完了",
      edit: "編集",
      delete: "削除",
      reset: "リセット",
      cancel: "キャンセル",
      remove: "削除",
      addField: "項目を追加",
      clearForm: "フォームをクリア",
      resetChanges: "変更をリセット",
      noDescriptionYet: "説明はまだありません。",
      removeLogoAria: "ロゴを削除",
      workspaceLogoAlt: "ワークスペースのロゴ",
      noLogoUploadedYet: "ロゴはまだアップロードされていません",
      clickToReplaceLogo: "クリックしてこのロゴを差し替えます。",
      clickToChooseLogo: "クリックしてロゴファイルを選択します。",
      sortBy: (label: string, active: boolean, direction: "asc" | "desc") => `${label}でカテゴリを並び替え${active ? `（現在は${direction === "asc" ? "昇順" : "降順"}）` : ""}`,
      action: "操作",
    },
    page: {
      eyebrow: "設定",
      title: "コントロールセンター",
      sectionEyebrow: (section: string) => `設定 · ${section}`,
      backToSettings: "設定に戻る",
      descriptionManage: "ブランディング、請求、ルーティング、ワークスペース運用を 1 か所で管理します。",
      descriptionReadOnly: "チーム向けのワークスペース構成と請求設定を管理します。",
      environment: "環境",
      environmentStatus: "本番環境は 4 分前に同期済み",
    },
    metrics: {
      brandSetupLabel: "ブランド設定",
      brandSetupDetail: "チームのワークスペース向けにメインカラーと内部アプリのロゴを設定済みです",
      ticketCategoriesLabel: "チケットカテゴリ",
      ticketCategoriesDetail: "チケットとナレッジベースで共通利用するルーティング分類です",
      inboxRoutesLabel: "受信ルート",
      inboxRoutesDetail: "ケース受付用のワークスペースサポートメールボックスを設定しています",
    },
    groups: {
      workspace: {
        eyebrow: "ブランドと認証",
        title: "ワークスペース",
        description: "ブランド設定、ワークスペース既定値、認証、ファイル共有、開発者プラットフォームを管理します。",
        items: [
          { title: "ブランド設定", detail: "メインカラー、コンソール名、ワークスペースのロゴ。" },
          { title: "ワークスペース既定値", detail: "ワークスペース全体で使うタイムゾーンと通貨。" },
          { title: "認証", detail: "ローカルアカウントのパスワード再設定とログイン制御。" },
          { title: "ファイル共有", detail: "添付ファイルの公開ダウンロードと個別上書き。" },
          { title: "開発者プラットフォーム", detail: "API アクセスとワークスペーストークンの有効期限。" },
        ],
      },
      ticketing: {
        eyebrow: "ルーティングと通知",
        title: "チケット運用",
        description: "チケットカテゴリ、受信ルート、チケット通知を管理します。",
        items: [
          { title: "チケットカテゴリ", detail: "チケットルーティングとナレッジベースで使う共通分類。" },
          { title: "受信ルート", detail: "ケース受付用のワークスペースサポートメールボックス。" },
          { title: "チケット通知", detail: "チケットイベントと SLA の節目のメールアラート。" },
        ],
      },
      security: {
        eyebrow: "ログと復元",
        title: "セキュリティとコンプライアンス",
        description: "監査ログ、アクセスログ、ログ保持期間、ログ言語、ワークスペースアーカイブ、データベース復元を管理します。",
        items: [
          { title: "監査ログ", detail: "ワークスペースの監査イベントを記録・保持します。" },
          { title: "アクセスログ", detail: "ワークスペースのアクセスイベントを記録・保持します。" },
          { title: "ログ保持期間", detail: "監査ログとアクセスログの保持期間を選びます。" },
          { title: "ログ言語", detail: "ログテキストの表示言語。" },
          { title: "ワークスペースアーカイブ", detail: "解決済みチケットを一定期間経過後にワークスペースストレージへアーカイブします。" },
          { title: "データベース復元", detail: "ワークスペースのデータベースを復元ポイントまで巻き戻します。" },
        ],
      },
    },
    categories: {
      eyebrow: "チケット運用",
      title: "カテゴリ",
      description: "チケットのルーティングとナレッジベース整理に使う共通分類を管理し、すべてのチームで同じカテゴリを再利用できるようにします。",
      loading: "カテゴリを読み込み中…",
      countLabel: "カテゴリ",
      count: (count: number) => `${count} 件のカテゴリ`,
      manageAction: "カテゴリを管理",
      newCategory: "新しいカテゴリ",
      searchPlaceholder: "カテゴリ名で検索",
      searchAriaLabel: "カテゴリを検索",
      emptySearchTitle: "一致するカテゴリはありません。",
      emptySearchDescription: "別のカテゴリ名を試してください。",
      noCategoriesTitle: "カテゴリはまだ作成されていません",
      noCategoriesDescription: "チケットとナレッジ記事で同じルーティング構成を再利用できるよう、共有カテゴリを作成してください。",
      table: {
        category: "カテゴリ",
        usage: "利用状況",
        status: "ステータス",
      },
      customFieldsCount: (count: number) => `カスタム項目 ${count} 件`,
      linkedCount: (count: number) => `${String(count).padStart(2, "0")} 件連携`,
      ticketsAndArticles: (tickets: number, articles: number) => `チケット ${tickets} 件 · 記事 ${articles} 件`,
      inUse: "使用中",
      ready: "準備完了",
      managerModal: {
        closeAria: "カテゴリ管理を閉じる",
        eyebrow: "カテゴリ管理",
        description: "共有チケットカテゴリを検索・確認・管理できる専用領域です。設定ページ全体が長くなりすぎるのを防ぎます。",
      },
      modal: {
        closeAria: "チケットカテゴリのモーダルを閉じる",
        createEyebrow: "カテゴリを作成",
        editEyebrow: "カテゴリを編集",
        newTitle: "新しいカテゴリ",
        fallbackTitle: "カテゴリ",
        description: "チケットとナレッジ記事で使う共通カテゴリ名と運用ガイダンスを設定します。",
        categoryName: "カテゴリ名",
        categoryNamePlaceholder: "セキュリティ / アクセス",
        routingGuidance: "ルーティングガイダンス",
        routingGuidancePlaceholder: "このカテゴリに含めるチケットやナレッジ記事の内容を説明してください。",
        customFields: "チケット用カスタム項目",
        customFieldsDescription: "このカテゴリで作成されるチケットに表示する項目を追加します。",
        fieldLabel: (index: number) => `項目 ${index}`,
        fieldLabelTitle: "項目ラベル",
        fieldLabelPlaceholder: "環境",
        fieldType: "項目タイプ",
        fieldTypes: {
          text: "1 行テキスト",
          textarea: "複数行テキスト",
          select: "選択リスト",
          date: "日付",
        },
        required: "必須",
        requiredDescription: "エージェントはチケット保存前にこの項目を入力する必要があります。",
        helpText: "補足テキスト",
        helpTextPlaceholder: "項目の横に表示する補足ガイダンス（任意）",
        options: "選択肢",
        optionsPlaceholder: "本番\n検証\nQA",
        optionsHelp: "1 行につき 1 つの選択肢を入力します。",
        noCustomFieldsTitle: "カスタム項目はまだありません",
        noCustomFieldsDescription: "このカテゴリのチケットに表示する任意または必須の項目を追加します。",
        createAction: "カテゴリを作成",
        saveAction: "カテゴリを保存",
      },
      errors: {
        missingCategoryName: "保存する前にカテゴリ名を入力してください。",
        missingFieldLabel: "カテゴリを保存する前に、すべてのカスタム項目にラベルを入力してください。",
        duplicateFieldLabel: "カスタム項目ラベルはカテゴリ内で一意である必要があります。",
        missingSelectOption: (label: string) => `${label || "各選択項目"}に少なくとも 1 つの選択肢を追加してください。`,
        duplicateCategory: "この名前のカテゴリはすでに存在します。",
      },
    },
    emailIntake: {
      eyebrow: "メール受付",
      title: "ワークスペースのサポートメールボックス",
      description: "これらのメールボックスへのメールはワークスペースのチケットになります。顧客が直接送信するか、独自のサポートアドレスからリダイレクトを設定できます。",
      loading: "メールボックスを読み込み中…",
      countLabel: "ルート",
      count: (count: number) => `${count} 件のルート`,
      manageAction: "メールボックスを管理",
      addRoute: "ルートを追加",
      searchPlaceholder: "メールボックス名で検索",
      searchAriaLabel: "ワークスペースのサポートメールボックスを検索",
      emptySearchTitle: "一致するメールボックスはありません。",
      emptySearchDescription: "別のメールボックス名を試してください。",
      managerModal: {
        closeAria: "メールボックス管理を閉じる",
        eyebrow: "メールからチケットへのルート",
        description: "受信メールから選択したカテゴリにチケットを作成します。独自のサポートアドレスを使うには、これらのメールボックスへの外部リダイレクトを設定してください。",
      },
      refreshAction: "更新",
      refreshingAction: "更新中…",
      saveBeforeOutbound: "送信メール設定を変更する前に、ルートの変更を保存してください。",
      tenantHostnameUnavailable: "ワークスペースのホスト名を利用できません",
      tenantHostnameUnavailableDetail: "直接受付用アドレスを設定するには、このワークスペースをワークスペースドメインで開いてください。",
      mailboxName: "メールボックス名",
      defaultCategory: "既定カテゴリ",
      defaultItem: "既定項目",
      outbound: "送信メール",
      enableOutbound: "送信を有効化",
      disableOutbound: "送信を無効化",
      outboundEnabledBadge: "送信オン",
      outboundPendingBadge: "確認待ち",
      outboundVerifiedBadge: "確認済み送信元",
      outboundDisabledBadge: "送信オフ",
      customOutboundBadge: "カスタム送信元",
      outboundFromLabel: "カスタム送信元アドレス",
      outboundFromPlaceholder: "billing@yourcompany.com",
      outboundFromHint: "空欄のままにするとワークスペースメールボックスから送信します。カスタム送信元を入力したら「ルートを保存」をクリックして AWS SES の確認メールを送信してください。使用前にその受信箱で送信元を承認してください。",
      customOutboundSaveHint: "「ルートを保存」をクリックして、このカスタム送信元アドレスの確認メールを送信してください。",
      outboundRouteSummary: (address: string) => `送信メールは ${address} から送信されます。`,
      customOutboundPendingHint: (address: string) => `AWS SES は ${address} に確認メールを送信しました。このアドレスから送信する前に、その受信箱で承認してください。`,
      outboundPendingMessage: "送信メールを有効化しました。AWS SES の確認待ちです。",
      outboundReadyMessage: "送信メールは有効で、この送信元アドレスは AWS SES で確認済みです。",
      outboundDisabledMessage: "このルートの送信メールは無効です。",
      outboundBusyMessage: "送信メール設定を更新しています…",
      outboundRefreshedMessage: "送信元の確認状態を更新しました。",
      templateButton: "メールテンプレート",
      templateDefaultBadge: "既定テンプレート",
      templateCustomBadge: "カスタムテンプレート",
      noDefaultCategory: "既定カテゴリなし",
      noDefaultItem: "既定項目なし",
      noMailboxesTitle: "ワークスペースのメールボックスはまだ設定されていません",
      noMailboxesDescription: "help や billing のようなメールボックス名を追加して、受信メールからチケットを作成するアドレスを設定します。",
      saveRoutes: "ルートを保存",
      saveRoute: "ルートを保存",
      templateModal: {
        closeAria: "メールテンプレートエディターを閉じる",
        eyebrow: "送信メールテンプレート",
        title: (address: string) => `${address} のテンプレート`,
        description: "このメールボックスから送信するチケット返信用にカスタム HTML を貼り付けます。空欄のままにすると Kuii の既定レイアウトを使います。",
        mailboxLabel: "送信元",
        subjectLabel: "件名テンプレート",
        subjectPlaceholder: "{{ticket_number}} の更新: {{ticket_title}}",
        htmlLabel: "HTML テンプレート",
        htmlPlaceholder: "<html>\n  <body>...</body>\n</html>",
        placeholdersTitle: "利用できるプレースホルダー",
        placeholders: {
          actor_name: "担当者またはチームメンバー名",
          recipient_name: "受信者名",
          reply_to_address: "返信先メールアドレス",
          ticket_notes: "整形済みのチケットノートブロック",
          ticket_number: "チケット番号",
          ticket_status: "チケット状態",
          ticket_title: "チケットタイトル",
          workspace_name: "ワークスペース名",
        },
        previewTitle: "ライブプレビュー",
        previewDescription: "保存前にレイアウト崩れを確認できるよう、サンプル値でプレビューします。メールクライアントでは完全に同じ表示にならない場合があるため、本番前に必ずテストしてください。",
        useDefault: "既定テンプレートを読み込む",
        useDefaultSubject: "既定件名を読み込む",
        clearCustom: "既定テンプレートを使う",
        done: "完了",
        subjectHint: "空欄にすると Kuii の既定のチケット更新件名を使います。",
        defaultHint: "空欄にすると Kuii の組み込みテンプレートに戻ります。",
        saveHint: "テンプレートの編集内容はルート一覧と一緒に保存されます。メールクライアントでは完全に同じ表示にならない場合があるため、本番前に必ずテストしてください。",
      },
      errors: {
        missingTenantDomain: "メールボックスを保存する前に、このワークスペースをワークスペースドメインで開いてください。",
        missingMailbox: "保存する前に、すべてのルートにメールボックス名を入力してください。",
        invalidMailbox: "support、help、billing などのメールボックス名を使用してください。",
        duplicateMailbox: "同じワークスペースメールボックスは 1 回だけ設定できます。",
        invalidOutboundFrom: "有効なカスタム送信元メールアドレスを入力するか、空欄のままにしてください。",
      },
      syncBadge: {
        success: "同期済み",
        error: "同期失敗",
        syncing: "同期中",
        idle: "準備完了",
      },
      syncMessages: {
        idle: "保存するとメールボックスルーティングが同期されます。",
        syncing: "メールボックスルートを同期しています…",
        success: "メールボックスルートを更新しました。",
        error: "メールボックスルートの更新に失敗しました。",
      },
    },
    notifications: {
      eyebrow: "チケット通知",
      title: "チケットメールアラート",
      descriptionPrefix: "どのチケットイベント、予定されたフォローアップ、SLA の節目でチケット通知メールを送るかを選び、必要に応じてカテゴリで絞り込みます。送信元は",
      descriptionSender: "notify@kuii.net",
      countLabel: "有効なルール",
      count: (count: number) => `${count} 件有効`,
      categoriesLabel: "カテゴリフィルター",
      allCategories: "すべてのカテゴリ",
      selectedCategories: (value: string) => `対象カテゴリ: ${value}`,
      timezoneLabel: "チケット通知タイムゾーン",
      timezoneDescription: "チケット通知メールで時刻や予定変更を表示するときに使うタイムゾーンを選択します。",
      useWorkspaceTimezone: (timezone: string) => `ワークスペースのタイムゾーンを使用 (${timezone})`,
      saveAction: "チケット通知を保存",
      modal: {
        eyebrow: "チケット通知ルール",
        title: "チケット通知ルール",
        description: "各チケット通知トリガー、送信のオン/オフ、必要に応じたカテゴリフィルターをモーダル内でまとめて管理します。",
        summaryLabel: "現在有効",
        noneEnabled: "有効なチケット通知ルールはまだありません。",
        openAction: "チケットアラートを管理",
        closeAria: "チケット通知ルールモーダルを閉じる",
      },
      events: {
        ticketCreated: {
          title: "チケット作成時",
          description: "ワークスペースで新しいチケットが作成されたときにメールを送信します。",
          toggleAria: "チケット作成メールアラートを切り替える",
        },
        ticketUpdated: {
          title: "チケット更新時",
          description: "既存のチケットが更新されたときにメールを送信します。",
          toggleAria: "チケット更新メールアラートを切り替える",
        },
        ticketScheduledReminder: {
          title: "予定フォローアップ",
          description: "チケットの開始時刻または終了時刻が 30 分以内に近づいたときにメールを送信します。",
          toggleAria: "予定フォローアップメールアラートを切り替える",
        },
        ticketSlaWarning: {
          title: "SLA 警告",
          description: "チケットが SLA 期限に近づいたときにメールを送信します。",
          toggleAria: "SLA 警告メールアラートを切り替える",
        },
        ticketSlaBreached: {
          title: "SLA 超過",
          description: "チケットが SLA 期限を過ぎたときにメールを送信します。",
          toggleAria: "SLA 超過メールアラートを切り替える",
        },
      },
    },
    billing: {
      eyebrow: "請求サマリー",
      title: "プラン",
      description: "ワークスペースの請求には、チケットと記事のトラッキング、保存ログ数、そしてプレミアム機能が含まれます。",
      choosePlan: "有料プランを選択して、請求を開始し、支払い方法を追加します。",
      upgradeAction: "アップグレード",
      renewalDate: "更新日",
      paymentMethod: "支払い方法",
      billingContact: "請求連絡先",
      billingSupportNotice: "請求連絡先の更新や支払いに関する異議申し立ては、accounts@kuii.net までご連絡ください。",
      managedInPaddle: "Paddle で管理",
      notAvailable: "未設定",
      freeDowngradeWarningEyebrow: "注意",
      freeDowngradeWarningTitle: "Free プランへの変更に関する注意",
      freeDowngradeWarningBody: (date: string) => `Free プラン上限を超えるチケット、ナレッジベース記事、ログは、このダウングレードから 30 日後の ${date} に削除されます。`,
      freeDowngradeWarningDetail: "Free プラン上限を超える利用を残したい場合は、その日までに再度アップグレードしてください。",
      ticketsTracked: "追跡中のチケット",
      articlesTracked: "追跡中の記事",
      storageUsed: "保存ログ数",
      startSoloCheckout: "Solo のチェックアウトを開始",
      startGrowthCheckout: "Growth のチェックアウトを開始",
      openingCheckout: "チェックアウトを開いています…",
      openCheckoutError: "現在、Paddle のチェックアウトを開けません。",
      changePlanAction: "プランを変更",
      changingPlan: "プランを変更しています…",
      changePlanError: "現在、Paddle のサブスクリプションを変更できません。",
      manageBilling: "請求を管理",
      openingBilling: "請求ポータルを開いています…",
      manageBillingError: "現在、Paddle の請求ポータルを開けません。",
      upgradeModal: {
        closeAria: "プラン選択を閉じる",
        eyebrow: "ワークスペースの請求",
        title: "プランを選択",
        description: "ワークスペースに合うプランを選択してください。",
        soloName: "Solo",
        soloPrice: "$15/月",
        soloDescription: "個人事業や一人運営向けのプランです。",
        soloFeatures: {
          tickets: "最大 5,000 件のチケットを追跡、5,000 件ごとに +$5",
          articles: "最大 100 件のナレッジ記事を公開、100 件ごとに +$1",
          storage: "最大 100,000 件のログ込み、100,000 件ごとに +$1",
          overages: "1 ユーザーまで",
        },
        growthBadge: "おすすめ",
        growthName: "Growth",
        growthPrice: "$49/月",
        growthDescription: "成長中のチームや大きなキューに対応する高容量プランです。",
        growthFeatures: {
          tickets: "最大 20,000 件のチケットを追跡、5,000 件ごとに +$5",
          articles: "最大 500 件のナレッジ記事を公開、100 件ごとに +$1",
          storage: "最大 500,000 件のログ込み、100,000 件ごとに +$1",
          overages: "ユーザー数は無制限",
        },
        currentPlan: "現在のプラン",
        selectPlan: (planName: string) => `${planName} を選択`,
        switchPlan: (planName: string) => `${planName} に変更`,
      },
    },
    currency: {
      eyebrow: "ワークスペース既定値",
      title: "地域の既定値",
      description: "通知、ログ画面、ログのエクスポート時刻に使うワークスペースのタイムゾーンと、価格やレポート集計に使う既定通貨を設定します。",
      timezoneLabel: "ワークスペースタイムゾーン",
      timezoneDescription: "通知、ログ画面、ログのエクスポート時刻はすべてこのタイムゾーンを使います。",
      timezoneHelp: "ここを更新すると、ワークスペース全体の時刻表示が一括でそろいます。",
      label: "ワークスペースの既定通貨",
      help: "この設定を変更すると、項目画面とレポートの集計表示がすぐに更新されます。",
      previewLabel: "現在の表示",
    },
    brand: {
      eyebrow: "ブランド",
      title: "内部アプリのブランド設定",
      description: "内部ワークスペース用のメインカラーとロゴを選択します。",
      consoleName: "コンソール名",
      consoleNameDetail: "アプリヘッダー左上の「コンソール」ラベルの上に表示されます。空欄の場合は「Kuii」が使用されます。",
      consoleNamePlaceholder: "Kuii",
      applyConsoleName: "名前を適用",
      primaryColor: "メインカラー",
      primaryColorDetail: "内部アプリのヘッダー、アクティブなナビゲーション状態、主要アクションに適用されます。",
      applyColor: "カラーを適用",
      logo: "ロゴ",
      logoUploadHelp: "プレビュー領域をクリックして PNG、JPG、SVG、または WebP のロゴをアップロードまたは差し替えます。",
      logoRatioHelp: "推奨画像比率: 1:1（正方形）。",
    },
    authentication: {
      eyebrow: "認証",
      title: "ワークスペースログイン",
      description: "ローカルのメールアドレスとパスワードのアカウントが、ワークスペースのログインページから直接パスワード再設定リンクを要求できるかを制御します。",
      passwordReset: "ローカルアカウントのパスワード再設定",
      toggleAria: "ローカルアカウントのパスワード再設定を切り替える",
      enabledDetail: "ローカルのメールアドレスとパスワードでサインインするユーザーは、ワークスペースのログインページから再設定リンクを要求できます。",
      disabledDetail: "セルフサービスのパスワード復旧をサポートする準備が整うまで、ワークスペースのログインページから再設定フローを非表示にします。",
      expiryDetail: "後からオフにしても、すでに発行済みの有効な再設定リンクは期限切れになるまで引き続き利用できます。",
    },
    attachmentSharing: {
      eyebrow: "ファイル共有",
      title: "添付ファイルの公開ダウンロード",
      description: "ワークスペースアカウントを持たない人でも共有リンクからチケットの添付ファイルをダウンロードできるようにし、顧客に直接ファイルを渡せるようにします。",
      systemLabel: "公開ダウンロード",
      systemToggleAria: "添付ファイルの公開ダウンロードを切り替える",
      systemEnabledDetail: "添付ファイルのリンクを知っていれば、サインインせずに誰でもダウンロードできます。リンクは推測できませんが、共有ファイルは公開されるものとして扱ってください。",
      systemDisabledDetail: "添付ファイルはサインイン済みのワークスペースメンバーのみが利用できます。公開リンクは「見つかりません」を返します。",
      overridesLabel: "添付ファイルごとの上書き",
      overridesToggleAria: "添付ファイルごとのダウンロード上書きを切り替える",
      overridesEnabledDetail: "チケット編集者はチケット画面から、個々の添付ファイルをワークスペースの既定とは逆の設定に切り替えられます。",
      overridesDisabledDetail: "すべての添付ファイルにワークスペースの既定が適用され、添付ファイルごとの操作は非表示になります。",
      maxSizeDetail: "1 件のチケットにつき、添付ファイルは合計 100MB まで保持できます。",
    },
    api: {
      eyebrow: "開発者プラットフォーム",
      title: "API アクセス",
      description: "ワークスペース API キーを有効にし、API アクセス権限を持つロールがプロフィール設定から個人キーを作成できるようにします。",
      workspaceApi: "ワークスペース API",
      toggleAria: "API アクセスを切り替える",
      enabledDetail: "ロールに API アクセスが含まれるユーザーは、プロフィールメニューから個人キーを作成し、ワークスペース API エンドポイントで利用できます。",
      disabledDetail: "承認済みロール向けの個人 API キーを発行する準備が整うまで、API アクセスを無効のままにします。",
      expiryEnabledDetail: (daysLabel: string) => `各 API キーは ${daysLabel} 後に期限切れになります。より短いキー個別の有効期限が設定されている場合はそちらが優先されます。`,
      expiryDisabledDetail: "有効期限を設定しない場合、ユーザーがキー作成時により短い有効期限を選ばない限り、キーは失効または削除されるまで有効です。",
      tenantTokenExpiry: "ワークスペーストークンの有効期限",
      noTenantExpiry: "ワークスペースの有効期限なし",
      expiryHelpTitle: "有効期限の適用方法",
      expiryHelpLineOne: "このポリシーは既存キーを含む、ワークスペース内のすべての API キーに適用されます。",
      expiryHelpLineTwo: "ユーザーはキー作成時に、さらに短い有効期限を選択できます。最も早い期限が常に優先されます。",
    },
    archiving: {
      eyebrow: "データライフサイクル",
      title: "ワークスペースアーカイブ",
      description: "解決済みチケットがワークスペースストレージに自動アーカイブされるまでの経過期間を設定します。",
      retentionLabel: "チケットのアーカイブ期間",
      noRetentionLabel: "自動アーカイブしない",
      retentionDetail: (label: string) => `${label} より古い解決済みチケットは自動的にアーカイブされます。`,
      noRetentionDetail: "解決済みチケットは手動でアーカイブするか、ストレージオーバーフローメンテナンスでアーカイブされるまでライブデータベースに保持されます。",
      helpTitle: "アーカイブについて",
      helpExistingAndNew: "このポリシーの変更は、既存の解決済みチケットと新しいチケットの両方に適用されます。",
      helpAccess: "アーカイブされたチケットは参照用に利用でき、アーカイブ画面から確認・ダウンロードできます。",
      helpScope: "自動アーカイブの対象は解決済みチケットのみです。オープン・進行中のチケットは自動アーカイブされません。",
      helpOverflow: "この設定はストレージオーバーフローアーカイブと並行して実行されます。オーバーフロー時はこの設定に関わらず最も古いコールドデータが移動されます。",
    },
    databaseRestore: {
      eyebrow: "データベース保護",
      title: "データベースを復元",
      description: (days: number) => `カレンダーの日付を使ってデータベースを巻き戻します。このワークスペースでは直近 ${days} 日まで復元できます。`,
      warningTitle: "破壊的な復元",
      warningBody: "この操作は稼働中のデータベースをその場で上書きします。実行中のクエリは中断される可能性があるため、続行前に全員が変更を止めてください。",
      windowDetail: (days: number) => `この請求プランでは、直近 ${days} 日まで復元できます。`,
      modalLauncherTitle: "先に利用可能な復元ポイントを確認",
      modalLauncherDescription: "復元フローを開いて、利用可能な復元期間を読み込んでからロールバックを確定します。",
      restoreAction: "データベースを復元",
      restoringAction: "復元中…",
      invalidSelection: "利用可能な期間内の復元日時を選択してください。",
      confirmation: (restoreTimestamp: string) => `${restoreTimestamp} までデータベースを復元しますか？ 現在のデータベースを上書きし、アプリを再読み込みします。`,
      success: (restoreTimestamp: string) => `${restoreTimestamp} へのデータベース復元を開始しました。ワークスペースを再読み込みします…`,
      error: "データベースの復元に失敗しました。",
      loadError: "利用可能な復元ポイントの読み込みに失敗しました。",
      modal: {
        closeAria: "データベース復元ダイアログを閉じる",
        eyebrow: "復元期間",
        description: "利用可能な復元期間内で任意のタイムスタンプを選択してください。Kuii は選択した時点まで復元します。",
        loading: "復元ポイントを読み込み中…",
        retryAction: "再試行",
        emptyTitle: "利用可能な復元ポイントはありません",
        emptyDescription: "このワークスペースでは現在、ロールバック期間内に利用できる復元ポイントがありません。",
        earliestLabel: "最も早い復元時刻",
        latestLabel: "最新の復元時刻",
        inputLabel: "復元日時",
        inputDescription: "復元可能な期間内で任意の日時を選択してください。",
        listTitle: "利用可能な復元ポイント",
        count: (count: number) => `${count} 件の復元ポイント`,
        restorePointDetail: (timestamp: string) => `${timestamp} まで復元します。`,
        timezoneDetail: (timezone: string) => `復元タイムスタンプはワークスペースのタイムゾーン（${timezone}）で表示されます。`,
        windowSummary: (earliest: string, latest: string) => `利用可能な期間: ${earliest} 〜 ${latest}`,
      },
    },
    productPlans: {
      syncedState: "項目テンプレートは 6 分前に同期済みです",
      createdState: "新しい項目テンプレートをたった今作成しました",
      updatedState: (name: string) => `${name} テンプレートをたった今更新しました`,
      errors: {
        missingName: "保存する前に名前を入力してください。",
        missingPrice: "この項目を保存する前に、少なくとも 1 つの価格を追加してください。",
        invalidPositiveSla: "この項目で SLA を有効にする前に、正の SLA 値を入力してください。",
        invalidSla: "この項目を保存する前に、有効な SLA 値を入力してください。",
      },
    },
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;

export const notificationsViewCopy = {
  en: {
    page: {
      eyebrow: "Service notices",
      title: "Updates, outages, and maintenance",
      description: "Review workspace-facing service notices, upcoming maintenance windows, and upgrade announcements in one place.",
    },
    metrics: {
      active: "Active",
      activeDescription: "Visible notices that have not been dismissed.",
      archived: "Archived",
      archivedDescription: "Dismissed notices remain available for reference.",
    },
    notices: {
      title: "Service notices",
      description: "Platform-wide announcements for your workspace.",
      total: (count: number) => `${count} total`,
      loading: "Loading service notices…",
      emptyTitle: "No current service notices",
      emptyDescription: "Planned upgrades, outage notices, and service announcements will appear here.",
      dismissed: "Dismissed",
      statusPage: "Status page",
      learnMore: "Learn more",
      dismissAction: "Don’t show again",
    },
    kinds: {
      planned_outage: "Planned outage",
      maintenance: "Maintenance",
      upgrade: "Upgrade",
      incident: "Incident",
      announcement: "Announcement",
    },
    schedule: {
      starts: (value: string) => `Starts ${value}`,
      ends: (value: string) => `Ends ${value}`,
      noWindow: "No scheduled window",
    },
  },
  ja: {
    page: {
      eyebrow: "サービス通知",
      title: "更新、障害、メンテナンス",
      description: "ワークスペース向けのお知らせ、今後のメンテナンス予定、アップグレード案内をまとめて確認できます。",
    },
    metrics: {
      active: "有効",
      activeDescription: "まだ非表示にしていない表示中のお知らせです。",
      archived: "アーカイブ",
      archivedDescription: "非表示にしたお知らせも参照用に残ります。",
    },
    notices: {
      title: "サービス通知",
      description: "このワークスペース向けのプラットフォーム全体のお知らせです。",
      total: (count: number) => `合計 ${count} 件`,
      loading: "通知を読み込んでいます…",
      emptyTitle: "現在のワークスペース通知はありません",
      emptyDescription: "予定されているアップグレード、障害のお知らせ、サービス案内がここに表示されます。",
      dismissed: "非表示済み",
      statusPage: "ステータスページ",
      learnMore: "詳細を見る",
      dismissAction: "今後表示しない",
    },
    kinds: {
      planned_outage: "予定停止",
      maintenance: "メンテナンス",
      upgrade: "アップグレード",
      incident: "インシデント",
      announcement: "お知らせ",
    },
    schedule: {
      starts: (value: string) => `開始 ${value}`,
      ends: (value: string) => `終了 ${value}`,
      noWindow: "予定期間なし",
    },
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;

export const logsViewCopy = {
  en: {
    page: {
      eyebrow: "Logs · Audit and access history",
      title: "Logs",
      description: "Review recent workspace changes, inspect access activity, and export date ranges from one dedicated logs workspace.",
    },
    states: {
      on: "On",
      off: "Off",
      enabled: "Enabled",
      disabled: "Disabled",
      loading: "Loading…",
      refresh: "Refresh",
      export: "Export",
      exporting: "Exporting…",
      exportLogs: "Export logs",
      cancel: "Cancel",
    },
    metrics: {
      auditCapture: "Audit capture",
      accessCapture: "Access capture",
      recentAuditEvents: "Recent audit events",
      recentAccessEvents: "Recent access events",
      auditEnabled: "Workspace changes are being written to the audit log.",
      auditDisabled: "No new audit entries will be captured until audit logging is re-enabled.",
      accessEnabled: "Session, login, logout, and denied API access attempts are being captured.",
      accessDisabled: "No new access entries will be captured until access logging is re-enabled.",
      retentionPolicy: (label: string) => `Retention policy: ${label}.`,
      neverAutoDelete: "never auto-delete",
    },
    audit: {
      eyebrow: "Audit trail",
      title: "Recent workspace changes",
      description: "Review the latest workspace changes captured in the workspace audit log.",
      disabledWarning: "Logging disabled. Currently showing up to 10 of the last available logs.",
      empty: "No audit events have been recorded yet.",
    },
    access: {
      eyebrow: "Access stream",
      title: "Recent workspace access events",
      description: "Review recent login, session, logout, and denied API access activity captured for this workspace.",
      disabledWarning: "Logging is disabled. Currently showing up to 10 of the last available logs.",
      empty: "No access log entries have been recorded yet.",
    },
    modals: {
      closeDialog: "Close dialog",
      audit: {
        eyebrow: "Audit log export",
        title: "Export workspace audit logs",
        description: "Choose the date range for recorded workspace changes, then download the export.",
      },
      access: {
        eyebrow: "Access log export",
        title: "Export workspace access logs",
        description: "Choose the date range for user and device access attempts, then download the export.",
      },
      startDate: "Start date",
      endDate: "End date",
    },
    errors: {
      loadLogs: "Unable to load logs.",
      loadAccess: "Unable to load access logs.",
      loadAudit: "Unable to load audit logs.",
      chooseRange: "Choose both a start date and an end date.",
      invalidRange: "The start date must be before the end date.",
      exportAccess: "Unable to export the access log range.",
      exportAudit: "Unable to export the audit log range.",
    },
    settings: {
      eyebrow: "Security",
      title: "Logs",
      description: "Control whether workspace audit and access activity is captured, how long stored log data is retained, and what language log text should use in the logs view.",
      audit: {
        title: "Workspace audit logging",
        enabledDetail: "Kuii records workspace configuration changes, operational updates, and management actions in the workspace audit log stream.",
        disabledDetail: "No new workspace audit log entries will be written until logging is enabled again.",
        retainedDetail: (label: string) => `Entries older than ${label} are deleted automatically.`,
        retainedUntilPolicyChanges: "Audit logs are retained until you change the policy.",
        retentionLabel: "Audit retention policy",
        toggleAria: "Toggle audit logs",
      },
      access: {
        title: "Workspace access logging",
        enabledDetail: "Kuii records login, logout, session, and denied API access attempts in the workspace access log stream.",
        disabledDetail: "No new workspace access log entries will be written until logging is enabled again.",
        retainedDetail: (label: string) => `Entries older than ${label} are deleted automatically.`,
        retainedUntilPolicyChanges: "Access logs are retained until you change the policy.",
        retentionLabel: "Access retention policy",
        toggleAria: "Toggle access logs",
      },
      logTextLanguage: {
        label: "Log text language",
        description: "Choose how log entry text should appear in the logs view. Existing entries are translated on display when possible.",
      },
      retentionHelp: {
        title: "How retention works",
        existingAndNew: "Changing either policy applies to existing stored logs as well as new entries.",
        disablingCapture: "Disabling capture stops new entries from being written, but retained historical entries remain available until they age out.",
        tenantStorage: "Stored audit and access logs are the only logs counted in billing usage and plan log totals.",
      },
    },
    labels: {
      actorUnknown: "Unknown",
    },
  },
  ja: {
    page: {
      eyebrow: "ログ · 監査とアクセス履歴",
      title: "ログ",
      description: "最近のワークスペース変更やアクセス状況を確認し、日付範囲を指定してエクスポートできる専用ログ画面です。",
    },
    states: {
      on: "オン",
      off: "オフ",
      enabled: "有効",
      disabled: "無効",
      loading: "読み込み中…",
      refresh: "更新",
      export: "エクスポート",
      exporting: "エクスポート中…",
      exportLogs: "ログをエクスポート",
      cancel: "キャンセル",
    },
    metrics: {
      auditCapture: "監査記録",
      accessCapture: "アクセス記録",
      recentAuditEvents: "最近の監査イベント",
      recentAccessEvents: "最近のアクセスイベント",
      auditEnabled: "ワークスペース変更は監査ログに記録されています。",
      auditDisabled: "監査ログを再有効化するまで、新しい監査記録は保存されません。",
      accessEnabled: "セッション、ログイン、ログアウト、拒否された API アクセス試行が記録されています。",
      accessDisabled: "アクセスログを再有効化するまで、新しいアクセス記録は保存されません。",
      retentionPolicy: (label: string) => `保持ポリシー: ${label}。`,
      neverAutoDelete: "自動削除なし",
    },
    audit: {
      eyebrow: "監査履歴",
      title: "最近のワークスペース変更",
      description: "ワークスペース監査ログに記録された最新のワークスペース変更を確認します。",
      disabledWarning: "ログ記録は無効です。現在は取得可能な最新 10 件までを表示しています。",
      empty: "監査イベントはまだ記録されていません。",
    },
    access: {
      eyebrow: "アクセスストリーム",
      title: "最近のワークスペースアクセスイベント",
      description: "このワークスペースで記録された最近のログイン、セッション、ログアウト、拒否された API アクセスを確認します。",
      disabledWarning: "ログ記録は無効です。現在は取得可能な最新 10 件までを表示しています。",
      empty: "アクセスログはまだ記録されていません。",
    },
    modals: {
      closeDialog: "ダイアログを閉じる",
      audit: {
        eyebrow: "監査ログをエクスポート",
        title: "ワークスペース監査ログをエクスポート",
        description: "記録されたワークスペース変更の日付範囲を選択してエクスポートします。",
      },
      access: {
        eyebrow: "アクセスログをエクスポート",
        title: "ワークスペースアクセスログをエクスポート",
        description: "ユーザーおよびデバイスのアクセス試行の日付範囲を選択してエクスポートします。",
      },
      startDate: "開始日",
      endDate: "終了日",
    },
    errors: {
      loadLogs: "ログを読み込めません。",
      loadAccess: "アクセスログを読み込めません。",
      loadAudit: "監査ログを読み込めません。",
      chooseRange: "開始日と終了日の両方を選択してください。",
      invalidRange: "開始日は終了日より前である必要があります。",
      exportAccess: "アクセスログ範囲をエクスポートできません。",
      exportAudit: "監査ログ範囲をエクスポートできません。",
    },
    settings: {
      eyebrow: "セキュリティ",
      title: "ログ",
      description: "ワークスペースの監査・アクセス記録を保存するかどうか、ログ保持期間、そしてログ画面で表示するログ本文の言語を設定します。",
      audit: {
        title: "ワークスペース監査ログ",
        enabledDetail: "Kuii はワークスペース設定変更、運用更新、管理操作をワークスペース監査ログに記録します。",
        disabledDetail: "ログを再有効化するまで、新しいワークスペース監査ログは記録されません。",
        retainedDetail: (label: string) => `${label} より古い記録は自動的に削除されます。`,
        retainedUntilPolicyChanges: "監査ログはポリシーを変更するまで保持されます。",
        retentionLabel: "監査ログ保持ポリシー",
        toggleAria: "監査ログを切り替え",
      },
      access: {
        title: "ワークスペースアクセスログ",
        enabledDetail: "Kuii はログイン、ログアウト、セッション、拒否された API アクセス試行をワークスペースアクセスログに記録します。",
        disabledDetail: "ログを再有効化するまで、新しいワークスペースアクセスログは記録されません。",
        retainedDetail: (label: string) => `${label} より古い記録は自動的に削除されます。`,
        retainedUntilPolicyChanges: "アクセスログはポリシーを変更するまで保持されます。",
        retentionLabel: "アクセスログ保持ポリシー",
        toggleAria: "アクセスログを切り替え",
      },
      logTextLanguage: {
        label: "ログ本文の言語",
        description: "ログ画面で表示するログ本文の言語を選択します。既存の記録も、可能な範囲で表示時に翻訳されます。",
      },
      retentionHelp: {
        title: "保持ポリシーについて",
        existingAndNew: "どちらのポリシー変更も、既存の保存済みログと新しいログの両方に適用されます。",
        disablingCapture: "記録を無効にすると新しいログは保存されませんが、保持期間内の過去ログは期限切れになるまで利用できます。",
        tenantStorage: "監査ログとアクセスログのみが、請求上のログ使用量とプランのログ合計に含まれます。",
      },
    },
    labels: {
      actorUnknown: "不明",
    },
  },
} satisfies Record<AppLanguage, Record<string, unknown>>;
